import axios from 'axios';
import createHttpError from 'http-errors';
import { axiosRetryConfig } from './functions';
import {
  CreateQuickBooksImport,
  LinkQuickBooksStore,
  QuickBooksCatalogPreviewStatus,
  QuickBooksConnection,
  QuickBooksImportJob,
  QuickBooksImportJobPage,
  QuickBooksMappings,
  QuickBooksStoreOption,
  RequestQuickBooksConnection,
  RequestQuickBooksConnectionResponse,
  UpdateQuickBooksMappings,
  VerifyQuickBooksConnection,
} from '../types/integrations.types';

import type {
  AgreementsSummary,
  GetAgreementsSummary,
  AssignPlatformFinanceExpense,
  AssignPlatformFinanceIncome,
  CreatePlatformExpense,
  CreatePlatformFinanceReceiptUpload,
  CreatePlatformFounder,
  CreatePlatformIncome,
  DeletePlatformExpense,
  DeletePlatformFounder,
  DeletePlatformIncome,
  GetPlatformBusinessFinance,
  GetPlatformFinanceReceiptUrl,
  PlatformBusinessFinance,
  PlatformExpense,
  PlatformFinanceReceiptUpload,
  PlatformFinanceReceiptUrl,
  PlatformFounder,
  PlatformIncome,
  ReimbursePlatformExpenses,
  ReimbursePlatformExpensesResult,
  UpdatePlatformExpense,
  UpdatePlatformFounder,
  UpdatePlatformIncome,
  DebitPlatformWalletParams,
  GetMobileMoneyLedger,
  GetPlatformFinanceBootstrap,
  GetPlatformFinanceClient,
  GetPlatformFinanceEmployee,
  GetPlatformFinanceAuditLog,
  GetPlatformFinanceReconciliation,
  GetPlatformFinanceTransaction,
  MobileMoneyLedgerPage,
  PlatformFinanceAuditLogPage,
  PlatformFinanceBootstrap,
  PlatformFinanceClientDetail,
  PlatformFinanceEmployeeDetail,
  PlatformFinanceReconciliation,
  PlatformFinanceTransaction,
  PlatformFinanceWaiver,
  PromoteTopupNumber,
  MergeTopupNumber,
  TopupNumberMergeResult,
  TopupDevicesPage,
  AssignTopupDevice,
  TopupDevice,
  RemovePlatformFinanceWaiver,
  ReplayPlatformFinanceSms,
  MergePlatformFinanceAccount,
  PlatformFinanceAccountMergeResult,
  AssignPlatformFinanceDevice,
  PlatformFinanceDeviceAssignResult,
  ReplayPlatformFinanceSmsResult,
  WaivePlatformFinanceInvoice,
  ArchivePricingPlan,
  AssignFundsToStorePayload,
  AssignUnassignResult,
  BillingRun,
  BillingRunsPage,
  CancelCustomerAgreement,
  CancelInvoice,
  CollectInvoice,
  CreateCustomerAgreement,
  CreatePricingPlan,
  CreditDebitResult,
  CreditPlatformWalletParams,
  CreditWallet,
  CustomerAgreement,
  CustomerAgreementAuditPage,
  CustomerAgreementsPage,
  DebitWallet,
  GenerateInvoice,
  GetActiveCustomerAgreement,
  GetBillingRun,
  GetBillingRuns,
  GetCustomerAgreement,
  GetCustomerAgreements,
  GetInvoice,
  GetInvoicePaymentAttempts,
  GetInvoices,
  GetPricingPlan,
  GetPricingPlans,
  GetWhatsappEvents,
  GrantFreeMonths,
  NotificationRequestType,
  UpdateNotificationSettings,
  NotificationsHistoryPage,
  NotificationTypeSettings,
  NotificationSettingsResponse,
  NotificationContact,
  NotificationContactsResponse,
  CreateNotificationContact,
  UpdateNotificationContact,
  SendNotificationContactVerificationResponse,
  VerifyNotificationContact,
  GetNotificationsHistory,
  GetTasks,
  GetTaskSummary,
  TaskList,
  TaskSummary,
  CreateTask,
  CompleteTask,
  Task,
  GetCreatedTasks,
  TaskPage,
  GetTaskReport,
  TaskReport,
  ResendNotificationRecipient,
  ResendNotificationRecipientResponse,
  GetIftttSmsMessages,
  IftttSmsMessagesPage,
  DeleteResult,
  FailedEvent,
  FailedEventPage,
  ListObservabilityByDevice,
  ListObservabilityByLevel,
  ListObservabilityByMerchant,
  ListObservabilityBySession,
  ListObservabilityByTrace,
  ObservabilityBatch,
  ObservabilityBatchesPage,
  ObservabilityEvent,
  ObservabilityEventsPage,
  ObservabilityIngestRequest,
  ObservabilityIngestResponse,
  ObservabilityIssuesPage,
  ObservabilitySettings,
  UpdateObservabilitySettings,
  QueueSummaryPage,
  QueueStatusPage,
  PeekedQueueMessages,
  RedriveResult,
  Invoice,
  InvoicePaymentAttemptsPage,
  InvoicesPage,
  InvoiceSummary,
  ListPlatformTransactionsParams,
  PlatformMetricsResponse,
  GetPlatformMetricsRangeRequest,
  PlatformBillingMetrics,
  PlatformSalesMetrics,
  PlatformRegistryMetrics,
  PlatformOperationsMetrics,
  PlatformTransactionsPage,
  PreviewPricingRequest,
  PricingCalculationRequest,
  PricingCalculationResult,
  PricingPlan,
  PricingPlansPage,
  PayOutstandingStoreInvoices,
  PayOutstandingStoreInvoicesResult,
  RetryInvoicePayment,
  RunBilling,
  StoreWalletBalance,
  StoreWalletsPage,
  SwitchCustomerAgreement,
  SwitchCustomerAgreementResponse,
  UnassignFundsFromStorePayload,
  UpdateCustomerAgreement,
  UpdatePricingPlan,
} from '@bofrak-backend/shared';
import {
  AccountSnapshotsByPaymentMethod,
  AccountTransaction,
  AccountTransactionChangeSummary,
  Action,
  ActionList,
  ActivityPage,
  AddShiftComment,
  AggregateEmployeeSalesReport,
  AggregateProductSalesReport,
  AggregateSalesReport,
  AggregateStoreSalesReport,
  AppBuildsResponse,
  ApproveTopupClaim,
  AttemptToCloseShift,
  AuditTrailPage,
  AuthenticateUserRequest,
  AuthenticateUserResponse,
  AuthorizeActions,
  AuthorizeActionsResult,
  AuthorizeOpenApp,
  AuthorizeOpenAppResult,
  BackfillPOPaymentSnapshotsRequest,
  BackfillPOPaymentSnapshotsResponse,
  BulkCreateStoreProductsResponse,
  BulkImportStoreProduct,
  BulkLambdaLoggingRequest,
  BulkLambdaLoggingResponse,
  CalculateDiscount,
  CalculateDiscountResult,
  CalculateTax,
  CalculateTaxResult,
  CancelTransferRequest,
  ClaimTopupByDetails,
  ClaimTopupByMessage,
  ClaimTopupByTransactionId,
  CloseShiftWithReconciliation,
  CreateAccountTransaction,
  CreateAuthUserResponse,
  CreateCustomer,
  CreateDiscount,
  CreateGlobalProduct,
  CreateGlobalProductFraction,
  CreateImage,
  CreateInventoryChange,
  CreateInventoryTemplate,
  CreateLoyaltyProgram,
  CreateMerchant,
  CreateMerchantPayment,
  CreateMerchantRole,
  CreateMerchantUserRequestBody,
  CreatePaymentType,
  CreatePermissionV2,
  CreatePlatformPaymentType,
  CreatePosDevice,
  CreatePrincipalV2,
  CreateProduct,
  CreateProductFraction,
  CreatePurchaseOrder,
  CreatePurchaseOrderPayment,
  CreateRoleV2,
  CreateSalesAgent,
  CreateShift,
  CreateStore,
  CreateStoreProduct,
  CreateStoreProductsExportJob,
  CreateSupplier,
  CreateTax,
  CreateThreshold,
  CreateTopupNumber,
  CreateTopupRequest,
  CreateTransferRequest,
  CreateUser,
  CurrentInventory,
  Customer,
  CustomerPage,
  DailyCountList,
  DailyCountListPage,
  Debt,
  DebtCommand,
  DebtorSummary,
  DebtPaymentsPage,
  DebtsPage,
  DecryptCredentialsRequestBody,
  DeletedProductsPage,
  Discount,
  DiscountPage,
  ProductDiscountAssignment,
  Employee,
  GetOfflineCredentials,
  OfflineCredentialsResponse,
  EmployeePage,
  EncryptCredentialsDto,
  EntityTypes,
  FindTopupClaimMatches,
  GenerateInventoryValueExportJobResponse,
  GenerateInventoryValueExportRequest,
  GenerateShiftReportResponse,
  GetAccountsByAccount,
  GetAccountsByOwner,
  GetAccountsByShift,
  GetAccountsByStore,
  GetAllMerchantPayments,
  GetAllPermissionsV2,
  GetAllPrincipalsByRoleV2,
  GetAllPurchaseOrderPayments,
  GetAllPurchaseOrders,
  GetAllRolesV2,
  GetMyActions,
  GetTeamActions,
  GetPrincipalStoreRoles,
  PrincipalStoreRoles,
  GetRolePrincipalsInStore,
  RolePrincipalsInStore,
  GetUserByCognitoId,
  MyActionsResult,
  TeamActionsResult,
  GetAllTopupNumbers,
  GetAllTopupRequests,
  GetAllWalletMessages,
  GetCustomers,
  GetDailyCountLists,
  GetDebtPayments,
  GetDebts,
  GetDiscounts,
  GetActivities,
  GetEmployeeTimeline,
  GetGlobalProductFractions,
  GetGlobalProducts,
  GetGlobalProductsByBarcode,
  GetGlobalProductsByTags,
  GetInventoryChanges,
  GetInventoryTemplates,
  GetPaymentTypes,
  GetPermissionV2,
  GetPlatformPaymentTypes,
  RequestAdminLoginCode,
  RequestAdminLoginCodeResponse,
  VerifyAdminLoginCode,
  VerifyAdminLoginCodeResponse,
  VerifyAdminLoginPassword,
  RequestAdminLoginEmailCode,
  EmailParams,
  VerifyAdminLoginEmailCode,
  WhatsappSettingsResponse,
  UpdateWhatsappSettings,
  Announcement,
  AnnouncementsResponse,
  CreateAnnouncement,
  UpdateAnnouncement,
  GetActiveAnnouncements,
  ActiveAnnouncementsResponse,
  GetPosDevicesByMerchant,
  GetPrincipalV2,
  GetProduct,
  GetProductChanges,
  GetProductFraction,
  GetProductFractionsByMerchant,
  GetProducts,
  GetProductsInventoryValue,
  GetReceiptEvents,
  GetReceipts,
  GetReportEvents,
  GetRoleV2,
  GetSalesAgents,
  GetSalesReport,
  GetSalesReportByDateRangeIntervalOptions,
  GetShifts,
  GetStoreInventoryValueDateRange,
  GetStoreProductByMerchant,
  GetStoreProductChanges,
  GetStoreProductsByMerchant,
  GetStoreSnapshotAtTime,
  GetStoreSnapshotsInRange,
  GetSuppliers,
  GetSupplierSnapshotAtTime,
  GetSupplierSnapshotsInRange,
  GetSupplierValuation,
  GetPurchaseOrderReceiveEvents,
  PurchaseOrderReceiveEventPage,
  GetTaxes,
  GetTopupNumber,
  GetTopupRequest,
  GetTopupsBalance,
  GetTopupsBalanceResponse,
  GetTransfers,
  GlobalProduct,
  GlobalProductFractionPage,
  GlobalProductPage,
  Image,
  InstantCloseShift,
  IntervalAggregateReport,
  InventoryChangePage,
  GetInventoryChangesAppliedLate,
  InventoryChangesAppliedLate,
  RepairInventoryChangesProcessedIndex,
  RepairInventoryChangesProcessedIndexResult,
  InventoryTemplate,
  InventoryTemplatesPage,
  InventoryValueExportsResponse,
  LambdaFunctionsListResponse,
  LiteAppUrlSettings,
  PermissionVisibility,
  PermissionVisibilityResponse,
  UpdatePermissionVisibility,
  LoyaltyProgram,
  LoyaltyProgramPage,
  Merchant,
  DrainStatus,
  SetOrdersTransfersModuleStatus,
  StoreInTransit,
  UpdateOrdersTransfersSettings,
  MerchantPage,
  MerchantPayment,
  MerchantPaymentPage,
  MerchantUserAttributes,
  MultiplePolicyBody,
  MultiplePolicyList,
  PaymentType,
  PaymentTypePage,
  PermissionsPageV2,
  PermissionV2,
  PlatformPaymentType,
  PlatformPaymentTypePage,
  Policy,
  PolicyList,
  POPaymentStoreSnapshot,
  POPaymentStoreSnapshotPage,
  POPaymentSupplierSnapshot,
  POPaymentSupplierSnapshotPage,
  PosDevice,
  PosDevicePage,
  PosSessionEventsPage,
  Principal,
  PrincipalIdsPageV2,
  PrincipalList,
  PrincipalV2,
  Product,
  ProductChangesPage,
  ProductFraction,
  ProductFractionPage,
  ProductInventoryValuePage,
  ProductPage,
  PurchaseOrder,
  PurchaseOrderPage,
  PurchaseOrderPayment,
  PurchaseOrderPaymentPage,
  PutStoreFavouriteCell,
  PutStoreScaleConfig,
  PutStoreScaleShortcut,
  Receipt,
  ReceiptEventsPage,
  ReceiptsPage,
  ReceivePurchaseOrder,
  SavePurchaseOrderItemEstimate,
  ActiveProductExpiryPage,
  DismissProductExpiry,
  GetProductExpiries,
  ProductExpiry,
  ReconcilePaymentFieldsResponse,
  RecordCloseAuditEntry,
  RecordDailyCount,
  RedeemPoints,
  RejectTopupClaim,
  RevokeTopupClaim,
  ReopenTopupClaim,
  CorrectTopupRequest,
  GetTopupStatusCounts,
  TopupStatusCounts,
  RejectTransferRequest,
  ReleasesVersionsResponse,
  RepairParentFractions,
  RepairParentFractionsResult,
  ReportEventsPage,
  ResetTopupPassword,
  Resource,
  ResourceList,
  ReverseAccountTransactionRequest,
  ReverseLoanGivenRequest,
  Role,
  RoleList,
  RolesPageV2,
  RoleV2,
  SalesAgent,
  SalesAgentPage,
  Shift,
  ShiftPage,
  ShiftReportsResponse,
  ShopAndSmileImageProps,
  ShopAndSmileOkResponse,
  SignOutUserRequestBody,
  SignOutUserResponse,
  SoftDeleteFlag,
  Store,
  StoreFavouriteCell,
  StoreFavouriteCellsPage,
  StoreInventoriesResponse,
  ApplyStockOperation,
  ManualStockReservation,
  AttachmentIntentResult,
  AttachmentViewUrl,
  GetAttachment,
  CreateAttachmentIntent,
  Attachment,
  StockOperationResult,
  StoreInventoryValue,
  StoreInventoryValueDateRangePage,
  StorePage,
  StoreProduct,
  StoreProductChangesPage,
  StoreProductExportsResponse,
  StoreProductPage,
  StoreProductsExportJobResponse,
  StoreScaleConfig,
  StoreScaleShortcut,
  StoreScaleShortcutsPage,
  StoreScaleType,
  StoreSnapshotsResult,
  Supplier,
  SupplierPage,
  SupplierValuation,
  Tax,
  TaxPage,
  Threshold,
  ToggleLoggingResponse,
  TopupClaimMatchesPage,
  TopupNumber,
  TopupNumbersPage,
  TopupRequest,
  TopupRequestsPage,
  WalletHandleSmsResponse,
  TransferApproval,
  TransferRequest,
  TriggerDailyCountList,
  UpdateCustomer,
  UpdateDiscount,
  SetProductDiscount,
  UpdateGlobalProduct,
  UpdateInventoryTemplate,
  UpdateLiteAppUrlRequest,
  UpdateLoyaltyAfterSaleOrRefund,
  UpdateLoyaltyProgram,
  UpdateMerchant,
  UpdateMerchantUserRequestBody,
  UpdatePaymentAllocations,
  UpdatePaymentType,
  UpdatePermissionV2,
  UpdatePlatformPaymentType,
  UpdatePosDevice,
  UpdatePosDeviceFeatureFlagsRequest,
  UpdatePosDeviceGeofenceRequest,
  UpdatePrincipalV2,
  UpdateProduct,
  UpdateProductFraction,
  UpdatePurchaseOrder,
  UpdateReceipt,
  UpdateRoleV2,
  UpdateSalesAgent,
  UpdateStore,
  UpdateStoreProduct,
  UpdateStoreProductInventory,
  UpdateSupplier,
  UpdateTax,
  UpdateThreshold,
  UpdateTopupNumber,
  UpdateTopupPassword,
  UpdateUser,
  UpdateUserLanguage,
  WalletGetAllTransferOuts,
  WalletMessagesPage,
  WalletSnapshot,
  StoreActivityResponse,
  StorePaymentsResponse,
  TimeSeriesPoint,
  WalletTransaction,
  WalletTransactionsPage,
  WalletTransferOutsPage,
  SendWhatsappOtpRequest,
  SendWhatsappOtpResponse,
  CreateAuthChallengeRequest,
  CreateAuthChallengeResponse,
  VerifyAuthChallengeRequest,
  UpdateOtpSettingsRequest,
  UpdateOtpSettingsResponse,
  InitiatePinRecoveryRequest,
  InitiatePinRecoveryResponse,
  CompletePinRecoveryRequest,
  WhatsappEventsPage,
  ObservabilityBackendMetricsPage,
  ObservabilityBackendTimeseriesPage,
  ListObservabilityBackendMetrics,
  ListLambdaInvocations,
  LambdaInvocationsPage,
  LambdaInvocationLogs,
} from '@bofrak-backend/shared';
import { ZodError } from 'zod';

/** Who is making a change; recorded on every versioned write (AGENTS.md §4.4.1). */
export interface ApiActor {
  id: string;
  name: string;
}

/**
 * Who authenticates a request to another Shop & Smile service.
 * - `user` (default): the signed-in user's bearer token, when one is set.
 * - `service`: an internal, IAM-authorized route; the transport signs the
 *   request with the calling service's own identity (SigV4) in addition to
 *   forwarding the user's token.
 */
export type RequestAuthMode = 'user' | 'service';

/** One HTTP request, built by an adapter method and sent by a `Transport`. */
export interface RequestSpec {
  method: string;
  url: string;
  headers: Record<string, string>;
  /** JSON-encoded body, when the request has one. */
  data?: string;
  auth?: RequestAuthMode;
}

export interface TransportResponse<T = any> {
  status: number;
  data: T;
  headers?: Record<string, unknown>;
}

/**
 * The port between the adapter's methods (which only build a `RequestSpec`)
 * and the wire. `HttpTransport` is the only implementation; a future one
 * (Lambda invoke, function URL) plugs in here and must return the same
 * response for the same spec.
 */
export interface Transport {
  send(spec: RequestSpec): Promise<TransportResponse>;
}

/** Signs a request with a service identity (see `RequestAuthMode`). */
export type RequestSigner = (spec: RequestSpec) => Promise<RequestSpec>;

export class HttpTransport implements Transport {
  constructor(
    private readonly options: {
      sign?: RequestSigner;
      retryCount?: number;
    } = {},
  ) {
    axiosRetryConfig(axios, options.retryCount);
  }

  async send(spec: RequestSpec): Promise<TransportResponse> {
    const signed =
      spec.auth === 'service' && this.options.sign
        ? await this.options.sign(spec)
        : spec;
    return axios.request({
      method: signed.method,
      url: signed.url,
      headers: signed.headers,
      data: signed.data,
    });
  }
}

/**
 * What `withRequestAuth` accepts: the result of backend-shared's
 * `getRequestAuth()` as is (`{ access_token, actor: { employee_id,
 * employee_name } }`), or an explicit `{ accessToken, actor: { id, name } }`.
 */
export interface RequestAuthLike {
  accessToken?: string;
  access_token?: string;
  actor?: ApiActor | { employee_id: string; employee_name: string };
}

export const normalizeRequestAuth = (
  auth: RequestAuthLike | undefined,
): { accessToken?: string; actor?: ApiActor } => {
  if (!auth) return {};
  const actor = auth.actor
    ? 'employee_id' in auth.actor
      ? { id: auth.actor.employee_id, name: auth.actor.employee_name }
      : auth.actor
    : undefined;
  return { accessToken: auth.access_token ?? auth.accessToken, actor };
};

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  Object.getPrototypeOf(value) === Object.prototype;

export class ShopAndSmileAPIAdapter {
  private readonly apiEndpoint: string;
  private accessToken?: string;
  private readonly retryCount?: number;
  private readonly transport: Transport;
  private actor?: ApiActor;

  /**
   * The signed-in user, sent as `actor_id` / `actor_name` on every write by
   * every adapter instance. Browsers only: call it once after login. Backend
   * code must use `withActor` instead, because a static value would leak
   * between invocations of a warm Lambda.
   */
  private static defaultActor?: ApiActor;

  /**
   * The signed-in user's Cognito access token, sent as `Authorization:
   * Bearer` by every adapter instance that has no token of its own. Browsers
   * only (set by `useAuth` after login); backend code must use
   * `withRequestAuth` for the same reason as `defaultActor`.
   */
  private static defaultAccessToken?: string;

  public static setDefaultActor(actor: ApiActor | undefined): void {
    ShopAndSmileAPIAdapter.defaultActor = actor;
  }

  public static setDefaultAccessToken(token: string | undefined): void {
    ShopAndSmileAPIAdapter.defaultAccessToken = token;
  }

  // Constructor accepting either a string or an object with apiEndpoint, accessToken, and retryCount
  constructor(
    config:
      | string
      | {
          apiEndpoint: string;
          accessToken?: string;
          retryCount?: number;
          transport?: Transport;
        },
  ) {
    if (typeof config === 'string') {
      // If a string is provided, treat it as the apiEndpoint
      this.apiEndpoint = this.normalizeApiEndpoint(config);
      this.transport = new HttpTransport();
    } else {
      // If an object is provided, destructure the properties
      const { apiEndpoint, accessToken, retryCount, transport } = config;
      this.apiEndpoint = this.normalizeApiEndpoint(apiEndpoint);
      this.accessToken = accessToken;
      this.retryCount = retryCount;
      this.transport =
        transport ?? new HttpTransport({ retryCount: this.retryCount });
    }
  }

  /**
   * Update the bearer token attached to subsequent requests.
   * Pass `undefined` to stop sending the Authorization header.
   */
  public setAccessToken(token: string | undefined): void {
    this.accessToken = token;
  }

  /**
   * A copy of this adapter that sends `actor` on every write. Backend
   * services use it to pass the originating user to another service.
   */
  public withActor(actor: ApiActor | undefined): ShopAndSmileAPIAdapter {
    const scoped = Object.create(this) as ShopAndSmileAPIAdapter;
    scoped.actor = actor;
    return scoped;
  }

  /**
   * A copy of this adapter that forwards the current request's verified
   * user (bearer token and actor) to the services it calls. Backend
   * services build it per request (`withAuth` stores the values); the
   * shared instance's own token is never changed.
   */
  public withRequestAuth(
    auth: RequestAuthLike | undefined,
  ): ShopAndSmileAPIAdapter {
    const scoped = Object.create(this) as ShopAndSmileAPIAdapter;
    const { accessToken, actor } = normalizeRequestAuth(auth);
    scoped.accessToken = accessToken;
    scoped.actor = actor;
    return scoped;
  }

  /**
   * Adds the actor to a write's JSON body unless the caller already set
   * `actor_id` (the server strips both fields before validation).
   */
  private withActorFields(method: string, data: any): any {
    const actor = this.actor ?? ShopAndSmileAPIAdapter.defaultActor;
    if (
      !actor ||
      !WRITE_METHODS.has(method.toLowerCase()) ||
      !isPlainObject(data) ||
      data.actor_id !== undefined
    )
      return data;
    return { ...data, actor_id: actor.id, actor_name: actor.name };
  }

  /**
   * Ensures the api endpoint has a protocol (https://).
   * Prevents axios from treating it as a relative path.
   */
  private normalizeApiEndpoint(endpoint: string): string {
    if (!/^https?:\/\//i.test(endpoint)) {
      return `https://${endpoint}`;
    }
    return endpoint;
  }

  /**
   * Logs the error response from Axios.
   */

  public logAxiosError(error: any): void {
    console.log(
      '---------------------------AXIOS ERROR-------------------------------',
    );
    if (axios.isAxiosError(error)) {
      const errorData = {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        method: error.config?.method,
        requestHeaders: error.config?.headers,
        requestData: error.config?.data
          ? JSON.parse(error.config.data)
          : error.config?.data,
        response: error.response
          ? {
              status: error.response.status,
              data: error.response.data,
            }
          : null,
      };
      // Handle Axios errors
      console.error('Axios error:', {
        error: errorData,
        stringifyError: JSON.stringify(errorData, null, 2),
      });
    } else if (error instanceof ZodError) {
      const errorData = {
        issues: error.issues.map((issue) => ({
          path: issue.path,
          message: issue.message,
          code: issue.code,
        })),
      };
      // Handle Zod validation errors
      console.error('Zod validation error:', {
        error: errorData,
        stringifyError: JSON.stringify(errorData, null, 2),
      });
    } else if (error instanceof Error) {
      // Handle generic JavaScript errors
      const errorData = {
        message: error.message,
        stack: error.stack,
      };
      console.error('General error:', {
        error: errorData,
        stringifyError: JSON.stringify(errorData, null, 2),
      });
    } else {
      // Handle unknown errors
      console.error('Unknown error:', {
        error,
        stringifyError: JSON.stringify(error, null, 2),
      });
    }
  }

  /**
   * Builds the request the transport sends.
   * @param method - HTTP method
   * @param url - API endpoint URL
   * @param data - Data to be sent in the request body
   * @param extraHeaders - Additional headers
   * @param auth - `service` for internal, IAM-authorized routes
   * @returns RequestSpec
   */
  public getRequestConfig({
    method,
    data,
    extraHeaders,
    url,
    auth,
  }: {
    method: string;
    url: string;
    data: any;
    extraHeaders: object;
    auth?: RequestAuthMode;
  }): RequestSpec {
    const body = this.withActorFields(method, data);
    const accessToken =
      this.accessToken ?? ShopAndSmileAPIAdapter.defaultAccessToken;
    return {
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        ...(extraHeaders as Record<string, string>),
        // Add access key to headers if it exists
        ...(accessToken && {
          Authorization: `Bearer ${accessToken}`,
        }),
      },
      ...(body && { data: JSON.stringify(body) }),
      ...(auth && { auth }),
    };
  }

  /**
   * Converts the query parameters to a query string.
   * @param params - The query parameters.
   * @returns The query string.
   */

  // Helper method to convert an object to a query string
  public toQueryString(
    params: Record<string, any>,
    splitArrayToCsv?: boolean,
  ): string {
    return Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map((key) => {
        const value = params[key];
        if (Array.isArray(value)) {
          const filteredValues = value.filter((v) => v !== undefined);
          if (splitArrayToCsv) {
            // Join array elements with commas and encode the entire string
            return `${encodeURIComponent(key)}=${encodeURIComponent(filteredValues.join(','))}`;
          } else {
            // Serialize each array element as a separate key=value pair
            return filteredValues
              .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
              .join('&');
          }
        } else {
          // Serialize non-array values normally
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      })
      .join('&');
  }

  public async toggleLogging(allow: boolean): Promise<ToggleLoggingResponse> {
    const url = `${this.apiEndpoint}/admin/logging/toggle`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { allow },
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async listLambdaFunctions(): Promise<LambdaFunctionsListResponse> {
    const url = `${this.apiEndpoint}/admin/logging/functions`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateLambdaLogging(
    payload: BulkLambdaLoggingRequest,
  ): Promise<BulkLambdaLoggingResponse> {
    const url = `${this.apiEndpoint}/admin/logging/bulk`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ==================== Lite App Script URL ====================

  public async getLiteAppUrl(): Promise<LiteAppUrlSettings> {
    const url = `${this.apiEndpoint}/admin/lite-app/url`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateLiteAppUrl(
    payload: UpdateLiteAppUrlRequest,
  ): Promise<LiteAppUrlSettings> {
    const url = `${this.apiEndpoint}/admin/lite-app/url`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ==================== Permission visibility (Settings → Roles) ====================

  /** The admin setting for Settings → Roles; `setting` is null before the first save. */
  public async getPermissionVisibility(): Promise<PermissionVisibilityResponse> {
    const url = `${this.apiEndpoint}/admin/permission-visibility`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Admin session only. 409 when `version` is stale. */
  public async updatePermissionVisibility(
    payload: UpdatePermissionVisibility,
  ): Promise<PermissionVisibility> {
    const url = `${this.apiEndpoint}/admin/permission-visibility`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ==================== Sales Agent CRUD ====================

  public async createSalesAgent(dto: CreateSalesAgent): Promise<SalesAgent> {
    const url = `${this.apiEndpoint}/admin/sales-agents`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<SalesAgent> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getSalesAgents(
    queryParams?: GetSalesAgents,
  ): Promise<SalesAgentPage> {
    let url = `${this.apiEndpoint}/admin/sales-agents`;

    const query = this.toQueryString(queryParams || {});
    if (query) url += `?${query}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<SalesAgentPage> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getSalesAgent(id: string): Promise<SalesAgent> {
    const url = `${this.apiEndpoint}/admin/sales-agents/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<SalesAgent> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateSalesAgent(dto: UpdateSalesAgent): Promise<SalesAgent> {
    const url = `${this.apiEndpoint}/admin/sales-agents/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<SalesAgent> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async deleteSalesAgent(id: string): Promise<void> {
    const url = `${this.apiEndpoint}/admin/sales-agents/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of Merchants from the API.
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of merchants to return
   * @param store_id - Store ID to filter merchants
   * @returns Promise object representing the MerchantPage
   * @throws Error if the request fails
   * @see MerchantPage
   */
  public async getMerchants({
    cursor,
    limit,
    store_id,
    with_stores,
  }: {
    cursor?: string;
    limit?: number;
    store_id?: string;
    /** Default true: only merchants that own at least one store. */
    with_stores?: boolean;
  }): Promise<MerchantPage> {
    let url = `${this.apiEndpoint}/merchants`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      store_id,
      with_stores: with_stores === undefined ? undefined : String(with_stores),
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as MerchantPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a Merchant in the API.
   * @param dto - Create Merchant DTO
   * @returns Promise object representing the Merchant
   * @throws Error if the request fails
   * @see Merchant
   * @see CreateMerchant
   *
   */

  public async createMerchant(dto: CreateMerchant): Promise<Merchant> {
    const url = `${this.apiEndpoint}/merchants`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Merchant> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a single Merchant from the API.
   * @param id - Merchant ID
   * @returns Promise object representing the Merchant
   * @throws Error if the request fails
   * @see Merchant
   */

  public async getMerchant(id: string): Promise<Merchant> {
    const url = `${this.apiEndpoint}/merchants/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Merchant> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a single Customer from the API.
   * @param id - Customer ID
   * @returns Promise object representing the Customer
   * @throws Error if the request fails
   * @see Customer
   */

  public async getCustomer(
    id: string,
    merchant_id: string,
    options?: {
      include_deleted?: SoftDeleteFlag;
      with_debt?: boolean;
      store_id?: string;
    },
  ): Promise<Customer> {
    let url = `${this.apiEndpoint}/customers/${merchant_id}/${id}`;

    if (options) {
      const query = this.toQueryString({
        include_deleted: options.include_deleted,
        with_debt: options.with_debt,
        store_id: options.store_id,
      });

      if (query) {
        url += `?${query}`;
      }
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Customer By Phone
   * @param phone_number - Phone number
   * @returns Promise object representing the Customer
   * @throws Error if the request fails
   * @see Customer
   *
   */

  public async getCustomerByPhone(phone_number: string): Promise<Customer> {
    const url = `${this.apiEndpoint}/customers?phone_number=${phone_number}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Users from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of users to return
   * @param role - User role to filter users
   */

  public async getUsers({
    merchant_id,
    cursor,
    limit,
    role,
    store_id,
  }: {
    merchant_id: string;
    cursor?: string;
    limit?: number;
    role?: string;
    store_id?: string;
  }): Promise<EmployeePage> {
    let url = `${this.apiEndpoint}/users`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      role,
      merchant_id,
      store_id,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<EmployeePage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a single User from the API.
   * @param user_id - User ID
   * @returns Promise object representing the User
   * @throws Error if the request fails
   * @see Employee
   */

  public async getUser(user_id: string): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/${user_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Employee> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a Store in the API.
   * @param dto - Create Store DTO
   * @returns Promise object representing the Store
   * @throws Error if the request fails
   */

  public async createStore(dto: CreateStore): Promise<Store> {
    const url = `${this.apiEndpoint}/stores`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Store> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a Store in the API.
   * @param dto - Update Store DTO
   * @returns Promise object representing the Store
   * @throws Error if the request fails
   * @see Store
   */

  public async updateStore(dto: UpdateStore): Promise<Store> {
    const url = `${this.apiEndpoint}/stores`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Store> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get A single Store from the API.
   * @param store_id - Store ID
   * @returns Promise object representing the Store
   * @throws Error if the request fails
   * @see Store
   */

  public async getStore(
    store_id: string,
    merchant_id: string,
    include_deleted?: SoftDeleteFlag,
  ): Promise<Store> {
    let url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}`;

    if (include_deleted) {
      url = `${url}?include_deleted=${include_deleted}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Store> =
        await this.transport.send(config);

      return data;
    } catch (error) {
      this.logAxiosError(error);

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw createHttpError.NotFound(
          `Store ${store_id} not found or has been deleted`,
        );
      }

      throw error;
    }
  }

  /**
   * Get a list of Store
   * @param merchant_id - Merchant ID
   * @returns Promise object representing the Store
   * @throws Error if the request fails
   * @see StorePage
   */

  /**
   * Delete a Store from the API (soft delete).
   * @param merchant_id - Merchant ID
   * @param id - Store ID
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteStore({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  public async getStores(
    merchant_id: string,
    include_deleted?: SoftDeleteFlag,
    cursor?: string,
    limit?: number,
  ): Promise<StorePage> {
    let url = `${this.apiEndpoint}/stores/${merchant_id}`;

    const params = new URLSearchParams();
    params.append(
      'include_deleted',
      include_deleted ?? SoftDeleteFlag.EXCLUDE_DELETED,
    );
    if (cursor !== undefined) {
      params.append('cursor', cursor);
    }
    if (limit !== undefined) {
      params.append('limit', String(limit));
    }
    url = url.concat(`?${params.toString()}`);

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StorePage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw createHttpError.NotFound(
          `Stores for merchant ${merchant_id} not found`,
        );
      }

      throw error;
    }
  }

  /**
   * Get the persisted scale provisioning snapshot for a store.
   *
   * Returns `null` when the backend has no snapshot for the requested
   * `(store_id, scale_type)` pair (HTTP 404). Any other error is thrown.
   *
   * @see StoreScaleConfig
   */
  public async getStoreScaleConfig({
    merchant_id,
    store_id,
    scale_type,
  }: {
    merchant_id: string;
    store_id: string;
    scale_type: StoreScaleType;
  }): Promise<StoreScaleConfig | null> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/scale-config/${scale_type}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreScaleConfig> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Upsert the scale provisioning snapshot for a store. Path parameters
   * (`merchant_id`, `store_id`, `scale_type`) override the corresponding
   * fields on `dto`.
   *
   * @see StoreScaleConfig
   */
  public async putStoreScaleConfig(
    dto: PutStoreScaleConfig,
  ): Promise<StoreScaleConfig> {
    const url = `${this.apiEndpoint}/stores/${dto.merchant_id}/${dto.store_id}/scale-config/${dto.scale_type}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreScaleConfig> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete the persisted scale provisioning snapshot for a store. No-op
   * server-side if the snapshot does not exist.
   */
  public async deleteStoreScaleConfig({
    merchant_id,
    store_id,
    scale_type,
  }: {
    merchant_id: string;
    store_id: string;
    scale_type: StoreScaleType;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/scale-config/${scale_type}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List every favourite-grid cell binding for a store. Each entry binds
   * a `(page, position)` slot in the POS favourites grid to a single
   * product. Bindings reference the product by id only; the live
   * `StoreProduct` cache supplies name, unit, price and image when the
   * grid is rendered.
   *
   * @see StoreFavouriteCell
   */
  public async listStoreFavourites({
    merchant_id,
    store_id,
  }: {
    merchant_id: string;
    store_id: string;
  }): Promise<StoreFavouriteCellsPage> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/favourites`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreFavouriteCellsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Upsert a favourite-grid cell binding. Path parameters
   * (`merchant_id`, `store_id`, `page`, `position`) override the
   * corresponding fields on `dto`.
   *
   * @see StoreFavouriteCell
   */
  public async putStoreFavourite(
    dto: PutStoreFavouriteCell,
  ): Promise<StoreFavouriteCell> {
    const url = `${this.apiEndpoint}/stores/${dto.merchant_id}/${dto.store_id}/favourites/${dto.page}/${dto.position}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreFavouriteCell> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a favourite-grid cell binding. No-op server-side if the cell
   * is not currently bound.
   */
  public async deleteStoreFavourite({
    merchant_id,
    store_id,
    page,
    position,
  }: {
    merchant_id: string;
    store_id: string;
    page: number;
    position: number;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/favourites/${page}/${position}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List every per-cell PLU shortcut for a store/scale-type pair.
   * Shortcuts are auto-synced from the native POS; the backend is the
   * source of truth (`scale settings`'s "Shortcuts" tab).
   *
   * @see StoreScaleShortcut
   */
  public async listStoreScaleShortcuts({
    merchant_id,
    store_id,
    scale_type,
  }: {
    merchant_id: string;
    store_id: string;
    scale_type: StoreScaleType;
  }): Promise<StoreScaleShortcutsPage> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/scale-config/${scale_type}/shortcuts`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreScaleShortcutsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Upsert a single scale-shortcut cell. Path parameters override the
   * corresponding fields on `dto`.
   *
   * @see StoreScaleShortcut
   */
  public async putStoreScaleShortcut(
    dto: PutStoreScaleShortcut,
  ): Promise<StoreScaleShortcut> {
    const url = `${this.apiEndpoint}/stores/${dto.merchant_id}/${dto.store_id}/scale-config/${dto.scale_type}/shortcuts/${dto.page}/${dto.position}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreScaleShortcut> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a single scale-shortcut cell. No-op server-side if the cell
   * is not currently bound.
   */
  public async deleteStoreScaleShortcut({
    merchant_id,
    store_id,
    scale_type,
    page,
    position,
  }: {
    merchant_id: string;
    store_id: string;
    scale_type: StoreScaleType;
    page: number;
    position: number;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/stores/${merchant_id}/${store_id}/scale-config/${scale_type}/shortcuts/${page}/${position}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Allocate payments to various entities.
   * @param allocations - Payment allocations
   * @returns Promise object
   * @throws Error if the request fails
   * @see UpdatePaymentAllocations
   */
  public async allocatePayments(
    allocations: UpdatePaymentAllocations,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/payments/allocations`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: allocations,
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Back up an Image to the API.
   * @param sku - SKU of the item
   * @param image_url - URL of the image
   * @returns Promise object
   * @throws Error if the request fails
   */

  public async backupImage({
    sku,
    image_url,
  }: {
    sku: string;
    image_url: string;
  }): Promise<any> {
    const url = `${this.apiEndpoint}/loyverse/store-image`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { sku, image_url },
      extraHeaders: {},
    });

    try {
      return await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a Receipt.
   * @param itemData - Receipt
   * @returns Promise object
   * @throws Error if the request fails
   * @see Receipt
   * @see Receipt
   */

  public async createReceipt(itemData: Receipt): Promise<Receipt> {
    const url = `${this.apiEndpoint}/receipts/${itemData.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: itemData,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Receipt> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a Receipt.
   * @param receiptId - Receipt ID
   * @returns Promise object
   * @throws Error if the request fails
   * @see Receipt
   */

  public async getReceipt(
    receiptId: string,
    merchantId: string,
  ): Promise<Receipt> {
    const url = `${this.apiEndpoint}/receipts/${merchantId}/${receiptId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Receipt> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Account Transaction
   * POST accounts/{merchant_id}/transactions/{store_id}
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   */
  public async createAccountTransaction(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transactions/${dto.store_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Transactions by Store
   * GET accounts/{merchant_id}/transactions/{store_id}
   * @param dto - GetAccountsByStore
   * @returns Promise object representing the AccountTransactionChangeSummary
   */
  public async getAccountTransactionsByStore(
    dto: GetAccountsByStore,
  ): Promise<AccountTransactionChangeSummary> {
    let url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transactions/${dto.store_id}`;

    const params = {
      start_date: dto.start_date,
      end_date: dto.end_date,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountTransactionChangeSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Count Account Transactions by Store and Date Range
   * GET accounts/{merchant_id}/transactions/{store_id}/count
   * @param merchant_id - The merchant ID
   * @param store_id - The store ID
   * @param start_date - Start date for the range
   * @param end_date - End date for the range
   * @returns Promise resolving to { count: number }
   */
  public async getAccountTransactionsCount(
    merchant_id: string,
    store_id: string,
    start_date: string,
    end_date: string,
  ): Promise<{ count: number }> {
    let url = `${this.apiEndpoint}/accounts/${merchant_id}/transactions/${store_id}/count`;

    const params = { start_date, end_date };
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ count: number }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Transactions by Owner
   * GET accounts/{merchant_id}/transactions/{store_id}/owner/{owner_type}/{owner_id}
   * @param dto - GetAccountsByOwner
   * @returns Promise object representing the AccountTransactionChangeSummary
   */
  public async getAccountTransactionsByOwner(
    dto: GetAccountsByOwner & { merchant_id: string; store_id: string },
  ): Promise<AccountTransactionChangeSummary> {
    let url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transactions/${dto.store_id}/owner/${dto.owner_type}/${dto.owner_id}`;

    const params = {
      start_date: dto.start_date,
      end_date: dto.end_date,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountTransactionChangeSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Transactions by Shift
   * GET accounts/{merchant_id}/transactions/{store_id}/shift/{shift_id}
   * @param dto - GetAccountsByShift
   * @returns Promise object representing the AccountTransactionChangeSummary
   */
  public async getAccountTransactionsByShift(
    dto: GetAccountsByShift & { merchant_id: string; store_id: string },
  ): Promise<AccountTransactionChangeSummary> {
    let url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transactions/${dto.store_id}/shift/${dto.shift_id}`;

    const params = {
      start_date: dto.start_date,
      end_date: dto.end_date,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountTransactionChangeSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Transactions by Account
   * GET accounts/{merchant_id}/transactions/{store_id}/account/{account_id}
   * @param dto - GetAccountsByAccount & { merchant_id: string }
   * @returns Promise object representing the AccountTransactionChangeSummary
   */
  public async getAccountTransactionsByAccount(
    dto: GetAccountsByAccount & { merchant_id: string },
  ): Promise<AccountTransactionChangeSummary> {
    let url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transactions/${dto.store_id}/account/${dto.account_id}`;

    const params = {
      start_date: dto.start_date,
      end_date: dto.end_date,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountTransactionChangeSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get latest Account Snapshot
   * GET accounts/{merchant_id}/snapshots/{store_id}/{account_id}/latest
   * @param persist - Whether to persist the computed snapshot (default: true)
   * @returns Promise object representing the AccountSnapshot
   */
  public async getLatestAccountSnapshot({
    merchant_id,
    store_id,
    account_id,
  }: {
    merchant_id: string;
    store_id: string;
    account_id: string;
  }): Promise<AccountSnapshotsByPaymentMethod> {
    const url = `${this.apiEndpoint}/accounts/${merchant_id}/snapshots/${store_id}/${account_id}/latest`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountSnapshotsByPaymentMethod> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Snapshot at a specific point in time
   * GET accounts/{merchant_id}/snapshots/{store_id}/{account_id}/at-time?at_time=...
   * @returns Promise object representing the AccountSnapshot
   */
  public async getAccountSnapshotAtTime({
    merchant_id,
    store_id,
    account_id,
    at_time,
  }: {
    merchant_id: string;
    store_id: string;
    account_id: string;
    at_time: string;
  }): Promise<AccountSnapshotsByPaymentMethod> {
    const url = `${this.apiEndpoint}/accounts/${merchant_id}/snapshots/${store_id}/${account_id}/at-time?at_time=${encodeURIComponent(at_time)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AccountSnapshotsByPaymentMethod> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List Account Snapshots in a date range
   * GET accounts/{merchant_id}/snapshots/{store_id}/{account_id}/history?start_date=...&end_date=...
   * @returns Promise object representing the list of AccountSnapshots
   */
  public async listAccountSnapshotsByDateRange({
    merchant_id,
    store_id,
    account_id,
    start_date,
    end_date,
  }: {
    merchant_id: string;
    store_id: string;
    account_id: string;
    start_date: string;
    end_date: string;
  }): Promise<AccountSnapshotsByPaymentMethod[]> {
    const url = `${this.apiEndpoint}/accounts/${merchant_id}/snapshots/${store_id}/${account_id}/history?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      if (Array.isArray(data)) {
        return data as AccountSnapshotsByPaymentMethod[];
      }
      return (data?.snapshots ?? []) as AccountSnapshotsByPaymentMethod[];
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Account Snapshots for all users in a store at a specific point in time
   * GET accounts/{merchant_id}/snapshots/{store_id}/all-users?at_time=...
   * @returns Promise object representing the store snapshots result
   */
  public async getStoreSnapshotsAtTime({
    merchant_id,
    store_id,
    at_time,
  }: {
    merchant_id: string;
    store_id: string;
    at_time: string;
  }): Promise<StoreSnapshotsResult> {
    const url = `${this.apiEndpoint}/accounts/${merchant_id}/snapshots/${store_id}/all-users?at_time=${encodeURIComponent(at_time)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreSnapshotsResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Transfer Request
   * POST accounts/{merchant_id}/transfers/{store_id}
   * @param dto - CreateTransferRequest
   * @returns Promise object representing the TransferRequest
   */
  public async createTransferRequest(
    dto: CreateTransferRequest,
  ): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Immediate Transfer
   * POST accounts/{merchant_id}/transfers/{store_id}/immediate
   * @param dto - CreateTransferRequest
   * @returns Promise object representing the TransferRequest
   */
  public async createImmediateTransfer(
    dto: CreateTransferRequest,
  ): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}/immediate`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Approve Transfer Request
   * POST accounts/{merchant_id}/transfers/{store_id}/{transfer_id}/approve
   * @param dto - TransferApproval
   * @returns Promise object representing the TransferRequest
   */
  public async approveTransferRequest(
    dto: TransferApproval & { merchant_id: string; store_id: string },
  ): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}/${dto.transfer_id}/approve`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...(dto.notes && { notes: dto.notes }),
        transferer_owner: dto.transferer_owner,
        transferee_owner: dto.transferee_owner,
        currency: dto.currency,
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reject Transfer Request
   * POST accounts/{merchant_id}/transfers/{store_id}/{transfer_id}/reject
   * @param dto - RejectTransferRequest
   * @returns Promise object representing the TransferRequest
   */
  public async rejectTransferRequest(
    dto: RejectTransferRequest & { merchant_id: string; store_id: string },
  ): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}/${dto.transfer_id}/reject`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Transfer Request by ID
   * GET accounts/{merchant_id}/transfers/{store_id}/{transfer_id}
   * @returns Promise object representing the TransferRequest
   */
  public async cancelTransferRequest(
    dto: CancelTransferRequest & { merchant_id: string; store_id: string },
  ): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}/${dto.transfer_id}/cancel`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Transfer Request by ID
   * GET accounts/{merchant_id}/transfers/{store_id}/{transfer_id}
   * @returns Promise object representing the TransferRequest
   */
  public async getTransferRequest({
    merchant_id,
    store_id,
    transfer_id,
  }: {
    merchant_id: string;
    store_id: string;
    transfer_id: string;
  }): Promise<TransferRequest> {
    const url = `${this.apiEndpoint}/accounts/${merchant_id}/transfers/${store_id}/${transfer_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TransferRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List Transfer Requests
   * GET accounts/{merchant_id}/transfers/{store_id}
   * @param dto - GetTransfers
   * @returns Promise object representing the transfer list
   */
  public async listTransferRequests(
    dto: GetTransfers,
  ): Promise<{ items: TransferRequest[]; cursor?: string }> {
    let url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/transfers/${dto.store_id}`;

    // Handler validates merchant_id and store_id in both path and query
    const params = {
      merchant_id: dto.merchant_id,
      store_id: dto.store_id,
      transferer_id: dto.transferer_id,
      transferee_id: dto.transferee_id,
      status: dto.status,
      date_from: dto.date_from,
      date_to: dto.date_to,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const {
        data,
      }: TransportResponse<{ items: TransferRequest[]; cursor?: string }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Dividend Payout.
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   * @throws Error if the request fails
   */

  public async createDividendPayout(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/dividends/${dto.store_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Investment
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   * @throws Error if the request fails
   */

  public async createInvestment(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/investments/${dto.store_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Correction
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   * @throws Error if the request fails
   */

  public async createCorrection(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/corrections/${dto.store_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create an Expense
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   * @throws Error if the request fails
   */

  public async createExpense(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/expenses/${dto.store_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Loan Given transaction
   * @param dto - CreateAccountTransaction
   * @returns Promise object representing the AccountTransaction
   * @throws Error if the request fails
   */

  public async createLoanGivenTransaction(
    dto: CreateAccountTransaction,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/loans-given/${dto.store_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reverse a previously recorded Loan Given transaction.
   * @param dto - ReverseLoanGivenRequest
   * @returns Promise object representing the reversal AccountTransaction
   * @throws Error if the request fails
   */
  public async reverseLoanGivenTransaction(
    dto: ReverseLoanGivenRequest,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/loans-given/${dto.store_id}/reverse`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reverse any previously recorded AccountTransaction by its composite key.
   * Supported categories: DEBT_PAYMENT, LOAN_PAYMENT, LOAN_GIVEN, EXPENSE,
   * DIVIDEND, INVESTMENT.
   */
  public async reverseAccountTransaction(
    dto: ReverseAccountTransactionRequest,
  ): Promise<AccountTransaction> {
    const url = `${this.apiEndpoint}/accounts/${dto.merchant_id}/account-transactions/${dto.store_id}/reverse`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { ...dto },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AccountTransaction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of Receipts.
   * @param queryParams - Query parameters
   * @returns Promise object
   */

  public async getReceipts(
    queryParams: GetReceipts,
    merchantId: string,
  ): Promise<ReceiptsPage> {
    let url = `${this.apiEndpoint}/receipts/${merchantId}`;

    // Add query parameters to the URL
    const query = this.toQueryString(queryParams);
    if (query) url += `?${query}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<ReceiptsPage> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a count of Receipts.
   * @param queryParams - Query parameters
   * @returns Promise object
   */

  public async getReceiptsCount(
    queryParams: GetReceipts,
    merchantId: string,
  ): Promise<number> {
    let url = `${this.apiEndpoint}/receipts/${merchantId}/count`;

    // Add query parameters to the URL
    const query = this.toQueryString(queryParams);
    if (query) url += `?${query}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<number> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update a Receipt.
   * @param UpdateReceipt - Update Receipt DTO
   * @returns Promise object
   * @throws Error if the request fails
   */

  public async updateReceipt(dto: UpdateReceipt): Promise<Receipt> {
    const url = `${this.apiEndpoint}/receipts/${dto.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Receipt> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create User in the API.
   * @param dto - Create User DTO
   * @returns Promise object
   * @throws Error if the request fails
   * @see CreateUser
   * @see Employee
   */

  public async createUser(dto: CreateUser): Promise<Employee> {
    const url = `${this.apiEndpoint}/users`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Employee> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update User in the API.
   * @param dto - Update User DTO
   * @returns Promise object
   * @throws Error if the request fails
   * @see Employee
   */

  public async updateUser(dto: UpdateUser): Promise<Employee> {
    const url = `${this.apiEndpoint}/users`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Employee> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Mark a user's phone number as verified/unverified without touching
   * any other user fields (OTP success flow).
   * @param user_id - User ID
   * @param is_number_verified - Whether the number is verified
   * @returns Promise object
   * @throws Error if the request fails
   * @see Employee
   */
  public async markUserNumberVerified(
    user_id: string,
    is_number_verified: boolean,
  ): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/${user_id}/number-verified`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: { is_number_verified },
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Employee> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Save the interface language a user picked, applied in every web app and
   * on the POS. `null` clears it so the merchant default applies.
   * @param user_id - User ID
   * @param language - The picked language, or null
   * @returns Promise object
   * @throws Error if the request fails
   * @see Employee
   */
  public async updateUserLanguage(
    user_id: string,
    language: UpdateUserLanguage['language'],
  ): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/${user_id}/language`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: { language },
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<Employee> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Upload Image
   * @param data - Image data
   * @returns Promise Image
   */

  public async uploadImage(data: CreateImage): Promise<Image> {
    const { merchant_id, entity_type, entity_id } = data;

    const pathMerchantId = data.isGlobalImage ? '_global' : merchant_id;
    const url = `${this.apiEndpoint}/images/${pathMerchantId}/${entity_type}/${entity_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Image> =
        await this.transport.send(config);

      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Upload Item image from url
   * @param itemData - Item image data
   * @returns Promise object
   * @throws Error if the request fails
   * @see ShopAndSmileOkResponse
   * @see ItemImageUploadDto
   */

  public async uploadItemImageFromUrl(
    itemData: ShopAndSmileImageProps,
  ): Promise<ShopAndSmileOkResponse> {
    const url = `${this.apiEndpoint}/loyverse/items/image/upload`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: itemData,
      extraHeaders: {},
    });

    try {
      const response: TransportResponse<ShopAndSmileOkResponse> =
        await this.transport.send(config);
      return response.data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get User By Phone
   * @param phone_number - Phone number
   * @returns Promise object representing the Employee
   * @throws Error if the request fails
   * @see Employee
   */

  /**
   * Service-only (SigV4): the PIN verifiers of a merchant's active employees
   * (of one store when `store_id` is given), for the POS offline sign-in. GET users/offline-credentials
   */
  public async getUsersOfflineCredentials({
    merchant_id,
    store_id,
  }: GetOfflineCredentials): Promise<OfflineCredentialsResponse> {
    const url = `${this.apiEndpoint}/users/offline-credentials?merchant_id=${encodeURIComponent(
      merchant_id,
    )}${store_id ? `&store_id=${encodeURIComponent(store_id)}` : ''}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {}, // IAM-only route: unsigned, API Gateway answers 403 "Missing
      // Authentication Token" and no till ever got the verifiers.
      auth: 'service',
    });
    const { data }: TransportResponse<OfflineCredentialsResponse> =
      await this.transport.send(config);
    return data;
  }

  public async getUserByPhone(phone_number: string): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/phones/${phone_number}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Employee> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update Merchant
   * @param dto - UpdateMerchant object
   * @returns Promise object representing the Merchant
   * @throws Error if the request fails
   * @see Merchant
   * @see UpdateMerchant
   */

  /**
   * Platform admin session only (docs/plans/orders-and-transfers.md §4.2).
   * 409 when the move is not allowed, or with the open work when DISABLED
   * is refused.
   */
  public async setOrdersTransfersModuleStatus(
    merchant_id: string,
    payload: SetOrdersTransfersModuleStatus,
  ): Promise<Merchant> {
    const url = `${this.apiEndpoint}/merchants/${merchant_id}/modules/orders-transfers`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The owner's module settings (`orders:settings.manage`). `idempotencyKey`
   * is made once per save and reused on retries: a replayed save gets its
   * first answer, never overwriting a newer change.
   */
  public async updateOrdersTransfersSettings(
    merchant_id: string,
    payload: UpdateOrdersTransfersSettings,
    idempotencyKey: string,
  ): Promise<Merchant> {
    const url = `${this.apiEndpoint}/merchants/${merchant_id}/modules/orders-transfers/settings`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** What a store has in transit, out and in, as of now. */
  public async getStoreInTransit(
    merchant_id: string,
    store_id: string,
  ): Promise<StoreInTransit> {
    const url = `${this.apiEndpoint}/inventory-transfers/${merchant_id}/stores/${store_id}/in-transit`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreInTransit> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Service-only (SigV4): the open work that keeps the module from DISABLED. */
  public async getDrainStatus(merchant_id: string): Promise<DrainStatus> {
    const url = `${this.apiEndpoint}/inventory-transfers/${merchant_id}/drain-status`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<DrainStatus> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateMerchant(dto: UpdateMerchant): Promise<Merchant> {
    const url = `${this.apiEndpoint}/merchants`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Merchant> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create Merchant User In Cognito
   * @param dto - CreateMerchantUserRequestBody object
   * @throws Error if the request fails
   * @see CreateMerchantUserRequestBody
   * @see CreateAuthUserResponse
   *
   */

  public async createMerchantUserInCognito(
    dto: CreateMerchantUserRequestBody,
  ): Promise<CreateAuthUserResponse> {
    const url = `${this.apiEndpoint}/authentications/merchants`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<CreateAuthUserResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update Merchant User In Cognito
   * @param dto - UpdateMerchantUserRequestBody object
   * @throws Error if the request fails
   * @see UpdateMerchantUserRequestBody
   *
   */

  public async updateMerchantUserInCognito(
    dto: UpdateMerchantUserRequestBody,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/authentications/merchants`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Merchant User From Cognito
   * @param user_id - User ID
   * @returns Promise object representing the MerchantUserAttributes
   * @throws Error if the request fails
   * @see MerchantUserAttributes
   */

  public async getMerchantUserFromCognito(
    user_id: string,
  ): Promise<MerchantUserAttributes> {
    const url = `${this.apiEndpoint}/authentications/merchants/${user_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MerchantUserAttributes> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Taxes from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of taxes to return
   * @returns Promise object representing the TaxPage
   * @throws Error if the request fails
   * @see TaxPage
   */
  public async getTaxes({
    merchant_id,
    cursor,
    limit,
    include_deleted,
  }: GetTaxes): Promise<TaxPage> {
    let url = `${this.apiEndpoint}/taxes/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      include_deleted: include_deleted?.toString(),
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as TaxPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a single Tax from the API.
   * @param merchant_id - Merchant ID
   * @param id - Tax ID
   * @returns Promise object representing the Tax
   * @throws Error if the request fails
   * @see Tax
   */
  public async getTax({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<Tax> {
    const url = `${this.apiEndpoint}/taxes/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Tax> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Tax in the API.
   * @param createTaxDto - CreateTax object
   * @returns Promise object representing the created Tax
   * @throws Error if the request fails
   * @see Tax
   * @see CreateTax
   */
  public async createTax(createTaxDto: CreateTax): Promise<Tax> {
    const url = `${this.apiEndpoint}/taxes`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createTaxDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Tax> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update an existing Tax in the API.
   * @param updateTaxDto - UpdateTax object
   * @returns Promise object representing the updated Tax
   * @throws Error if the request fails
   * @see Tax
   * @see UpdateTax
   */
  public async updateTax(updateTaxDto: UpdateTax): Promise<Tax> {
    const url = `${this.apiEndpoint}/taxes`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateTaxDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Tax> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete a Tax from the API.
   * @param merchant_id - Merchant ID
   * @param id - Tax ID
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteTax({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/taxes/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Calculate taxes based on item price and tax rates.
   * @param calculateTaxDto - CalculateTax object
   * @returns Promise object representing the calculated tax result
   * @throws Error if the request fails
   * @see CalculateTaxResult
   * @see CalculateTax
   */
  public async calculateTaxes(
    calculateTaxDto: CalculateTax,
  ): Promise<CalculateTaxResult> {
    const url = `${this.apiEndpoint}/taxes/calculate`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: calculateTaxDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<CalculateTaxResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Customers from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of customers to return
   * @returns Promise object representing the CustomerPage
   * @throws Error if the request fails
   * @see CustomerPage
   */
  public async getCustomers({
    merchant_id,
    cursor,
    limit,
    include_deleted,
    with_debt,
    store_id,
  }: GetCustomers): Promise<CustomerPage> {
    let url = `${this.apiEndpoint}/customers/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      include_deleted: include_deleted?.toString(),
      with_debt,
      store_id,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as CustomerPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Customer in the API.
   * @param createCustomerDto - CreateCustomer object
   * @returns Promise object representing the created Customer
   * @throws Error if the request fails
   * @see Customer
   * @see CreateCustomer
   */
  public async createCustomer(
    createCustomerDto: CreateCustomer,
  ): Promise<Customer> {
    const url = `${this.apiEndpoint}/customers`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createCustomerDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update an existing Customer in the API.
   * @param updateCustomerDto - UpdateCustomer object
   * @returns Promise object representing the updated Customer
   * @throws Error if the request fails
   * @see Customer
   * @see UpdateCustomer
   */
  public async updateCustomer(
    updateCustomerDto: UpdateCustomer,
  ): Promise<Customer> {
    const url = `${this.apiEndpoint}/customers`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateCustomerDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete a Customer from the API.
   * @param merchant_id - Merchant ID
   * @param id - Customer ID
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteCustomer({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Process a sale for loyalty points.
   * @param merchant_id - Merchant ID
   * @param updateLoyaltyAfterSaleOrRefundDto - UpdateLoyaltyAfterSaleOrRefund object
   * @returns Promise object representing the updated Customer
   * @throws Error if the request fails
   * @see Customer
   * @see UpdateLoyaltyAfterSaleOrRefund
   */
  public async updateLoyaltyAfterSaleOrRefund({
    merchant_id,
    updateLoyaltyAfterSaleOrRefundDto,
  }: {
    merchant_id: string;
    updateLoyaltyAfterSaleOrRefundDto: UpdateLoyaltyAfterSaleOrRefund;
  }): Promise<Customer> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty/process-receipt`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: updateLoyaltyAfterSaleOrRefundDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Redeem loyalty points for a customer.
   * @param merchant_id - Merchant ID
   * @param redeemPointsDto - RedeemPoints object
   * @returns Promise object representing the updated Customer
   * @throws Error if the request fails
   * @see Customer
   * @see RedeemPoints
   */
  public async redeemLoyaltyPoints({
    merchant_id,
    redeemPointsDto,
  }: {
    merchant_id: string;
    redeemPointsDto: RedeemPoints;
  }): Promise<Customer> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty/redeem-points`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: redeemPointsDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Customer> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a Loyalty Program by ID.
   * @param merchant_id - Merchant ID
   * @param id - Loyalty Program ID
   * @returns Promise object representing the Loyalty Program
   * @throws Error if the request fails
   * @see LoyaltyProgram
   */
  public async getLoyaltyProgram({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<LoyaltyProgram> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<LoyaltyProgram> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Loyalty Programs.
   * @param merchant_id - Merchant ID
   * @returns Promise object representing the LoyaltyProgramPage
   * @throws Error if the request fails
   * @see LoyaltyProgramPage
   */
  public async getLoyaltyPrograms({
    merchant_id,
  }: {
    merchant_id: string;
  }): Promise<LoyaltyProgramPage> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as LoyaltyProgramPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Loyalty Program.
   * @param merchant_id - Merchant ID
   * @param createLoyaltyProgramDto - CreateLoyaltyProgram object
   * @returns Promise object representing the created Loyalty Program
   * @throws Error if the request fails
   * @see LoyaltyProgram
   * @see CreateLoyaltyProgram
   */
  public async createLoyaltyProgram({
    merchant_id,
    createLoyaltyProgramDto,
  }: {
    merchant_id: string;
    createLoyaltyProgramDto: CreateLoyaltyProgram;
  }): Promise<LoyaltyProgram> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createLoyaltyProgramDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<LoyaltyProgram> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update an existing Loyalty Program.
   * @param merchant_id - Merchant ID
   * @param updateLoyaltyProgramDto - UpdateLoyaltyProgram object
   * @returns Promise object representing the updated Loyalty Program
   * @throws Error if the request fails
   * @see LoyaltyProgram
   * @see UpdateLoyaltyProgram
   */
  public async updateLoyaltyProgram({
    merchant_id,
    updateLoyaltyProgramDto,
  }: {
    merchant_id: string;
    updateLoyaltyProgramDto: UpdateLoyaltyProgram;
  }): Promise<LoyaltyProgram> {
    const url = `${this.apiEndpoint}/customers/${merchant_id}/loyalty`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateLoyaltyProgramDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<LoyaltyProgram> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Products from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of products to return
   * @param is_parent - Filter by parent products
   * @returns Promise object representing the ProductPage
   * @throws Error if the request fails
   * @see ProductPage
   */
  public async getProducts(dto: GetProducts): Promise<ProductPage> {
    const {
      merchant_id,
      cursor,
      limit,
      is_parent,
      include_deleted,
      include_fractions,
    } = dto;
    let url = `${this.apiEndpoint}/products/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      is_parent: is_parent?.toString(),
      include_deleted,
      include_fractions: include_fractions?.toString(),
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ProductPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a single Product from the API.
   * @param merchant_id - Merchant ID
   * @param id - Product ID
   * @returns Promise object representing the Product
   * @throws Error if the request fails
   * @see Product
   */
  public async getProduct({
    merchant_id,
    id,
    include_deleted,
  }: GetProduct): Promise<Product> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/${id}`;

    if (include_deleted) {
      url += `?include_deleted=${include_deleted}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Product> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Product in the API.
   * @param createProductDto - CreateProduct object
   * @returns Promise object representing the created Product
   * @throws Error if the request fails
   * @see Product
   * @see CreateProduct
   */
  public async createProduct(
    createProductDto: CreateProduct,
  ): Promise<Product> {
    const url = `${this.apiEndpoint}/products`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createProductDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Product> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update an existing Product in the API.
   * @param updateProductDto - UpdateProduct object
   * @returns Promise object representing the updated Product
   * @throws Error if the request fails
   * @see Product
   * @see UpdateProduct
   */
  public async updateProduct(
    updateProductDto: UpdateProduct,
  ): Promise<Product> {
    const url = `${this.apiEndpoint}/products/${updateProductDto.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateProductDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Product> =
        await this.transport.send(config);

      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Repair fraction drift for a parent product. Recomputes
   * `ProductFraction.fraction` and child `Product.fraction` from
   * `quantity_in_unit_package`, and emits per-store SQS events so each
   * `StoreProduct.fraction` and `StoreProduct.cost` is refreshed.
   *
   * @see RepairParentFractions
   * @see RepairParentFractionsResult
   */
  public async repairParentFractions(
    dto: RepairParentFractions,
  ): Promise<RepairParentFractionsResult> {
    const url = `${this.apiEndpoint}/products/${dto.merchant_id}/repair-fractions/${dto.parent_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { updator_id: dto.updator_id },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<RepairParentFractionsResult> =
        await this.transport.send(config);

      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete a Product from the API.
   *
   * The caller MUST supply `deleted_by_id` and `deleted_by_name` so the
   * audit trail (who deleted, when) can be recorded on the row. The
   * backend will reject the request if these are missing.
   */
  public async deleteProduct({
    merchant_id,
    id,
    deleted_by_id,
    deleted_by_name,
  }: {
    merchant_id: string;
    id: string;
    deleted_by_id: string;
    deleted_by_name: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: { deleted_by_id, deleted_by_name },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Restore a soft-deleted Product (clears deleted_at + audit fields).
   */
  public async restoreProduct({
    merchant_id,
    id,
    restored_by_id,
    restored_by_name,
  }: {
    merchant_id: string;
    id: string;
    restored_by_id: string;
    restored_by_name: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/${id}/restore`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { restored_by_id, restored_by_name },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * List soft-deleted Products (with audit info: who/when).
   */
  public async listDeletedProducts({
    merchant_id,
    cursor,
    limit,
  }: {
    merchant_id: string;
    cursor?: string;
    limit?: number;
  }): Promise<DeletedProductsPage> {
    let url = `${this.apiEndpoint}/products/deleted-products/${merchant_id}`;

    const queryString = this.toQueryString({
      cursor,
      limit: limit?.toString(),
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as DeletedProductsPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Restore a soft-deleted in-store product row (per-store recycle bin).
   */
  public async restoreStoreProduct({
    merchant_id,
    store_id,
    id,
    restored_by_id,
    restored_by_name,
  }: {
    merchant_id: string;
    store_id: string;
    id: string;
    restored_by_id: string;
    restored_by_name: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/${id}/restore`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { restored_by_id, restored_by_name },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Product Fraction.
   * @param merchant_id - Merchant ID
   * @param createProductFractionDto - CreateProductFraction object
   * @returns Promise object representing the created ProductFraction
   * @throws Error if the request fails
   * @see ProductFraction
   * @see CreateProductFraction
   */
  public async createProductFraction({
    merchant_id,
    createProductFractionDto,
  }: {
    merchant_id: string;
    createProductFractionDto: CreateProductFraction;
  }): Promise<ProductFraction> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/fractions`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createProductFractionDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ProductFraction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a Product Fraction by ID.
   * @param merchant_id - Merchant ID
   * @param id - Product Fraction ID
   * @returns Promise object representing the ProductFraction
   * @throws Error if the request fails
   * @see ProductFraction
   */
  public async getProductFraction({
    merchant_id,
    id,
    include_deleted,
  }: GetProductFraction): Promise<ProductFraction> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/fractions/${id}`;

    if (include_deleted) {
      url += `?include_deleted=${include_deleted}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ProductFraction> =
        await this.transport.send(config);

      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Product Fractions.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of fractions to return
   * @param fraction_id - Filter by fraction ID
   * @param parent_id - Filter by parent ID
   * @returns Promise object representing an array of ProductFractions
   * @throws Error if the request fails
   * @see ProductFraction[]
   */
  public async getProductFractions({
    merchant_id,
    cursor,
    limit,
    fraction_id,
    parent_id,
    include_deleted,
  }: GetProductFractionsByMerchant): Promise<ProductFractionPage> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/fractions`;

    // Create query parameters object
    const params = {
      cursor,
      limit,
      fraction_id,
      parent_id,
      include_deleted,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ProductFractionPage;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a Product Fraction.
   * @param merchant_id - Merchant ID
   * @param id - Product Fraction ID
   * @param updateProductFractionDto - UpdateProductFraction object
   * @returns Promise object representing the updated ProductFraction
   * @throws Error if the request fails
   * @see ProductFraction
   * @see UpdateProductFraction
   */
  public async updateProductFraction({
    merchant_id,
    id,
    updateProductFractionDto,
  }: {
    merchant_id: string;
    id: string;
    updateProductFractionDto: UpdateProductFraction;
  }): Promise<ProductFraction> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/fractions/${id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateProductFractionDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ProductFraction> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete a Product Fraction.
   * @param merchant_id - Merchant ID
   * @param id - Product Fraction ID
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteProductFraction({
    merchant_id,
    id,
    parent_id,
    deleted_by_id,
    deleted_by_name,
  }: {
    merchant_id: string;
    id: string;
    parent_id: string;
    deleted_by_id: string;
    deleted_by_name: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/fractions/${id}?parent_id=${parent_id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: { deleted_by_id, deleted_by_name },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Store Product.
   * @param merchant_id - Merchant ID
   * @param createStoreProductDto - CreateStoreProduct object
   * @returns Promise object representing the created StoreProduct
   * @throws Error if the request fails
   * @see StoreProduct
   * @see CreateStoreProduct
   */
  public async createStoreProduct({
    merchant_id,
    createStoreProductDto,
  }: {
    merchant_id: string;
    createStoreProductDto: CreateStoreProduct;
  }): Promise<StoreProduct> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createStoreProductDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Product Changes
   * @param merchant_id - Merchant ID
   * @param productChangeDto - GetProductChanges query params
   * @returns Promise object representing the ProductChangesPage
   */
  public async getProductChanges({
    merchant_id,
    productChangeDto,
  }: {
    merchant_id: string;
    productChangeDto: GetProductChanges;
  }): Promise<ProductChangesPage> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/changes`;

    const queryString = this.toQueryString(productChangeDto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ProductChangesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of Store Product Changes
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param storeProductChageDto - StoreProductChange object
   * @returns Promise object representing the StoreProductChangePage
   */

  public async getStoreProductChanges({
    merchant_id,
    storeProductChangeDto,
  }: {
    merchant_id: string;
    storeProductChangeDto: GetStoreProductChanges;
  }): Promise<StoreProductChangesPage> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/changes/store-products`;

    // Create query parameters object
    // Generate query string from params
    const queryString = this.toQueryString(storeProductChangeDto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProductChangesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Store Products.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of store products to return
   * @param is_parent - Filter by parent products
   * @returns Promise object representing the StoreProductPage
   * @throws Error if the request fails
   * @see StoreProductPage
   */
  public async getStoreProducts({
    merchant_id,
    store_id,
    cursor,
    limit,
    is_parent,
    include_deleted,
  }: GetStoreProductsByMerchant): Promise<StoreProductPage> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit: limit?.toString(),
      is_parent: is_parent?.toString(),
      include_deleted,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as StoreProductPage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a store product export job.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param payload - Export job preferences
   * @returns Promise object representing the scheduled job
   * @throws Error if the request fails
   * @see StoreProductsExportJobResponse
   */
  public async createStoreProductsExportJob({
    merchant_id,
    store_id,
    include_deleted,
    triggered_by,
    requested_by,
  }: {
    merchant_id: string;
    store_id: string;
  } & CreateStoreProductsExportJob): Promise<StoreProductsExportJobResponse> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/export`;

    const payload: CreateStoreProductsExportJob = {};

    if (include_deleted !== undefined) {
      payload.include_deleted = include_deleted;
    }

    if (triggered_by) {
      payload.triggered_by = triggered_by;
    }

    if (requested_by) {
      payload.requested_by = requested_by;
    }

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProductsExportJobResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * List generated store product exports for a store.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param cursor - Pagination cursor
   * @param limit - Number of exports per page
   * @returns Promise with the available exports and optional next cursor
   */
  public async listStoreProductExports({
    merchant_id,
    store_id,
    cursor,
    limit,
  }: {
    merchant_id: string;
    store_id: string;
    cursor?: string;
    limit?: number;
  }): Promise<StoreProductExportsResponse> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/exports`;

    const params = this.toQueryString({ cursor, limit });

    if (params) {
      url += `?${params}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProductExportsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Generate (or fetch the existing) PDF shift report for a closed shift.
   * Server-side is idempotent: a deterministic S3 key is checked first and
   * the existing object is reused unless `force` is true.
   */
  public async generateShiftReport({
    merchant_id,
    store_id,
    shift_id,
    force,
  }: {
    merchant_id: string;
    store_id: string;
    shift_id: string;
    force?: boolean;
  }): Promise<GenerateShiftReportResponse> {
    const url = `${this.apiEndpoint}/reports/${merchant_id}/${store_id}/shifts/${shift_id}/report`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { force: force ?? false },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GenerateShiftReportResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List PDF shift reports for a store. Optionally filter to a single shift.
   */
  public async listShiftReports({
    merchant_id,
    store_id,
    shift_id,
    cursor,
    limit,
  }: {
    merchant_id: string;
    store_id: string;
    shift_id?: string;
    cursor?: string;
    limit?: number;
  }): Promise<ShiftReportsResponse> {
    let url = `${this.apiEndpoint}/reports/${merchant_id}/${store_id}/shift-reports`;

    const params = this.toQueryString({ shift_id, cursor, limit });

    if (params) {
      url += `?${params}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ShiftReportsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Generate an inventory value CSV export for a store. The CSV is built
   * server-side from live inventory, uploaded to S3, and the response
   * carries the record with a download URL for the on-the-spot download.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param date - Optional ISO 8601 date (defaults to today server-side)
   * @param requested_by - Optional user id of the requester
   * @returns Promise with the generated export record
   */
  public async generateInventoryValueExport({
    merchant_id,
    store_id,
    date,
    requested_by,
  }: {
    merchant_id: string;
    store_id: string;
    date?: string;
    requested_by?: string;
  }): Promise<GenerateInventoryValueExportJobResponse> {
    const url = `${this.apiEndpoint}/reports/${merchant_id}/inventory-value/${store_id}/export`;

    const payload: GenerateInventoryValueExportRequest = {};

    if (date) {
      payload.date = date;
    }

    if (requested_by) {
      payload.requested_by = requested_by;
    }

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const {
        data,
      }: TransportResponse<GenerateInventoryValueExportJobResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List generated inventory value CSV exports for a store.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param cursor - Pagination cursor
   * @param limit - Number of exports per page
   * @returns Promise with the available exports and optional next cursor
   */
  public async listInventoryValueExports({
    merchant_id,
    store_id,
    cursor,
    limit,
  }: {
    merchant_id: string;
    store_id: string;
    cursor?: string;
    limit?: number;
  }): Promise<InventoryValueExportsResponse> {
    let url = `${this.apiEndpoint}/reports/${merchant_id}/inventory-value/${store_id}/exports`;

    const params = this.toQueryString({ cursor, limit });

    if (params) {
      url += `?${params}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryValueExportsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Store Product.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param id - Store Product ID
   * @returns Promise object representing the StoreProduct
   * @throws Error if the request fails
   * @see StoreProduct
   */
  public async getStoreProduct({
    store_id,
    id,
    include_deleted,
    merchant_id,
  }: GetStoreProductByMerchant): Promise<StoreProduct> {
    let url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/${id}`;

    // Create query parameters object
    const params = {
      include_deleted,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update Store Product Inventory.
   * @param updateStoreProductInventoryDto - UpdateStoreProductInventory object
   * @returns Promise object representing the updated StoreProduct
   * @throws Error if the request fails
   * @see StoreProduct
   */

  public async updateStoreProductInventory(
    updateStoreProductInventoryDto: UpdateStoreProductInventory,
  ): Promise<StoreProduct> {
    const { merchant_id, store_id } = updateStoreProductInventoryDto;

    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/inventory`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateStoreProductInventoryDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get All Latest Inventories For a Store.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @returns Promise object representing the StoreInventoriesResponse
   * @throws Error if the request fails
   * @see StoreInventoriesResponse
   */

  /**
   * Applies a stock operation (reserve, release, bucket move, correction):
   * the only writer of the stock buckets. Service-only (SigV4). Idempotent by
   * `operation_id`: a replay returns the recorded result with
   * `applied: false`.
   */
  public async applyStockOperation(
    merchant_id: string,
    operation: ApplyStockOperation,
  ): Promise<StockOperationResult> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/operations`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: operation,
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<StockOperationResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** A product's stock row in every store of the merchant. Service-only. */
  public async getProductStockAcrossStores(
    merchant_id: string,
    product_id: string,
  ): Promise<StoreInventoriesResponse> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/products/${product_id}/stores`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<StoreInventoriesResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reserves or releases stock by hand (Products app). `idempotencyKey` is
   * generated once per user action and reused on retries: it is also the
   * operation's id, so a retry never reserves twice.
   */
  public async reserveStock(
    merchant_id: string,
    store_id: string,
    product_id: string,
    reservation: ManualStockReservation,
    idempotencyKey: string,
  ): Promise<StockOperationResult> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/products/${product_id}/reservations`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: reservation,
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<StockOperationResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** A private upload link for one file; PUT it, then `confirmAttachment`. */
  public async createAttachmentIntent(
    merchant_id: string,
    intent: CreateAttachmentIntent,
    idempotencyKey: string,
  ): Promise<AttachmentIntentResult> {
    const url = `${this.apiEndpoint}/inventory-transfers/${merchant_id}/attachments`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: intent,
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<AttachmentIntentResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Checks the uploaded file: VERIFIED, or REJECTED with the reason. */
  public async confirmAttachment(
    merchant_id: string,
    attachment_id: string,
    idempotencyKey: string,
  ): Promise<Attachment> {
    const url = `${this.apiEndpoint}/inventory-transfers/${merchant_id}/attachments/${attachment_id}/confirm`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<Attachment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** A 15-minute link to open a VERIFIED attachment. */
  public async getAttachmentViewUrl(
    merchant_id: string,
    attachment_id: string,
  ): Promise<AttachmentViewUrl> {
    const url = `${this.apiEndpoint}/inventory-transfers/${merchant_id}/attachments/${attachment_id}/url`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AttachmentViewUrl> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Service-only (SigV4): an attachment's record, to check a file a task references. */
  public async getAttachment(dto: GetAttachment): Promise<Attachment> {
    const url = `${this.apiEndpoint}/inventory-transfers/${dto.merchant_id}/attachments/${dto.attachment_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<Attachment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getStoreInventories(
    merchant_id: string,
    store_id: string,
  ): Promise<StoreInventoriesResponse> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/inventories`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreInventoriesResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a Store Product.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param updateStoreProductDto - UpdateStoreProduct object
   * @returns Promise object representing the updated StoreProduct
   * @throws Error if the request fails
   * @see StoreProduct
   * @see UpdateStoreProduct
   */
  public async updateStoreProduct({
    merchant_id,
    store_id,
    updateStoreProductDto,
  }: {
    merchant_id: string;
    store_id: string;
    updateStoreProductDto: UpdateStoreProduct;
  }): Promise<StoreProduct> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateStoreProductDto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete a Store Product.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param id - Store Product ID
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteStoreProduct({
    merchant_id,
    store_id,
    id,
    deleted_by_id,
    deleted_by_name,
  }: {
    merchant_id: string;
    store_id: string;
    id: string;
    deleted_by_id: string;
    deleted_by_name: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/products/${merchant_id}/store-products/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: { deleted_by_id, deleted_by_name },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Inventory Changes.
   * @param merchant_id - Merchant ID
   * @param getInventoryChangesDto - GetInventoryChanges object
   * @returns Promise object representing the InventoryChangePage
   */

  public async getInventoryChanges({
    merchant_id,
    getInventoryChangesDto,
  }: {
    merchant_id: string;
    getInventoryChangesDto: GetInventoryChanges;
  }): Promise<InventoryChangePage> {
    let url = `${this.apiEndpoint}/inventory/${merchant_id}/changes`;

    // Create query parameters object
    // Generate query string from params
    const queryString = this.toQueryString(getInventoryChangesDto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryChangePage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Totals of a store's changes that happened before `start_date` but were
   * applied (processed) by `end_date`: they are in the period's closing
   * value snapshot but not in its changes.
   */
  public async getInventoryChangesAppliedLate({
    merchant_id,
    store_id,
    start_date,
    applied_after,
    applied_until,
  }: {
    merchant_id: string;
    store_id: string;
  } & GetInventoryChangesAppliedLate): Promise<InventoryChangesAppliedLate> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/changes/applied-late?${this.toQueryString({ start_date, applied_after, applied_until })}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryChangesAppliedLate> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Backfills the processed-time index on one page of a store's inventory
   * changes. Dry run unless `dry_run` is false; resume with `cursor`.
   */
  public async repairInventoryChangesProcessedIndex({
    merchant_id,
    store_id,
    ...body
  }: RepairInventoryChangesProcessedIndex & {
    merchant_id: string;
    store_id: string;
  }): Promise<RepairInventoryChangesProcessedIndexResult> {
    const config = this.getRequestConfig({
      method: 'POST',
      url: `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/changes/repair-processed-index`,
      data: body,
      extraHeaders: {},
    });

    try {
      const {
        data,
      }: TransportResponse<RepairInventoryChangesProcessedIndexResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Inventory Changes Count (DynamoDB COUNT — fast, no items fetched).
   */
  public async getInventoryChangesCount({
    merchant_id,
    store_id,
    start_date,
    end_date,
    reason,
  }: {
    merchant_id: string;
    store_id: string;
    start_date: string;
    end_date: string;
    reason?: string;
  }): Promise<{ count: number }> {
    let url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/changes/count`;
    const queryParams: Record<string, string> = { start_date, end_date };
    if (reason) queryParams.reason = reason;
    const queryString = this.toQueryString(queryParams);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ count: number }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Daily Count Lists Count (DynamoDB COUNT — fast, no items fetched).
   */
  public async getDailyCountListsCount({
    merchant_id,
    store_id,
    start_date,
    end_date,
  }: {
    merchant_id: string;
    store_id: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ count: number }> {
    let url = `${this.apiEndpoint}/inventory-templates/${merchant_id}/stores/${store_id}/daily-counts/count`;
    const queryParams: Record<string, string> = {};
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;
    const queryString = this.toQueryString(queryParams);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ count: number }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Purchase Orders Count (DynamoDB COUNT — fast, no items fetched).
   */
  public async getPurchaseOrdersCount({
    merchant_id,
    store_id,
    start_date,
    end_date,
  }: {
    merchant_id: string;
    store_id: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ count: number }> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/count`;
    const queryParams: Record<string, string> = {};
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;
    const queryString = this.toQueryString(queryParams);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ count: number }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Debts Count (DynamoDB COUNT — fast, no items fetched).
   */
  public async getDebtsCount({
    merchant_id,
    store_id,
    start_date,
    end_date,
  }: {
    merchant_id: string;
    store_id: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ count: number }> {
    let url = `${this.apiEndpoint}/debts/${merchant_id}/stores/${store_id}/count`;
    const queryParams: Record<string, string> = {};
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;
    const queryString = this.toQueryString(queryParams);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ count: number }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Outstanding Debt Balance As Of Date
   * GET debts/{merchant_id}/stores/{store_id}/outstanding-balance
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param as_of - ISO 8601 date to compute balance as of
   * @returns Promise object representing outstanding balance
   * @throws Error if the request fails
   */
  public async getDebtsOutstandingBalance({
    merchant_id,
    store_id,
    as_of,
  }: {
    merchant_id: string;
    store_id: string;
    as_of: string;
  }): Promise<{ outstanding_balance: number; debt_count: number }> {
    const url = `${this.apiEndpoint}/debts/${merchant_id}/stores/${store_id}/outstanding-balance?as_of=${encodeURIComponent(as_of)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const {
        data,
      }: TransportResponse<{
        outstanding_balance: number;
        debt_count: number;
      }> = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Inventory of a Product.
   * @param merchant_id - Merchant ID
   * @param product_id - Product ID
   * @param store_id - Store ID
   * @returns Promise object representing the CurrentInventory
   */

  public async getInventory({
    merchant_id,
    product_id,
    store_id,
  }: {
    merchant_id: string;
    product_id: string;
    store_id: string;
  }): Promise<CurrentInventory> {
    const url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/products/${product_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<CurrentInventory> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new Inventory Change.
   * @param merchant_id - Merchant ID
   * @param createInventoryChangeDto - CreateInventoryChange object
   * @returns Promise object representing the created CurrentInventory
   */

  public async createInventoryChange({
    merchant_id,
    createInventoryChangeDto,
  }: {
    merchant_id: string;
    createInventoryChangeDto: CreateInventoryChange;
  }): Promise<CurrentInventory> {
    const { store_id, product_id, source_created_at } =
      createInventoryChangeDto;

    const url = `${this.apiEndpoint}/inventory/${merchant_id}/stores/${store_id}/products/${product_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...createInventoryChangeDto,
        source_created_at: source_created_at ?? new Date().toISOString(),
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<CurrentInventory> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a list of Payment Types from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of payment types to return
   * @param stores - Filter by store IDs
   * @returns Promise object representing the PaymentTypePage
   */

  public async getPaymentTypes({
    merchant_id,
    cursor,
    limit = 50,
    stores,
    include_deleted,
  }: GetPaymentTypes): Promise<PaymentTypePage> {
    let url = `${this.apiEndpoint}/payment-types/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit,
      stores: stores?.join(','),
      include_deleted: include_deleted?.toString(),
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PaymentTypePage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Payment Type from the API.
   * @param merchant_id - Merchant ID
   * @param id - Payment Type ID
   * @returns Promise object representing the PaymentType
   */

  public async getPaymentType({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<PaymentType> {
    const url = `${this.apiEndpoint}/payment-types/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a new Payment Type in the API.
   * @param createPaymentTypeDto - CreatePaymentType object
   * @returns Promise object representing the created PaymentType
   */

  public async createPaymentType(
    createPaymentTypeDto: CreatePaymentType,
  ): Promise<PaymentType> {
    const url = `${this.apiEndpoint}/payment-types`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createPaymentTypeDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update an existing Payment Type in the API.
   * @param updatePaymentTypeDto - UpdatePaymentType object
   * @returns Promise object representing the updated PaymentType
   */

  public async updatePaymentType(
    updatePaymentTypeDto: UpdatePaymentType,
  ): Promise<PaymentType> {
    const url = `${this.apiEndpoint}/payment-types`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updatePaymentTypeDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Payment Type from the API.
   * @param merchant_id - Merchant ID
   * @param id - Payment Type ID
   */

  public async deletePaymentType({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/payment-types/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ==========================================================================
  // Platform Payment Types (service-admin)
  // ==========================================================================

  /**
   * Request a WhatsApp OTP for the admin web app login.
   * POST /admin/login/request-code
   */
  public async requestAdminLoginCode(
    dto: RequestAdminLoginCode,
  ): Promise<RequestAdminLoginCodeResponse> {
    const url = `${this.apiEndpoint}/admin/login/request-code`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as RequestAdminLoginCodeResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Verify a WhatsApp OTP and obtain an admin session token.
   * POST /admin/login/verify-code
   */
  public async verifyAdminLoginCode(
    dto: VerifyAdminLoginCode,
  ): Promise<VerifyAdminLoginCodeResponse> {
    const url = `${this.apiEndpoint}/admin/login/verify-code`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as VerifyAdminLoginCodeResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Request a 6-digit admin login code by email.
   * POST /admin/login/email/request-code
   */
  public async requestAdminLoginEmailCode(
    dto: RequestAdminLoginEmailCode,
  ): Promise<RequestAdminLoginCodeResponse> {
    const url = `${this.apiEndpoint}/admin/login/email/request-code`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as RequestAdminLoginCodeResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Verify an emailed admin login code and obtain an admin session token.
   * POST /admin/login/email/verify-code
   */
  public async verifyAdminLoginEmailCode(
    dto: VerifyAdminLoginEmailCode,
  ): Promise<VerifyAdminLoginCodeResponse> {
    const url = `${this.apiEndpoint}/admin/login/email/verify-code`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as VerifyAdminLoginCodeResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Send an email through service-notifications. IAM only: build the
   * adapter with a SigV4 transport (`sigV4RequestSigner`).
   * POST /notifications/send-mail
   */
  public async sendMail(params: EmailParams): Promise<unknown> {
    const url = `${this.apiEndpoint}/notifications/send-mail`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: params,
      extraHeaders: {},
      // aws_iam route: unsigned, API Gateway answers 403.
      auth: 'service',
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Sign in with the admin password (last resort).
   * POST /admin/login/password
   */
  public async verifyAdminLoginPassword(
    dto: VerifyAdminLoginPassword,
  ): Promise<VerifyAdminLoginCodeResponse> {
    const url = `${this.apiEndpoint}/admin/login/password`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as VerifyAdminLoginCodeResponse;
    } catch (error) {
      // No logAxiosError: it would log the request body, i.e. the password.
      throw error;
    }
  }

  /** Whether WhatsApp messages are sent. Public; no record = disabled. */
  public async getWhatsappSettings(): Promise<WhatsappSettingsResponse> {
    const url = `${this.apiEndpoint}/notifications/whatsapp/settings`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as WhatsappSettingsResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Every announcement, newest first. Admin session only. */
  public async getAnnouncements(): Promise<AnnouncementsResponse> {
    const url = `${this.apiEndpoint}/admin/announcements`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as AnnouncementsResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Create an announcement. Admin session only. */
  public async createAnnouncement(
    payload: CreateAnnouncement,
  ): Promise<Announcement> {
    const url = `${this.apiEndpoint}/admin/announcements`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Announcement;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Edit an announcement (409 when `version` is stale). Admin session only. */
  public async updateAnnouncement(
    id: string,
    payload: UpdateAnnouncement,
  ): Promise<Announcement> {
    const url = `${this.apiEndpoint}/admin/announcements/${id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Announcement;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** End an announcement now, for everyone. Admin session only. */
  public async endAnnouncement(id: string): Promise<Announcement> {
    const url = `${this.apiEndpoint}/admin/announcements/${id}/end`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Announcement;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** The announcements live now for a store (public; no ids = all-stores ones). */
  public async getActiveAnnouncements({
    merchant_id,
    store_id,
  }: GetActiveAnnouncements = {}): Promise<ActiveAnnouncementsResponse> {
    let url = `${this.apiEndpoint}/admin/announcements/active`;
    const queryString = this.toQueryString({
      merchant_id: merchant_id ?? undefined,
      store_id: store_id ?? undefined,
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ActiveAnnouncementsResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Turn WhatsApp on or off for the whole platform. Admin session only. */
  public async updateWhatsappSettings(
    payload: UpdateWhatsappSettings,
  ): Promise<WhatsappSettingsResponse> {
    const url = `${this.apiEndpoint}/notifications/whatsapp/settings`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as WhatsappSettingsResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a paginated list of platform payment types.
   * GET /platform-payment-types
   */
  public async getPlatformPaymentTypes({
    cursor,
    limit = 250,
    include_deleted,
  }: GetPlatformPaymentTypes): Promise<PlatformPaymentTypePage> {
    let url = `${this.apiEndpoint}/admin/platform-payment-types`;

    const params: Record<string, unknown> = {
      cursor,
      limit,
      include_deleted: include_deleted?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PlatformPaymentTypePage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single platform payment type by ID.
   * GET /platform-payment-types/{id}
   */
  public async getPlatformPaymentType(
    id: string,
  ): Promise<PlatformPaymentType> {
    const url = `${this.apiEndpoint}/admin/platform-payment-types/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PlatformPaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a new platform payment type.
   * POST /platform-payment-types
   */
  public async createPlatformPaymentType(
    dto: CreatePlatformPaymentType,
  ): Promise<PlatformPaymentType> {
    const url = `${this.apiEndpoint}/admin/platform-payment-types`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PlatformPaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update an existing platform payment type.
   * PUT /platform-payment-types/{id}
   */
  public async updatePlatformPaymentType(
    dto: UpdatePlatformPaymentType,
  ): Promise<PlatformPaymentType> {
    const url = `${this.apiEndpoint}/admin/platform-payment-types/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PlatformPaymentType;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a platform payment type (soft delete).
   * DELETE /platform-payment-types/{id}
   */
  public async deletePlatformPaymentType(id: string): Promise<void> {
    const url = `${this.apiEndpoint}/admin/platform-payment-types/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of Discounts from the API.
   * @param store_id - Store ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of discounts to return
   * @returns Promise object representing the DiscountPage
   */

  public async getDiscounts({
    store_id,
    cursor,
    limit = 50,
    include_manual,
  }: GetDiscounts): Promise<DiscountPage> {
    let url = `${this.apiEndpoint}/discounts/${store_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit,
      include_manual,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as DiscountPage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Discount from the API.
   * @param store_id - Store ID
   * @param id - Discount ID
   * @returns Promise object representing the Discount
   */

  public async getDiscount({
    store_id,
    id,
  }: {
    store_id: string;
    id: string;
  }): Promise<Discount> {
    const url = `${this.apiEndpoint}/discounts/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Discount;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a new Discount in the API.
   * @param createDiscountDto - CreateDiscount object
   * @returns Promise object representing the created Discount
   */

  public async createDiscount(
    createDiscountDto: CreateDiscount,
  ): Promise<Discount> {
    const url = `${this.apiEndpoint}/discounts`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createDiscountDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Discount;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update an existing Discount in the API.
   * @param updateDiscountDto - UpdateDiscount object
   * @returns Promise object representing the updated Discount
   */

  public async updateDiscount(
    updateDiscountDto: UpdateDiscount,
  ): Promise<Discount> {
    const url = `${this.apiEndpoint}/discounts`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateDiscountDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Discount;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async setProductDiscount(
    assignment: SetProductDiscount,
  ): Promise<ProductDiscountAssignment> {
    const url = `${this.apiEndpoint}/discounts/product-assignment`;
    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: assignment,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ProductDiscountAssignment;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Discount from the API.
   * @param store_id - Store ID
   * @param id - Discount ID
   */

  public async deleteDiscount({
    store_id,
    id,
    merchant_id,
    updator_id,
  }: {
    store_id: string;
    id: string;
    merchant_id?: string;
    updator_id?: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/discounts/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: { merchant_id, updator_id },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Calculate the discount based on item price and discount type.
   * @param calculateDiscountDto - CalculateDiscount object
   * @returns Promise object representing the calculated discount
   */

  public async calculateDiscount(
    calculateDiscountDto: CalculateDiscount,
  ): Promise<CalculateDiscountResult> {
    const url = `${this.apiEndpoint}/discounts/calculate`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: calculateDiscountDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as CalculateDiscountResult;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of POS Devices from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of devices to return
   * @param store_id - Store ID
   * @returns Promise object representing the PosDevicePage
   */

  public async getPOSDevices({
    merchant_id,
    cursor,
    limit = 50,
    store_id,
  }: GetPosDevicesByMerchant): Promise<PosDevicePage> {
    let url = `${this.apiEndpoint}/pos-devices/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit,
      store_id,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevicePage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single POS Device from the API.
   * @param merchant_id - Merchant ID
   * @param id - POS Device ID
   * @returns Promise object representing the PosDevice
   */

  public async getPOSDevice({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<PosDevice> {
    const url = `${this.apiEndpoint}/pos-devices/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevice;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a new POS Device in the API.
   * @param createPosDeviceDto - CreatePosDevice object
   * @returns Promise object representing the created PosDevice
   */
  public async createPOSDevice(
    createPosDeviceDto: CreatePosDevice,
  ): Promise<PosDevice> {
    const url = `${this.apiEndpoint}/pos-devices/${createPosDeviceDto.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createPosDeviceDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevice;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update an existing POS Device in the API.
   * @param updatePosDeviceDto - UpdatePosDevice object
   * @returns Promise object representing the updated PosDevice
   */
  public async updatePOSDevice(
    updatePosDeviceDto: UpdatePosDevice,
  ): Promise<PosDevice> {
    const url = `${this.apiEndpoint}/pos-devices/${updatePosDeviceDto.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updatePosDeviceDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevice;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Patch a POS device's feature flags. The body's `feature_flags` is
   * merged onto the device's existing flags server-side, so callers can
   * send only the keys they want to change.
   */
  public async updatePOSDeviceFeatureFlags(
    req: UpdatePosDeviceFeatureFlagsRequest,
  ): Promise<PosDevice> {
    const url = `${this.apiEndpoint}/pos-devices/${req.merchant_id}/${req.id}/feature-flags`;

    const config = this.getRequestConfig({
      method: 'PATCH',
      url,
      data: req,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevice;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Set or clear (`geofence: null`) the sales location limit of a POS
   * device.
   */
  public async updatePOSDeviceGeofence(
    req: UpdatePosDeviceGeofenceRequest,
  ): Promise<PosDevice> {
    const url = `${this.apiEndpoint}/pos-devices/${req.merchant_id}/${req.id}/geofence`;

    const config = this.getRequestConfig({
      method: 'PATCH',
      url,
      data: req,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosDevice;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Paginated CLAIM / EVICT / HEARTBEAT_FAIL history for a single POS
   * device (any user). Newest events first.
   */
  public async listSessionEventsByDevice({
    pos_device_id,
    cursor,
    limit = 50,
  }: {
    pos_device_id: string;
    cursor?: string;
    limit?: number;
  }): Promise<PosSessionEventsPage> {
    let url = `${this.apiEndpoint}/pos-devices/sessions/history/by-device/${pos_device_id}`;
    const queryString = this.toQueryString({ cursor, limit });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PosSessionEventsPage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a POS Device from the API.
   * @param merchant_id - Merchant ID
   * @param id - POS Device ID
   */
  public async deletePOSDevice({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/pos-devices/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Clear a user from every POS device they currently hold. Used when an
   * admin deactivates a user account so no stale claims survive.
   * @param user_id - The user being signed out from all devices
   * @returns Promise resolving to the count and list of evicted devices
   */
  public async evictUserFromAllPOSDevices({
    user_id,
  }: {
    user_id: string;
  }): Promise<{ evicted_count: number; evicted: PosDevice[] }> {
    const url = `${this.apiEndpoint}/pos-devices/sessions/evict-user`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { user_id },
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as { evicted_count: number; evicted: PosDevice[] };
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a receipt threshold
   * @param createReceiptThresholdDto - CreateReceiptThreshold object
   * @returns Promise object representing the created ReceiptThreshold
   */

  public async createStoreThreshold(
    createThresholdDto: CreateThreshold,
  ): Promise<Threshold> {
    const url = `${this.apiEndpoint}/receipts/${createThresholdDto.merchant_id}/thresholds`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createThresholdDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);

      return data as Threshold;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a store threshold
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @returns Promise object representing the Threshold
   */

  public async getStoreThreshold({
    merchant_id,
    store_id,
  }: {
    merchant_id: string;
    store_id: string;
  }): Promise<Threshold> {
    const url = `${this.apiEndpoint}/receipts/${merchant_id}/thresholds/${store_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Threshold;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update a store threshold
   * @param updateThresholdDto - UpdateThreshold object
   * @returns Promise object representing the updated Threshold
   */

  public async updateStoreThreshold(
    updateThresholdDto: UpdateThreshold,
  ): Promise<Threshold> {
    const url = `${this.apiEndpoint}/receipts/${updateThresholdDto.merchant_id}/thresholds`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateThresholdDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Threshold;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a list of Suppliers from the API.
   * @param merchant_id - Merchant ID
   * @param cursor - Cursor returned from a previous request
   * @param limit - Number of suppliers to return
   * @returns Promise object representing the SupplierPage
   */

  public async getSuppliers({
    merchant_id,
    cursor,
    limit = 50,
    include_deleted,
  }: GetSuppliers): Promise<SupplierPage> {
    let url = `${this.apiEndpoint}/suppliers/${merchant_id}`;

    // Create query parameters object
    const params = {
      cursor,
      limit,
      include_deleted,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as SupplierPage;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Supplier from the API.
   * @param merchant_id - Merchant ID
   * @param id - Supplier ID
   * @returns Promise object representing the Supplier
   */

  public async getSupplier({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<Supplier> {
    const url = `${this.apiEndpoint}/suppliers/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Supplier;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a new Supplier in the API.
   * @param createSupplierDto - CreateSupplier object
   * @returns Promise object representing the created Supplier
   */

  public async createSupplier(
    createSupplierDto: CreateSupplier,
  ): Promise<Supplier> {
    const url = `${this.apiEndpoint}/suppliers`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: createSupplierDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Supplier;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update an existing Supplier in the API.
   * @param updateSupplierDto - UpdateSupplier object
   * @returns Promise object representing the updated Supplier
   */

  public async updateSupplier(
    updateSupplierDto: UpdateSupplier,
  ): Promise<Supplier> {
    const url = `${this.apiEndpoint}/suppliers`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: updateSupplierDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Supplier;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Supplier from the API.
   * @param merchant_id - Merchant ID
   * @param id - Supplier ID
   */

  public async deleteSupplier({
    merchant_id,
    id,
  }: {
    merchant_id: string;
    id: string;
  }): Promise<void> {
    const url = `${this.apiEndpoint}/suppliers/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Authenticate a user
   * @param authenticateUserDto - AuthenticateUserRequest object
   * @returns Promise object representing the authenticated User
   * @throws Error if the request fails
   */

  public async authenticateUser(
    authenticateUserDto: AuthenticateUserRequest,
  ): Promise<AuthenticateUserResponse> {
    const url = `${this.apiEndpoint}/authentications/authenticate`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: authenticateUserDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as AuthenticateUserResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Decrypt a user data
   * @param decryptUserDataDto - DecryptUserDataRequest object
   * @returns Promise object representing the decrypted User
   * @throws Error if the request fails
   */

  public async decryptUserData({
    encryptedCredentials,
  }: DecryptCredentialsRequestBody): Promise<EncryptCredentialsDto> {
    const url = `${this.apiEndpoint}/authentications/decrypt`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { encryptedToken: encryptedCredentials },
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as EncryptCredentialsDto;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Sign out a user
   * @param signOutUserDto - SignOutRequestBody object
   * @returns Promise object representing the signed out User
   */

  public async signOutUser(
    signOutUserDto: SignOutUserRequestBody,
  ): Promise<SignOutUserResponse> {
    const url = `${this.apiEndpoint}/authentications/sign-out`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: signOutUserDto,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as SignOutUserResponse;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all the resources
   * @param numItems - number of resources to get
   * @param lastCursor - last read cursor
   * @returns Promise object representing the resources fetched
   * @see ResourceList
   */

  public async getResources(
    numItems: number,
    lastCursor?: string,
  ): Promise<ResourceList> {
    let url = `${this.apiEndpoint}/authorizations/resources`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ResourceList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a principal by id
   * @param principalId - the id of the principal
   * @returns Promise object representing the principal fetched
   */

  public async getPrincipal(
    principalId: string,
  ): Promise<Principal | undefined> {
    const url = `${this.apiEndpoint}/authorizations/principals/${principalId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Principal;
    } catch (error) {
      this.logAxiosError(error);
      return undefined;
    }
  }

  /**
   * Get All Principals V2
   * GET authorizations-v2/principals
   * @param dto - GetAllPrincipalsV2
   * @returns Promise object representing the PrincipalIdsPageV2
   */

  public async getAllPrincipalsByRoleV2(
    dto: GetAllPrincipalsByRoleV2,
  ): Promise<PrincipalIdsPageV2> {
    let url = `${this.apiEndpoint}/authorizations-v2/principals`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PrincipalIdsPageV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get an Action by id
   * @param actionId - the id of the action
   * @returns Promise object representing the action fetched
   */

  public async getAction(actionId: string): Promise<Action> {
    const url = `${this.apiEndpoint}/authorizations/actions/${actionId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Action;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a Resource by id
   * @param resourceId - the id of the resource
   * @returns Promise object representing the resource fetched
   */

  public async getResource(resourceId: string): Promise<Resource> {
    const url = `${this.apiEndpoint}/authorizations/resources/${resourceId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Resource;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a Policy by id
   * @param policyId - the id of the policy
   * @returns Promise object representing the policy fetched
   */

  public async getPolicy(policyId: string): Promise<Policy> {
    const url = `${this.apiEndpoint}/authorizations/policies/${policyId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Policy;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a Role by id
   * @param roleId - the id of the role
   * @returns Promise object representing the role fetched
   */

  public async getRole(roleId: string): Promise<Role> {
    const url = `${this.apiEndpoint}/authorizations/roles/${roleId}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as Role;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all the actions
   * @param numItems - number of actions to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the actions fetched
   */

  public async getActions(
    numItems: number,
    lastCursor?: string,
  ): Promise<ActionList> {
    let url = `${this.apiEndpoint}/authorizations/actions`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ActionList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all policies
   * @param resourceId - the id of the resource
   * @param numItems - number of policies to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the policies fetched
   */

  public async getPolicies(
    numItems: number,
    lastCursor?: string,
  ): Promise<PolicyList> {
    let url = `${this.apiEndpoint}/authorizations/policies`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PolicyList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all policies with resource
   * @param resourceId - the id of the resource
   * @param numItems - number of policies to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the actions fetched
   */

  public async getPoliciesWithResource(
    resourceId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<PolicyList> {
    let url = `${this.apiEndpoint}/authorizations/resources/${resourceId}/policies`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PolicyList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all policies in role
   * @param roleId - the id of the role
   * @param numItems - number of policies to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the policies fetched
   */

  public async getPoliciesInRole(
    roleId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<PolicyList> {
    let url = `${this.apiEndpoint}/authorizations/roles/${roleId}/policies`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PolicyList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all roles with policy
   * @param policyId - the id of the policy
   * @param numItems - number of policies to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the roles fetched
   */

  public async getRolesWithPolicy(
    policyId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<RoleList> {
    let url = `${this.apiEndpoint}/authorizations/policies/${policyId}/roles`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as RoleList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all roles
   * @param merchantId - the id of the merchant
   * @param numItems - number of roles to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the roles fetched
   */

  public async getRoles(
    merchantId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<RoleList> {
    let url = `${this.apiEndpoint}/authorizations/merchants/${merchantId}/roles`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as RoleList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all resources in app
   * @param appName - the name of the app
   * @param numItems - number of resources to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the resources fetched
   */

  public async getResourcesInApp(
    appName: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<ResourceList> {
    let url = `${this.apiEndpoint}/authorizations/apps/${appName}/resources`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ResourceList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Principal
   * @param principalId - principal id
   * @param merchantId - merchant id
   * @returns Promise object representing the principal created
   * @throws Error if the request fails
   */

  public async createPrincipal(
    principalId: string,
    merchantId: string,
  ): Promise<Principal> {
    const url = `${this.apiEndpoint}/authorizations/principals`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        principal_id: principalId,
        merchant_id: merchantId,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Principal> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create an Action
   * @param description - Action description
   * @param verb - Action verb
   * @returns Promise object representing the created Action
   * @throws Error if the request fails
   */

  public async createAction(
    description: string,
    verb: string,
    resourceId: string,
  ): Promise<Action> {
    const url = `${this.apiEndpoint}/authorizations/actions`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        description,
        verb,
        resource_id: resourceId,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Action> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Assign a Role to a Principal
   * @param principalId - principal id
   * @param roleId - role id
   * @returns Promise object representing the principal
   * @throws Error if the request fails
   */

  public async assignRole(
    principalId: string,
    roleId: string,
  ): Promise<Principal> {
    const url = `${this.apiEndpoint}/authorizations/principals/${principalId}/roles`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        role_id: roleId,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Principal> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a Resource
   * @param description - Resource description
   * @param path - Resource path
   * @returns Promise object representing the created Resource
   * @throws Error if the request fails
   */

  public async createResource(
    description: string,
    path: string,
  ): Promise<Resource> {
    const url = `${this.apiEndpoint}/authorizations/resources`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        description,
        path,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Resource> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a Policy
   * @param description - Resource description
   * @param actionId - id of the action
   * @param resourceId - id of the resource
   * @returns Promise object representing the created Policy
   * @throws Error if the request fails
   */

  public async createPolicy(
    description: string,
    merchantId: string,
    actionId: string,
    resourceId: string,
  ): Promise<Policy> {
    const url = `${this.apiEndpoint}/authorizations/policies`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        description,
        merchant_id: merchantId,
        resource_id: resourceId,
        action_id: actionId,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Policy> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create multiple policies.
   *
   * This method calls the endpoint `${this.apiEndpoint}/authorizations/policies/multiple`
   * using a POST request with the given payload. The payload must adhere to the
   * MultiplePolicyBody interface, containing a merchant_id and a list of policy items.
   *
   * @param body - The request payload containing a merchant_id and an array of policy items.
   * @returns A promise that resolves to a MultiplePolicyList containing the created policies and meta information.
   *
   * @throws Error if the request fails.
   */
  public async createMultiplePolicies(
    body: MultiplePolicyBody,
  ): Promise<MultiplePolicyList> {
    const url = `${this.apiEndpoint}/authorizations/policies/multiple`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MultiplePolicyList> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Role
   * @param description - description
   * @param merchantId - merchant id
   * @returns Promise object representing the role created
   * @throws Error if the request fails
   */

  public async createRole(
    description: string,
    merchantId: string,
  ): Promise<Role> {
    const url = `${this.apiEndpoint}/authorizations/roles`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        description,
        merchant_id: merchantId,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Role> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update an action
   * @param actionId - action id
   * @param description - description
   * @param verb - verb
   * @returns Promise object representing the Action
   * @throws Error if the request fails
   * @see Action
   */

  public async updateAction(
    actionId: string,
    description: string,
    verb: string,
  ): Promise<Action> {
    const url = `${this.apiEndpoint}/authorizations/actions/${actionId}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        description,
        verb,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Action> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a resource
   * @param resourceId - resource id
   * @param description - description
   * @param path - resource path
   * @returns Promise object representing the Resource
   * @throws Error if the request fails
   * @see Resource
   */

  public async updateResource(
    resourceId: string,
    description: string,
    path: string,
  ): Promise<Resource> {
    const url = `${this.apiEndpoint}/authorizations/resources/${resourceId}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        description,
        path,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Resource> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a policy
   * @param policyId - resource id
   * @param description - description
   * @param actionId - action id
   * @param resourceId - resource id
   * @returns Promise object representing the Policy
   * @throws Error if the request fails
   * @see Policy
   */

  public async updatePolicy(
    policyId: string,
    description: string,
    actionId: string,
    resourceId: string,
  ): Promise<Policy> {
    const url = `${this.apiEndpoint}/authorizations/policies/${policyId}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        action_id: actionId,
        resource_id: resourceId,
        description,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Policy> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete an Action
   * @param action_id - action id
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteAction(actionId: string): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/actions/${actionId}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Resource
   * @param resourceId - resource id
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteResource(resourceId: string): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/resources/${resourceId}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Policy
   * @param policyId - policy id
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deletePolicy(policyId: string): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/policies/${policyId}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Unassign a role
   * @param principalId - principal id
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async unassignRole(principalId: string): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/principals/${principalId}/roles`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Remove Policies from Role
   * @param roleId - role id
   * @param policies - list of policy ids that need to be removed
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async removePoliciesFromRole(
    roleId: string,
    policies: string[],
  ): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/roles/${roleId}/policies`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {
        policies,
      },
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Add policies to role
   * @param roleId - role id
   * @param policies - list of policy id's to be added to the role
   * @returns Promise object representing the updated role
   * @throws Error if the request fails
   */

  public async addPoliciesToRole(
    roleId: string,
    policies: string[],
  ): Promise<Role> {
    const url = `${this.apiEndpoint}/authorizations/roles/${roleId}/policies`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        policies,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Role> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get a Resource With Path
   * @param path - Resource path
   * @returns Promise object representing Resource
   * @throws Error if the request fails
   */

  public async getResourceWithPath(path: string): Promise<Resource> {
    const url = `${this.apiEndpoint}/authorizations/resources/paths`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        path_name: path,
      },
      extraHeaders: {},
    });

    const { data }: TransportResponse<Resource> =
      await this.transport.send(config);
    return data;
  }

  /**
   * Get all the actions in a resource
   * @param resourceId - the id of the resource
   * @param numItems - number of actions to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the actions fetched
   */

  public async getActionsInResource(
    resourceId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<ActionList> {
    let url = `${this.apiEndpoint}/authorizations/resources/${resourceId}/actions`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ActionList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all the principals in a role
   * @param roleId - the id of the role
   * @param numItems - number of principals to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the principals fetched
   */

  public async getPrincipalsInRole(
    roleId: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<PrincipalList> {
    let url = `${this.apiEndpoint}/authorizations/roles/${roleId}/principals`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PrincipalList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update a role
   * @param roleId - the id of the role to update
   * @param description - the new description
   * @returns Promise object representing the Role
   * @throws Error if the request fails
   * @see Role
   */

  public async updateRoleDescription(
    roleId: string,
    description: string,
  ): Promise<Role> {
    const url = `${this.apiEndpoint}/authorizations/roles/${roleId}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        description,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Role> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Delete role
   * @param roleId - role id
   * @returns Promise object
   * @throws Error if the request fails
   */
  public async deleteRole(roleId: string): Promise<void> {
    const url = `${this.apiEndpoint}/authorizations/roles/${roleId}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all actions with a description
   * @param description - the description
   * @param numItems - number of actions to get
   * @param last_cursor - last read cursor
   * @returns Promise object representing the actions fetched
   */
  public async getActionsWithDescription(
    description: string,
    numItems: number,
    lastCursor?: string,
  ): Promise<ActionList> {
    let url = `${this.apiEndpoint}/authorizations/descriptions/${encodeURIComponent(description)}`;

    const params = {
      num_items: numItems?.toString(),
      last_cursor: lastCursor,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as ActionList;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Purchase Order
   * POST {merchant_id}
   */
  public async createPurchaseOrder(
    merchant_id: string,
    dto: CreatePurchaseOrder,
  ): Promise<PurchaseOrder> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrder> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Purchase Order
   * GET {merchant_id}/stores/{store_id}/{id}
   */
  public async getPurchaseOrder(
    merchant_id: string,
    store_id: string,
    id: string,
  ): Promise<PurchaseOrder> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrder> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Receive items for a Purchase Order
   * POST {merchant_id}/receive
   */
  public async receivePurchaseOrderItems(
    merchant_id: string,
    dto: ReceivePurchaseOrder,
  ): Promise<PurchaseOrder> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/receive`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrder> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Confirm a complete line's selling prices and save its profit estimate
   * PUT {merchant_id}/stores/{store_id}/{id}/items/{item_id}/estimate
   */
  public async savePurchaseOrderItemEstimate(
    dto: SavePurchaseOrderItemEstimate,
  ): Promise<PurchaseOrder> {
    const { merchant_id, store_id, id, item_id } = dto;
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/${id}/items/${item_id}/estimate`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrder> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Expiry warnings of a store: undismissed purchase-order lines whose
   * product still has stock (earliest date per product).
   * GET {merchant_id}/stores/{store_id}/expiries
   */
  public async getProductExpiries(
    params: GetProductExpiries,
  ): Promise<ActiveProductExpiryPage> {
    const { merchant_id, store_id, ...query } = params;
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/expiries`;
    const queryString = this.toQueryString(query);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ActiveProductExpiryPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Dismiss one expiry warning for good (per store).
   * POST {merchant_id}/stores/{store_id}/expiries/{po_id}/{item_id}/dismiss
   */
  public async dismissProductExpiry(
    dto: DismissProductExpiry,
  ): Promise<ProductExpiry> {
    const { merchant_id, store_id, po_id, item_id } = dto;
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/expiries/${po_id}/${item_id}/dismiss`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ProductExpiry> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update a Purchase Order
   * PUT {merchant_id}
   */
  public async updatePurchaseOrder(
    merchant_id: string,
    dto: UpdatePurchaseOrder,
  ): Promise<PurchaseOrder> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrder> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all Purchase Orders
   * GET {merchant_id} with query parameters
   */
  public async getAllPurchaseOrders(
    merchant_id: string,
    params: GetAllPurchaseOrders,
  ): Promise<PurchaseOrderPage> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}`;
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrderPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete a Purchase Order
   * DELETE {merchant_id}/stores/{store_id}/{id}
   */
  public async deletePurchaseOrder(
    merchant_id: string,
    store_id: string,
    id: string,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/stores/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create a Purchase Order Payment
   * POST {merchant_id}/payments
   */
  public async createPurchaseOrderPayment(
    merchant_id: string,
    dto: CreatePurchaseOrderPayment,
  ): Promise<PurchaseOrderPayment> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payments`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrderPayment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single Purchase Order Payment
   * GET {merchant_id}/payments/stores/{store_id}/{id}
   */
  public async getPurchaseOrderPayment(
    merchant_id: string,
    store_id: string,
    id: string,
  ): Promise<PurchaseOrderPayment> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payments/stores/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrderPayment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get all Purchase Order Payments
   * GET {merchant_id}/payments with query parameters
   */
  public async getAllPurchaseOrderPayments(
    merchant_id: string,
    params: GetAllPurchaseOrderPayments,
  ): Promise<PurchaseOrderPaymentPage> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payments`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrderPaymentPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a store PO payment snapshot at a point in time.
   * GET {merchant_id}/payment-snapshots/store with query parameters
   */
  public async getStorePOPaymentSnapshotAtTime(
    merchant_id: string,
    params: GetStoreSnapshotAtTime,
  ): Promise<POPaymentStoreSnapshot | null> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payment-snapshots/store`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<POPaymentStoreSnapshot | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get store PO payment snapshots in a date range.
   * GET {merchant_id}/payment-snapshots/store/range with query parameters
   */
  public async getStorePOPaymentSnapshotsInRange(
    merchant_id: string,
    params: GetStoreSnapshotsInRange,
  ): Promise<POPaymentStoreSnapshotPage> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payment-snapshots/store/range`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<POPaymentStoreSnapshotPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a supplier PO payment snapshot at a point in time.
   * GET {merchant_id}/payment-snapshots/supplier with query parameters
   */
  public async getSupplierPOPaymentSnapshotAtTime(
    merchant_id: string,
    params: GetSupplierSnapshotAtTime,
  ): Promise<POPaymentSupplierSnapshot | null> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payment-snapshots/supplier`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<POPaymentSupplierSnapshot | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get supplier PO payment snapshots in a date range.
   * GET {merchant_id}/payment-snapshots/supplier/range with query parameters
   */
  public async getSupplierPOPaymentSnapshotsInRange(
    merchant_id: string,
    params: GetSupplierSnapshotsInRange,
  ): Promise<POPaymentSupplierSnapshotPage> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/payment-snapshots/supplier/range`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<POPaymentSupplierSnapshotPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reconcile PO payment tracking fields (migration).
   * POST {merchant_id}/reconcile-payments
   */
  public async reconcilePurchaseOrderPayments(
    merchant_id: string,
    store_id: string,
  ): Promise<ReconcilePaymentFieldsResponse> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/reconcile-payments`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { store_id },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ReconcilePaymentFieldsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Backfill PO payment snapshots for a single PO.
   * POST {merchant_id}/backfill-payment-snapshots
   */
  public async backfillPOPaymentSnapshots(
    merchant_id: string,
    dto: BackfillPOPaymentSnapshotsRequest,
  ): Promise<BackfillPOPaymentSnapshotsResponse> {
    const url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/backfill-payment-snapshots`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<BackfillPOPaymentSnapshotsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Purchase Order Receive Events
   * GET purchase-orders/{merchant_id}/receive-events
   */
  public async getPurchaseOrderReceiveEvents(
    merchant_id: string,
    params: GetPurchaseOrderReceiveEvents,
  ): Promise<PurchaseOrderReceiveEventPage> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/receive-events`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PurchaseOrderReceiveEventPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get supplier valuation metrics for a date range.
   * GET {merchant_id}/supplier-valuation
   */
  public async getSupplierValuation(
    merchant_id: string,
    params: GetSupplierValuation,
  ): Promise<SupplierValuation> {
    let url = `${this.apiEndpoint}/purchase-orders/${merchant_id}/supplier-valuation`;
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<SupplierValuation> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Store Inventory Value
   * POST {merchant_id}/inventory-value?store_id={store_id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @returns Promise object representing the InventoryValue
   * @throws Error if the request fails
   * @see StoreInventoryValue
   */

  public async updateStoreInventoryValue(
    merchant_id: string,
    store_id: string,
  ): Promise<StoreInventoryValue> {
    const url = `${this.apiEndpoint}/reports/${merchant_id}/inventory-value?store_id=${store_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<StoreInventoryValue> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Store Inventory Value
   * GET {merchant_id}/inventory-value/{store_id}?date={date}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param date - date
   * @returns Promise object representing the InventoryValue
   * @throws Error if the request fails
   */

  public async getStoreInventoryValue({
    date,
    merchant_id,
    store_id,
  }: {
    merchant_id: string;
    store_id: string;
    date: string;
  }): Promise<StoreInventoryValue> {
    const url = `${this.apiEndpoint}/reports/${merchant_id}/inventory-value/${store_id}?date=${date}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreInventoryValue> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Store Inventory Value Date Range
   * GET reports/{merchant_id}/inventory-value/{store_id}/date-range?from={from}&to={to}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param from - start date (ISO 8601)
   * @param to - end date (ISO 8601)
   * @returns Promise object representing inventory value changes in the date range
   * @throws Error if the request fails
   */

  public async getStoreInventoryValueDateRange({
    merchant_id,
    store_id,
    from,
    to,
    max_points,
  }: GetStoreInventoryValueDateRange): Promise<StoreInventoryValueDateRangePage> {
    const queryString = this.toQueryString({ from, to, max_points }, true);
    const url = `${this.apiEndpoint}/reports/${merchant_id}/inventory-value/${store_id}/date-range?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreInventoryValueDateRangePage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * 
   * export interface GetSalesReport {
  merchant_id: string;
  store_id: string;
  products?: string[];
  from?: string;
  to?: string;
  date?: string;
  by_date?: boolean;
  by_date_range?: boolean;
  by_products_and_date?: boolean;
  by_products_and_date_range?: boolean;
}
   */

  /**
   * Get Sales Report
   * GET reports/{merchant_id}/sales/{store_id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param products - products
   * @param from - from
   * @param to - to
   * @param date - date
   * @param by_date - by date
   * @param by_date_range - by date range
   * @param by_products_and_date - by products and date
   * @param by_products_and_date_range - by products and date range
   * @returns Promise object representing the SalesReport
   * @throws Error if the request fails
   */

  public async getSalesReport(
    dto: GetSalesReport,
  ): Promise<AggregateSalesReport> {
    let url = `${this.apiEndpoint}/reports/${dto.merchant_id}/sales/${dto.store_id}`;

    // To query string

    const queryString = this.toQueryString(dto, true);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<AggregateSalesReport> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Sales Report Events
   * GET reports/{merchant_id}/sales/events
   * @param dto - GetSalesReportEvents
   * @returns Promise object representing the ReportEventsPage
   * @throws Error if the request fails
   */

  public async getSalesReportEvents(
    dto: GetReportEvents,
  ): Promise<ReportEventsPage> {
    let url = `${this.apiEndpoint}/reports/${dto.merchant_id}/sales/events`;

    // To query string

    const queryString = this.toQueryString(dto, true);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<ReportEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Add Receipt to Report
   * POST reports/{merchant_id}/add/{receipt_id}
   * @param merchant_id - merchant id
   * @param receipt_id - receipt id
   */

  public async addReceiptToReport(
    merchant_id: string,
    receipt_id: string,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/reports/${merchant_id}/add/${receipt_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Add Missing Receipts to Report For Store
   * POST reports/{merchant_id}/add-missing/{store_id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param from - from
   * @param to - to
   * @returnes Promise object representing the ShopAndSmileOkResponse
   */

  public async addMissingReceiptsToReportForStore({
    merchant_id,
    store_id,
    from,
    to,
  }: {
    merchant_id: string;
    store_id: string;
    from: Date;
    to: Date;
  }): Promise<ShopAndSmileOkResponse> {
    let url = `${this.apiEndpoint}/reports/${merchant_id}/add-missing/${store_id}`;

    // To query string
    const queryString = this.toQueryString(
      { start_date: from.toISOString(), end_date: to.toISOString() },
      true,
    );
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<ShopAndSmileOkResponse> =
        await this.transport.send(config);
      console.log('🚀 ~ addMissingReceiptsToReportForStore ~ data:', data);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Sales Report By Interval
   * GET reports/{merchant_id}/sales/{store_id}/interval/{entity_type}/{entity_id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param entity_type - entity type
   * @param entity_id - entity id
   * @param from - from
   * @param to - to
   * @param is_cumulative - is cumulative
   * @param interval - interval (daily, weekly, monthly)
   * @returns Promise object representing the SalesReport
   */

  public async getSalesReportByInterval({
    dto,
    merchant_id,
    store_id,
  }: {
    dto: GetSalesReportByDateRangeIntervalOptions;
    store_id: string;
    merchant_id: string;
  }): Promise<
    Array<
      | IntervalAggregateReport<AggregateStoreSalesReport>
      | IntervalAggregateReport<AggregateEmployeeSalesReport>
      | IntervalAggregateReport<AggregateProductSalesReport>
    >
  > {
    let url = `${this.apiEndpoint}/reports/${merchant_id}/sales/${store_id}/intervals/${dto.entityType}/${dto.entityId}`;

    const { from, to, is_cumulative, interval } = dto;

    // To query string

    const queryString = this.toQueryString(
      { from, to, is_cumulative, interval },
      true,
    );
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const {
        data,
      }: TransportResponse<
        Array<
          | IntervalAggregateReport<AggregateStoreSalesReport>
          | IntervalAggregateReport<AggregateEmployeeSalesReport>
          | IntervalAggregateReport<AggregateProductSalesReport>
        >
      > = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Products Inventory Value
   * GET reports/{merchant_id}/inventory-value/{store_id}/products
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param date - date
   * @param cursor - cursor
   * @param limit - limit
   * @returns Promise object representing the InventoryValue
   * @throws Error if the request fails
   */

  public async getProductsInventoryValue(
    dto: GetProductsInventoryValue,
  ): Promise<ProductInventoryValuePage> {
    let url = `${this.apiEndpoint}/reports/${dto.merchant_id}/inventory-value/${dto.store_id}/products`;

    // To query string

    const queryString = this.toQueryString(
      {
        date: dto.date,
        limit: dto.limit,
        cursor: dto.cursor,
      },
      true,
    );
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<ProductInventoryValuePage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the audit trail / timeline of actions performed by an employee within a date range.
   * GET reports/{merchant_id}/employee-timeline
   * @param dto - GetEmployeeTimeline
   * @returns Promise object representing the AuditTrailPage
   * @throws Error if the request fails
   */
  public async getEmployeeTimeline(
    dto: GetEmployeeTimeline,
  ): Promise<AuditTrailPage> {
    let url = `${this.apiEndpoint}/reports/${dto.merchant_id}/employee-timeline`;

    const queryString = this.toQueryString({
      employee_id: dto.employee_id,
      start_date: dto.start_date,
      end_date: dto.end_date,
      store_id: dto.store_id,
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<AuditTrailPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The store's Activity Timeline: every recorded change, newest first
   * (docs/plans/concurrency-safe-writes.md §4.3).
   * GET history/{merchant_id}/stores/{store_id}/activities
   * @param dto - GetActivities
   * @returns Promise object representing the ActivityPage
   * @throws Error if the request fails
   */
  public async getActivities(dto: GetActivities): Promise<ActivityPage> {
    let url = `${this.apiEndpoint}/history/${dto.merchant_id}/stores/${dto.store_id}/activities`;

    const queryString = this.toQueryString({
      from: dto.from,
      to: dto.to,
      user_id: dto.user_id,
      entity_type: dto.entity_type,
      include_automatic: dto.include_automatic,
      cursor: dto.cursor,
      limit: dto.limit,
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      extraHeaders: {},
      data: {},
    });

    try {
      const { data }: TransportResponse<ActivityPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create an Inventory Template
   * POST /inventory-templates/{merchant_id}
   * @param merchant_id - merchant id
   * @param dto - CreateInventoryTemplate
   * @returns Promise object representing the InventoryTemplate
   * @throws Error if the request fails
   */

  public async createInventoryTemplate(
    merchant_id: string,
    dto: CreateInventoryTemplate,
  ): Promise<InventoryTemplate> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryTemplate> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get An Inventory Template
   * GET /inventory-templates/{merchant_id}/{store_id}/{id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param id - id
   * @returns Promise object representing the InventoryTemplate
   * @throws Error if the request fails
   */

  public async getInventoryTemplate(
    merchant_id: string,
    store_id: string,
    id: string,
  ): Promise<InventoryTemplate> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryTemplate> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All Inventory Templates
   * GET /inventory-templates/{merchant_id}
   * @param merchant_id - merchant id
   * @param GetInventoryTemplates - GetInventoryTemplates options
   * @returns Promise object representing the InventoryTemplatesPage
   * @throws Error if the request fails
   */

  public async getInventoryTemplates(
    merchant_id: string,
    params: GetInventoryTemplates,
  ): Promise<InventoryTemplatesPage> {
    let url = `${this.apiEndpoint}/inventory-templates/${merchant_id}`;

    // To query string

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryTemplatesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Inventory Template
   * PUT /inventory-templates/{merchant_id}
   * @param merchant_id - merchant id
   * @param dto - UpdateInventoryTemplate
   * @returns Promise object representing the InventoryTemplate
   * @throws Error if the request fails
   */

  public async updateInventoryTemplate(
    merchant_id: string,
    dto: UpdateInventoryTemplate,
  ): Promise<InventoryTemplate> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InventoryTemplate> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Delete Inventory Template
   * DELETE /inventory-templates/{merchant_id}/{store_id}/{id}
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param id - id
   * @returns Promise void
   * @throws Error if the request fails
   */

  public async deleteInventoryTemplate(
    merchant_id: string,
    store_id: string,
    id: string,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}/${store_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      await this.transport.send(config);
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Daily Count Lists
   * GET /inventory-templates/{merchant_id}/daily-counts
   * @param dto - GetDailyCountLists
   * @returns Promise object representing the DailyCountListPage
   * @throws Error if the request fails
   */

  public async getDailyCountLists(
    dto: GetDailyCountLists,
  ): Promise<DailyCountListPage> {
    let url = `${this.apiEndpoint}/inventory-templates/${dto.merchant_id}/daily-counts`;

    const queryString = this.toQueryString(dto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Record Daily Count
   * PUT /inventory-templates/{merchant_id}/daily-counts/record
   * @param merchant_id - merchant id
   * @param body - RecordDailyCount
   * @returns Promise object representing the updated DailyCountList
   * @throws Error if the request fails
   */

  public async recordDailyCount(
    merchant_id: string,
    body: RecordDailyCount,
  ): Promise<DailyCountList> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}/daily-counts/record`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: body,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Trigger Daily Count List
   * POST /inventory-templates/{merchant_id}/daily-counts/trigger
   * @param merchant_id - merchant id
   * @param body - TriggerDailyCountList (store_id, optional force)
   * @returns Promise object representing the generated DailyCountList
   * @throws Error if the request fails
   */

  public async triggerDailyCountList(
    merchant_id: string,
    body: TriggerDailyCountList,
  ): Promise<DailyCountList> {
    const url = `${this.apiEndpoint}/inventory-templates/${merchant_id}/daily-counts/trigger`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Debts
   * GET /debts/{merchant_id}
   * @param dto - GetDebts
   * @returns Promise object representing the DebtsPage
   * @throws Error if the request fails
   */

  public async getDebts(dto: GetDebts): Promise<DebtsPage> {
    let url = `${this.apiEndpoint}/debts/${dto.merchant_id}`;

    // To query string

    const queryString = this.toQueryString(dto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<DebtsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Debt Payments
   * GET /debts/{merchant_id}/payments
   * @param dto - GetDebtPayments
   * @returns Promise object representing the DebtPaymentsPage
   * @throws Error if the request fails
   */

  public async getDebtPayments(
    dto: GetDebtPayments,
  ): Promise<DebtPaymentsPage> {
    let url = `${this.apiEndpoint}/debts/${dto.merchant_id}/payments`;

    const queryString = this.toQueryString(dto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<DebtPaymentsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Debtor Details
   * GET /debts/:merchant_id/:store_id/debtors/:debtor_type/:debtor_id
   * @param merchant_id - merchant id
   * @param store_id - store id
   * @param debtor_type - debtor type
   * @param debtor_id - debtor id
   * @returns Promise object representing the DebtorDetails
   * @throws Error if the request fails
   */

  public async getDebtorDetails({
    merchant_id,
    store_id,
    debtor_type,
    debtor_id,
    with_debts,
    closed_debt_cursor,
    closed_debt_limit,
  }: {
    merchant_id: string;
    store_id: string;
    debtor_type: EntityTypes;
    debtor_id: string;
    with_debts?: boolean;
    closed_debt_cursor?: string;
    closed_debt_limit?: number;
  }): Promise<DebtorSummary> {
    let url = `${this.apiEndpoint}/debts/${merchant_id}/${store_id}/debtors/${debtor_type}/${debtor_id}`;

    const queryString = this.toQueryString({
      with_debts,
      closed_debt_cursor,
      closed_debt_limit,
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<DebtorSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Debt
   * POST /debts/{merchant_id}
   * @param merchant_id - merchant id
   * @param dto - DebtCommand
   * @returns Promise object representing the Debt
   */

  public async updateDebt(
    merchant_id: string,
    dto: DebtCommand,
  ): Promise<Debt> {
    const url = `${this.apiEndpoint}/debts/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Debt> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Bulk Create Store Products
   * POST /products/{merchant_id}/store-products/imports/{store_id}
   * @param dto - BulkCreateStoreProducts
   * @returns Promise object representing the BulkCreateStoreProductsResponse
   */

  public async bulkCreateStoreProducts(
    dto: BulkImportStoreProduct,
  ): Promise<BulkCreateStoreProductsResponse> {
    const url = `${this.apiEndpoint}/products/${dto.merchant_id}/store-products/imports/${dto.store_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<BulkCreateStoreProductsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Merchant Payment
   * POST merchants/payments/{merchant_id}
   * @param CreateMerchantPayment - CreateMerchantPayment
   * @returns Promise object representing the MerchantPayment
   */

  public async createMerchantPayment(
    dto: CreateMerchantPayment,
  ): Promise<MerchantPayment> {
    const url = `${this.apiEndpoint}/merchants/payments`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MerchantPayment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Merchant Payment
   * GET merchants/payments/{merchant_id}/{id}
   * @param merchant_id - merchant id
   * @param id - id
   * @returns Promise object representing the MerchantPayment
   */

  public async getMerchantPayment(
    merchant_id: string,
    id: string,
  ): Promise<MerchantPayment> {
    const url = `${this.apiEndpoint}/merchants/payments/${merchant_id}/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MerchantPayment> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All Merchant Payments
   * GET merchants/payments/{merchant_id}
   * @param dto - GetAllMerchantPayments
   * @returns Promise object representing the MerchantPaymentPage
   */

  public async getAllMerchantPayments(
    dto: GetAllMerchantPayments,
  ): Promise<MerchantPaymentPage> {
    let url = `${this.apiEndpoint}/merchants/payments/${dto.merchant_id}`;

    // To query string

    const queryString = this.toQueryString(dto);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MerchantPaymentPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Permission V2
   * POST authorizations-v2/permissions/
   * @param dto - CreatePermissionV2
   * @returns Promise object representing the created Permission
   */

  public async createPermissionV2(
    dto: CreatePermissionV2,
  ): Promise<PermissionV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/permissions`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PermissionV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Permission V2
   * GET authorizations-v2/permissions/{id}
   * @param dto - GetPermissionV2
   * @returns Promise object representing the Permission
   */

  public async getPermissionV2(dto: GetPermissionV2): Promise<PermissionV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/permissions/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PermissionV2;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All Permissions V2
   * GET authorizations-v2/permissions
   * @param dto - GetAllPermissionV2
   * @returns Promise object representing the PermissionsPage
   */

  public async getAllPermissionsV2(
    dto: GetAllPermissionsV2,
  ): Promise<PermissionsPageV2> {
    let url = `${this.apiEndpoint}/authorizations-v2/permissions`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PermissionsPageV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Permission V2
   * PUT authorizations-v2/permissions
   * @param dto - UpdatePermissionV2
   * @returns Promise object representing the updated Permission
   */

  public async updatePermissionV2(
    dto: UpdatePermissionV2,
  ): Promise<PermissionV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/permissions`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PermissionV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create Principal V2
   * POST authorizations-v2/principals/
   * @param dto - CreatePrincipalV2
   * @returns Promise object representing the created Principal
   */

  public async createPrincipalV2(dto: CreatePrincipalV2): Promise<PrincipalV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/principals`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PrincipalV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Principal V2
   * GET authorizations-v2/principals/{id}
   * @param dto - GetPrincipalV2
   * @returns Promise object representing the Principal
   */

  public async getPrincipalV2(dto: GetPrincipalV2): Promise<PrincipalV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/principals/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as PrincipalV2;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Principal V2
   * PUT authorizations-v2/principals
   * @param dto - UpdatePrincipal
   * @returns Promise object representing the updated Principal
   */

  public async updatePrincipalV2(dto: UpdatePrincipalV2): Promise<PrincipalV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/principals`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PrincipalV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create Role V2
   * POST authorizations-v2/{merchant_id}/roles/
   * @param dto - CreateRoleV2
   * @returns Promise object representing the created Role
   */

  public async createRoleV2(dto: CreateRoleV2): Promise<RoleV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/roles`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<RoleV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create Merchant Role V2
   * POST authorizations-v2/merchants/roles/
   * @param dto - CreateMerchantRole
   * @returns Promise object representing the created Role
   */

  public async createMerchantRoleV2(dto: CreateMerchantRole): Promise<RoleV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/merchants/roles`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<RoleV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Role V2
   * GET authorizations-v2/{merchant_id}/roles/{id}
   * @param dto - GetRoleV2
   * @returns Promise object representing the Role
   */

  public async getRoleV2(dto: GetRoleV2): Promise<RoleV2> {
    let url = `${this.apiEndpoint}/authorizations-v2/roles/${dto.id}`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data } = await this.transport.send(config);
      return data as RoleV2;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All Roles V2
   * GET authorizations-v2/{merchant_id}/roles
   * @param dto - GetAllRolesV2
   * @returns Promise object representing the RolesPageV2
   */

  public async getAllRolesV2(dto: GetAllRolesV2): Promise<RolesPageV2> {
    let url = `${this.apiEndpoint}/authorizations-v2/roles`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<RolesPageV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Update Role V2
   * PUT authorizations-v2/{merchant_id}/roles
   * @param dto - UpdateRoleV2
   * @returns Promise object representing the updated Role
   */

  public async updateRoleV2(dto: UpdateRoleV2): Promise<RoleV2> {
    const url = `${this.apiEndpoint}/authorizations-v2/roles/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<RoleV2> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Authorize App Open
   * POST authorizations-v2/authorizer/open-app/
   * @param dto - AuthorizeOpenApp
   */

  public async authorizeAppOpen(
    dto: AuthorizeOpenApp,
  ): Promise<AuthorizeOpenAppResult> {
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/open-app/${dto.merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AuthorizeOpenAppResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }
  /**
   * Decide business actions for a principal (service-to-service only).
   * POST authorizations-v2/authorizer/decisions/{merchant_id}
   * The route is IAM-authorized: the transport signs the request with the
   * calling service's identity.
   */
  public async authorizeActions(
    dto: AuthorizeActions & { merchant_id: string },
  ): Promise<AuthorizeActionsResult> {
    const { merchant_id, ...body } = dto;
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/decisions/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<AuthorizeActionsResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Service-only (SigV4): the roles a principal holds, per store. */
  public async getPrincipalStoreRoles(
    dto: GetPrincipalStoreRoles,
  ): Promise<PrincipalStoreRoles> {
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/principal-store-roles/${dto.merchant_id}?principal_id=${encodeURIComponent(dto.principal_id)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<PrincipalStoreRoles> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Service-only (SigV4): who holds a role in one store. */
  public async getRolePrincipalsInStore(
    dto: GetRolePrincipalsInStore,
  ): Promise<RolePrincipalsInStore> {
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/role-principals/${dto.merchant_id}?role_id=${encodeURIComponent(dto.role_id)}&store_id=${encodeURIComponent(dto.store_id)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<RolePrincipalsInStore> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The business actions of several principals in one store
   * (service-to-service only, for the POS offline credentials).
   * POST authorizations-v2/authorizer/team-actions/{merchant_id}
   */
  public async getTeamActions(
    dto: GetTeamActions & { merchant_id: string },
  ): Promise<TeamActionsResult> {
    const { merchant_id, ...body } = dto;
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/team-actions/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
      auth: 'service',
    });

    try {
      const { data }: TransportResponse<TeamActionsResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The business actions the signed-in user may perform in a store.
   * GET authorizations-v2/authorizer/my-actions/{merchant_id}?store_id=
   * Requires the user's bearer token; the principal is the verified user.
   */
  public async getMyActions(dto: GetMyActions): Promise<MyActionsResult> {
    const url = `${this.apiEndpoint}/authorizations-v2/authorizer/my-actions/${dto.merchant_id}?store_id=${encodeURIComponent(dto.store_id)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<MyActionsResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The employee of a merchant behind a Cognito user.
   * GET users/cognito/{cognito_id}?merchant_id=
   */
  public async getUserByCognitoId(dto: GetUserByCognitoId): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/cognito/${encodeURIComponent(dto.cognito_id)}?merchant_id=${encodeURIComponent(dto.merchant_id)}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Employee> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All Receipt events
   * GET receipts/{merchant_id}/receipt-events
   * @param dto - GetReceiptEvents
   * @returns Promise object representing the ReceiptEventsPage
   */

  public async getReceiptEvents(
    dto: GetReceiptEvents,
  ): Promise<ReceiptEventsPage> {
    let url = `${this.apiEndpoint}/receipts/${dto.merchant_id}/receipt-events`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ReceiptEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get WhatsApp webhook events
   * GET notifications/whatsapp-events
   * @param dto - GetWhatsappEvents
   * @returns Promise object representing the WhatsappEventsPage
   */
  public async getWhatsappEvents(
    dto: GetWhatsappEvents,
  ): Promise<WhatsappEventsPage> {
    let url = `${this.apiEndpoint}/notifications/whatsapp-events`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<WhatsappEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get notification settings for a store — per type: enabled flag,
   * subscribers, schedule and next runs (notifications.md §11).
   * GET notifications/settings/{merchant_id}/{store_id}
   */
  public async getNotificationSettings({
    merchant_id,
    store_id,
  }: {
    merchant_id: string;
    store_id: string;
  }): Promise<NotificationSettingsResponse> {
    const url = `${this.apiEndpoint}/notifications/settings/${merchant_id}/${store_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<NotificationSettingsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Upsert subscribers + schedule for one notification type of a store.
   * Validates 30-minute steps and syncs the EventBridge schedule.
   * PUT notifications/settings/{merchant_id}/{store_id}/{type}
   */
  public async updateNotificationSettings({
    merchant_id,
    store_id,
    type,
    settings,
  }: {
    merchant_id: string;
    store_id: string;
    type: NotificationRequestType;
    settings: UpdateNotificationSettings;
  }): Promise<NotificationTypeSettings> {
    const url = `${this.apiEndpoint}/notifications/settings/${merchant_id}/${store_id}/${type}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: settings,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<NotificationTypeSettings> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getNotificationContacts(
    merchant_id: string,
  ): Promise<NotificationContactsResponse> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    const { data }: TransportResponse<NotificationContactsResponse> =
      await this.transport.send(config);
    return data;
  }

  public async createNotificationContact(
    merchant_id: string,
    input: CreateNotificationContact,
  ): Promise<NotificationContact> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: input,
      extraHeaders: {},
    });
    const { data }: TransportResponse<NotificationContact> =
      await this.transport.send(config);
    return data;
  }

  public async updateNotificationContact(
    merchant_id: string,
    contact_id: string,
    input: UpdateNotificationContact,
  ): Promise<NotificationContact> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}/${contact_id}`;
    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: input,
      extraHeaders: {},
    });
    const { data }: TransportResponse<NotificationContact> =
      await this.transport.send(config);
    return data;
  }

  public async deleteNotificationContact(
    merchant_id: string,
    contact_id: string,
  ): Promise<void> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}/${contact_id}`;
    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });
    await this.transport.send(config);
  }

  public async sendNotificationContactVerification(
    merchant_id: string,
    contact_id: string,
  ): Promise<SendNotificationContactVerificationResponse> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}/${contact_id}/verification`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: {},
    });
    const {
      data,
    }: TransportResponse<SendNotificationContactVerificationResponse> =
      await this.transport.send(config);
    return data;
  }

  public async verifyNotificationContact(
    merchant_id: string,
    contact_id: string,
    input: VerifyNotificationContact,
  ): Promise<NotificationContact> {
    const url = `${this.apiEndpoint}/notifications/contacts/${merchant_id}/${contact_id}/verify`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: input,
      extraHeaders: {},
    });
    const { data }: TransportResponse<NotificationContact> =
      await this.transport.send(config);
    return data;
  }

  /**
   * Notification history for a store with per-recipient delivery statuses.
   * GET notifications/history/{merchant_id}/{store_id}?type&from&to&cursor&count
   */
  public async getNotificationsHistory(
    dto: GetNotificationsHistory,
  ): Promise<NotificationsHistoryPage> {
    const { merchant_id, store_id, ...query } = dto;
    let url = `${this.apiEndpoint}/notifications/history/${merchant_id}/${store_id}`;

    const queryString = this.toQueryString(query);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<NotificationsHistoryPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * The signed-in user's open tasks of a store (the calling app's first) and
   * those closed in the last 24 hours.
   * GET notifications/tasks/{merchant_id}/stores/{store_id}?app=
   */
  public async getTasks(dto: GetTasks): Promise<TaskList> {
    const { merchant_id, store_id, ...query } = dto;
    let url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}/stores/${store_id}`;

    const queryString = this.toQueryString(query);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TaskList> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Open task count and recent closes per store of the signed-in user.
   * GET notifications/tasks/{merchant_id}/summary
   */
  public async getTaskSummary(dto: GetTaskSummary): Promise<TaskSummary> {
    const url = `${this.apiEndpoint}/notifications/tasks/${dto.merchant_id}/summary`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TaskSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** A manual task for a person or a role of the store (`tasks:create`). */
  public async createTask(
    merchant_id: string,
    task: CreateTask,
    idempotencyKey: string,
  ): Promise<Task> {
    const url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: task,
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<Task> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Ticks a manual task (its assignee, or anyone in its role). */
  public async completeTask(
    merchant_id: string,
    task_id: string,
    body: CompleteTask,
    idempotencyKey: string,
  ): Promise<Task> {
    const url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}/${encodeURIComponent(task_id)}/done`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<Task> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Withdraws a manual task (its creator, or `tasks:view_all`). */
  public async withdrawTask(
    merchant_id: string,
    task_id: string,
    idempotencyKey: string,
  ): Promise<Task> {
    const url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}/${encodeURIComponent(task_id)}/withdraw`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    });

    try {
      const { data }: TransportResponse<Task> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** The manual tasks the signed-in user created: open first, then closed. */
  public async getCreatedTasks(dto: GetCreatedTasks): Promise<TaskPage> {
    const { merchant_id, ...query } = dto;
    let url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}/created`;
    const queryString = this.toQueryString(query);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TaskPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** The store's tasks active in a date range, with totals (`tasks:view_all`). */
  public async getTaskReport(dto: GetTaskReport): Promise<TaskReport> {
    const { merchant_id, ...query } = dto;
    let url = `${this.apiEndpoint}/notifications/tasks/${merchant_id}/report`;
    const queryString = this.toQueryString(query);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TaskReport> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Re-queue a PENDING notification recipient for delivery.
   * POST notifications/history/{merchant_id}/{store_id}/{message_id}/recipients/{recipient_id}/resend
   * @param dto - ResendNotificationRecipient
   * @returns Promise object representing the ResendNotificationRecipientResponse
   */
  public async resendNotificationRecipient(
    dto: ResendNotificationRecipient,
  ): Promise<ResendNotificationRecipientResponse> {
    const { merchant_id, store_id, message_id, recipient_id } = dto;
    const url = `${this.apiEndpoint}/notifications/history/${merchant_id}/${store_id}/${message_id}/recipients/${recipient_id}/resend`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ResendNotificationRecipientResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get raw SMS messages forwarded by the IFTTT Android applet
   * GET notifications/sms/messages
   * @param dto - GetIftttSmsMessages
   * @returns Promise object representing the IftttSmsMessagesPage
   */
  public async getIftttSmsMessages(
    dto: GetIftttSmsMessages,
  ): Promise<IftttSmsMessagesPage> {
    let url = `${this.apiEndpoint}/notifications/sms/messages`;

    const params = {
      ...dto,
    };

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<IftttSmsMessagesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Send a WhatsApp OTP using an authentication template
   * POST notifications/whatsapp/send-otp
   * @param dto - SendWhatsappOtpRequest
   * @returns Promise object representing the SendWhatsappOtpResponse
   */
  public async sendWhatsappOtp(
    dto: SendWhatsappOtpRequest,
  ): Promise<SendWhatsappOtpResponse> {
    const url = `${this.apiEndpoint}/notifications/whatsapp/send-otp`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<SendWhatsappOtpResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Sync Cognito user attributes to the service-users DynamoDB record.
   * PUT users/{user_id}/sync-from-cognito
   * @param user_id - Cognito user ID (phone number)
   * @returns Promise object representing the updated Employee
   */
  public async syncUserFromCognito(user_id: string): Promise<Employee> {
    const url = `${this.apiEndpoint}/users/${user_id}/sync-from-cognito`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Employee> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Initiate a custom auth challenge (triggers OTP via WhatsApp or PIN).
   * POST authentications/auth-challenge
   * @param dto - CreateAuthChallengeRequest
   * @returns Promise with the Cognito session
   */
  public async createAuthChallenge(
    dto: CreateAuthChallengeRequest,
  ): Promise<CreateAuthChallengeResponse> {
    const url = `${this.apiEndpoint}/authentications/auth-challenge`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<CreateAuthChallengeResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Verify an auth challenge answer (OTP or PIN).
   * POST authentications/verify-auth-challenge
   * @param dto - VerifyAuthChallengeRequest
   * @returns Promise with auth tokens and user data
   */
  public async verifyAuthChallenge(
    dto: VerifyAuthChallengeRequest,
  ): Promise<AuthenticateUserResponse> {
    const url = `${this.apiEndpoint}/authentications/verify-auth-challenge`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AuthenticateUserResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Toggle WhatsApp OTP authentication for a merchant user.
   * PUT authentications/merchants/otp-settings
   * @param dto - UpdateOtpSettingsRequest
   * @returns Promise with update confirmation
   */
  public async updateOtpSettings(
    dto: UpdateOtpSettingsRequest,
  ): Promise<UpdateOtpSettingsResponse> {
    const url = `${this.apiEndpoint}/authentications/merchants/otp-settings`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<UpdateOtpSettingsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Initiate PIN recovery — sends a WhatsApp OTP even when
   * use_whatsapp_otp is false. The CreateAuthChallenge trigger
   * checks ClientMetadata.pin_recovery to force OTP mode.
   *
   * POST authentications/pin-recovery/initiate
   * @param dto - InitiatePinRecoveryRequest
   * @returns Promise with the Cognito session
   */
  public async initiatePinRecovery(
    dto: InitiatePinRecoveryRequest,
  ): Promise<InitiatePinRecoveryResponse> {
    const url = `${this.apiEndpoint}/authentications/pin-recovery/initiate`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<InitiatePinRecoveryResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Complete PIN recovery — verifies the OTP, permanently enables
   * use_whatsapp_otp on the Cognito user, and returns auth tokens.
   *
   * POST authentications/pin-recovery/verify
   * @param dto - CompletePinRecoveryRequest
   * @returns Promise with auth tokens and user data
   */
  public async completePinRecovery(
    dto: CompletePinRecoveryRequest,
  ): Promise<AuthenticateUserResponse> {
    const url = `${this.apiEndpoint}/authentications/pin-recovery/verify`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AuthenticateUserResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All app builds
   * GET images/app-builds
   * @returns Promise object representing the AppBuilds
   */

  public async getAppBuilds(): Promise<AppBuildsResponse> {
    let url = `${this.apiEndpoint}/images/app-builds`;

    const params = {};

    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<AppBuildsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get All app release versions
   * GET images/releases/versions
   * @returns Promise object representing the ReleasesVersionsResponse
   */

  public async getReleasesVersions(): Promise<ReleasesVersionsResponse> {
    const url = `${this.apiEndpoint}/images/releases/versions`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ReleasesVersionsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }
  /**
   * Update Topups Password
   * PUT wallets/topups/password
   * @param dto - UpdateTopupsPassword
   * @returns Promise object representing the update status
   */

  public async updateTopupPassword(
    dto: UpdateTopupPassword,
  ): Promise<{ message: string }> {
    const url = `${this.apiEndpoint}/topups/password`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ message: string }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Reset Topups Password
   * PUT wallets/topups/password/reset
   * @param dto - ResetTopupPassword
   * @returns Promise object representing the reset status
   */

  public async resetTopupPassword(
    dto: ResetTopupPassword,
  ): Promise<{ message: string }> {
    const url = `${this.apiEndpoint}/topups/password/reset`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<{ message: string }> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  // ===== Topups — claim flow =====

  /**
   * Claim a topup by its SMS-derived `transaction_id`. On success the merchant
   * wallet is credited via `addMoneyToWallet` and the topup is marked as
   * `CLAIMED_AUTO`.
   * POST topups/claims/by-transaction-id
   */
  public async claimTopupByTransactionId(args: {
    body: ClaimTopupByTransactionId;
  }): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/by-transaction-id`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Claim a topup by amount + time window (fallback when the `transaction_id`
   * is unknown). Auto-claims only when exactly one unclaimed request matches.
   * POST topups/claims/by-details
   */
  public async claimTopupByDetails(args: {
    body: ClaimTopupByDetails;
  }): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/by-details`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Claim a topup by pasting the original SMS message. The backend extracts
   * the transaction_id and amount (regex first, AI fallback for non-eMola
   * operators like Airtel/MTN/Zamtel), then auto-claims.
   * POST topups/claims/by-message
   */
  public async claimTopupByMessage(args: {
    body: ClaimTopupByMessage;
  }): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/by-message`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Read-only matcher for the review UI. Returns unclaimed topup requests
   * within a time window around a given amount + optional narrowing fields.
   * GET topups/claims/matches
   */
  public async findTopupClaimMatches(
    args: FindTopupClaimMatches,
  ): Promise<TopupClaimMatchesPage> {
    let url = `${this.apiEndpoint}/topups/claims/matches`;
    const queryString = this.toQueryString({ ...args });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupClaimMatchesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Fetch a single topup request by `transaction_id`.
   * GET topups/requests/{transaction_id}
   */
  public async getTopupRequest(args: GetTopupRequest): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/requests/${args.transaction_id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Approve a topup claim that was placed in manual review (or that is still
   * unclaimed). Credits the merchant wallet and marks the topup
   * `CLAIMED_MANUAL`.
   * POST topups/claims/{transaction_id}/approve
   */
  public async approveTopupClaim(args: {
    transaction_id: string;
    body: Omit<ApproveTopupClaim, 'transaction_id'>;
  }): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/${args.transaction_id}/approve`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reject a topup claim. Never calls the Wallets API.
   * POST topups/claims/{transaction_id}/reject
   */
  public async rejectTopupClaim(args: {
    transaction_id: string;
    body: Omit<RejectTopupClaim, 'transaction_id'>;
  }): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/${args.transaction_id}/reject`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** POST to a topups/claims/{transaction_id}/<action> endpoint. */
  private async postTopupClaimAction<B>(
    transaction_id: string,
    action: string,
    body: B,
  ): Promise<TopupRequest> {
    const url = `${this.apiEndpoint}/topups/claims/${encodeURIComponent(transaction_id)}/${action}`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupRequest> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Revoke a claimed topup: takes its credit back from the wallet it went to
   * (422 when that wallet no longer holds it) and marks it REVOKED.
   * POST topups/claims/{transaction_id}/revoke
   */
  public revokeTopupClaim(args: {
    transaction_id: string;
    body: Omit<RevokeTopupClaim, 'transaction_id'>;
  }): Promise<TopupRequest> {
    return this.postTopupClaimAction(args.transaction_id, 'revoke', args.body);
  }

  /**
   * Put a REJECTED topup back to UNCLAIMED.
   * POST topups/claims/{transaction_id}/reopen
   */
  public reopenTopupClaim(args: {
    transaction_id: string;
    body: Omit<ReopenTopupClaim, 'transaction_id'>;
  }): Promise<TopupRequest> {
    return this.postTopupClaimAction(args.transaction_id, 'reopen', args.body);
  }

  /**
   * Correct the details of an UNCLAIMED, NEEDS_REVIEW or REJECTED topup.
   * POST topups/claims/{transaction_id}/correct
   */
  public correctTopupRequest(args: {
    transaction_id: string;
    body: Omit<CorrectTopupRequest, 'transaction_id'>;
  }): Promise<TopupRequest> {
    return this.postTopupClaimAction(args.transaction_id, 'correct', args.body);
  }

  /**
   * Number of topup requests per claim status.
   * GET topups/status-counts
   */
  public async getTopupStatusCounts(
    args: GetTopupStatusCounts = {},
  ): Promise<TopupStatusCounts> {
    let url = `${this.apiEndpoint}/topups/status-counts`;
    const queryString = this.toQueryString({ ...args });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<TopupStatusCounts> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get wallet Transfer Outs
   * GET wallets/transfer-outs
   * @param dto - GetWalletTransferOuts
   * @returns Promise object representing the WalletTransferOutsPage
   */

  public async getWalletTransferOuts(
    dto: WalletGetAllTransferOuts,
  ): Promise<WalletTransferOutsPage> {
    let url = `${this.apiEndpoint}/topups/transfer-outs`;
    const params = {
      ...dto,
      count: dto.count?.toString(),
    };
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransferOutsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Create Topup Request
   * POST topups/sms
   * @param dto - CreateTopupRequest
   * @returns Promise object representing the created topuprequest
   */

  public async createTopupRequest(
    dto: CreateTopupRequest,
  ): Promise<WalletHandleSmsResponse> {
    const url = `${this.apiEndpoint}/topups/sms`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        ...dto,
      },
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<WalletHandleSmsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get Shifts
   * GET shifts/{merchant_id}/{store_id}
   * @param dto - GetShifts
   * @returns Promise object representing the ShiftPage
   * @throws Error if the request fails
   */
  public async getShifts(dto: GetShifts): Promise<ShiftPage> {
    let url = `${this.apiEndpoint}/shifts/${dto.merchant_id}/${dto.store_id}`;

    const params = {
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
      status: dto.status,
      opened_by: dto.opened_by,
      opened_user_type: dto.opened_user_type,
      pos_id: dto.pos_id,
      from: dto.from,
      to: dto.to,
      by_date_range: dto.by_date_range?.toString(),
      by_status: dto.by_status?.toString(),
      by_status_and_date_range: dto.by_status_and_date_range?.toString(),
      by_user: dto.by_user?.toString(),
      by_user_and_date_range: dto.by_user_and_date_range?.toString(),
      by_pos: dto.by_pos?.toString(),
      by_pos_and_date_range: dto.by_pos_and_date_range?.toString(),
      by_store: dto.by_store?.toString(),
      by_store_and_date_range: dto.by_store_and_date_range?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ShiftPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Shift
   * GET shifts/{merchant_id}/{store_id}/{shift_id}
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async getShift(
    merchant_id: string,
    store_id: string,
    shift_id: string,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Most Recent Shift
   * GET shifts/{merchant_id}/{store_id}/pos/{pos_id}/recent
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param pos_id - POS ID
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async getMostRecentShift(
    merchant_id: string,
    store_id: string,
    pos_id: string,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/pos/${pos_id}/recent`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get Most Recent Shift By Account
   * GET shifts/{merchant_id}/{store_id}/account/recent?account_id=&account_type=
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param account_id - The account (user/customer) ID
   * @param account_type - The account type (USER, CUSTOMER, or SYSTEM)
   * @returns Promise object representing the Shift, or null if none found
   */
  public async getMostRecentShiftByAccount(
    merchant_id: string,
    store_id: string,
    account_id: string,
    account_type: string,
  ): Promise<Shift | null> {
    const params = {
      account_id,
      account_type,
    };
    const queryString = this.toQueryString(params);
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/account/recent?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Open Shift
   * POST shifts/{merchant_id}/{store_id}
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param dto - CreateShift
   * @returns Promise object representing the created Shift
   * @throws Error if the request fails
   */
  public async openShift(
    merchant_id: string,
    store_id: string,
    dto: CreateShift,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Start Close Shift
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/close/start
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @param dto - AttemptToCloseShift
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async startCloseShift(
    merchant_id: string,
    store_id: string,
    shift_id: string,
    dto: AttemptToCloseShift,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}/close/start`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Mark Closing Started
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/close/mark-started
   * Stamp `closing_started_at` on an OPEN shift (idempotent). Does not
   * change shift status — the shift remains OPEN so the operator can still
   * add / cancel transactions during the Reconcile step of the
   * CloseShiftStepper. Safe to call more than once.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  /**
   * Record Close Audit Entry
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/close/audit
   * Append a closing-audit-trail entry on an OPEN shift. Called by the
   * CloseShiftStepper at every step transition (BALANCES_SUBMITTED,
   * RECONCILED). Does not change shift status — the shift remains OPEN so
   * the operator can still add/cancel transactions during the Reconcile
   * step. The first BALANCES_SUBMITTED stamps `closing_started_at`.
   * The server computes `expected_balances` and `differences`, and resolves
   * `session_transaction_ids`, so the client only sends what the operator
   * typed.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @param dto - RecordCloseAuditEntry
   * @returns Promise object representing the updated Shift
   * @throws Error if the request fails
   */
  public async recordCloseAuditEntry(
    merchant_id: string,
    store_id: string,
    shift_id: string,
    dto: RecordCloseAuditEntry,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}/close/audit`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Instant Close Shift
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/close/instant
   * Instantly close an OPEN shift (skips NEEDS_ATTENTION). Requires a reason.
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @param dto - InstantCloseShift
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async instantCloseShift(
    merchant_id: string,
    store_id: string,
    shift_id: string,
    dto: InstantCloseShift,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}/close/instant`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Finalize Close Shift
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/close/finalize
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @param dto - CloseShiftWithReconciliation
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async finalizeCloseShift(
    merchant_id: string,
    store_id: string,
    shift_id: string,
    dto: CloseShiftWithReconciliation,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}/close/finalize`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Add Shift Comment
   * POST shifts/{merchant_id}/{store_id}/{shift_id}/comments
   * @param merchant_id - Merchant ID
   * @param store_id - Store ID
   * @param shift_id - Shift ID
   * @param dto - AddShiftComment
   * @returns Promise object representing the Shift
   * @throws Error if the request fails
   */
  public async addShiftComment(
    merchant_id: string,
    store_id: string,
    shift_id: string,
    dto: AddShiftComment,
  ): Promise<Shift> {
    const url = `${this.apiEndpoint}/shifts/${merchant_id}/${store_id}/${shift_id}/comments`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<Shift> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single topup number
   * @param dto GetTopupNumber
   * @returns Promise object representing the TopupNumber
   * @throws Error if the request fails
   * @see TopupNumber
   */

  public async getTopupNumber(dto: GetTopupNumber): Promise<TopupNumber> {
    const url = `${this.apiEndpoint}/topups/numbers/${dto.phone_number}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TopupNumber> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get all topup numbers
   * @param dto GetAllTopupNumbers
   * @returns Promise object representing the TopupNumbersPage
   * @throws Error if the request fails
   * @see TopupNumber
   * @see TopupNumbersPage
   */

  public async getAllTopupNumbers(
    dto: GetAllTopupNumbers,
  ): Promise<TopupNumbersPage> {
    let url = `${this.apiEndpoint}/topups/numbers/`;

    const query = this.toQueryString({
      ...dto,
    });

    if (query) {
      url += `?${query}`;
    }

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TopupNumbersPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Create a new TopupNumber in the API.
   * @param dto - CreateTopupNumber object
   * @returns Promise object representing the created TopupNumber
   * @throws Error if the request fails
   * @see TopupNumber
   * @see CreateTopupNumber
   */
  public async createTopupNumber(dto: CreateTopupNumber): Promise<TopupNumber> {
    const url = `${this.apiEndpoint}/topups/numbers`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TopupNumber> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Update a TopupNumber in the API.
   * @param dto - UpdateTopupNumber object
   * @returns Promise object representing the Updated TopupNumber
   * @throws Error if the request fails
   * @see TopupNumber
   * @see UpdateTopupNumber
   */
  public async updateTopupNumber(dto: UpdateTopupNumber): Promise<TopupNumber> {
    const url = `${this.apiEndpoint}/topups/numbers`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TopupNumber> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get the total money that went in and out of the whole program
   * @param dto GetTopupsBalance
   * @returns Promise object representing the balance
   * @throws Error if the request fails
   * @see GetTopupsBalance
   * @see GetTopupsBalanceResponse
   */

  public async getTopupsBalance(
    dto: GetTopupsBalance,
  ): Promise<GetTopupsBalanceResponse> {
    let url = `${this.apiEndpoint}/topups/balance/${dto.receiver_number}`;

    const params = {
      ...dto,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GetTopupsBalanceResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  /**
   * Get all the topup requests.
   * @param dto GetAllTopupRequests
   * @returns Promise object representing the Topup requests page
   * @throws Error if the request fails
   * @see GetAllTopupRequests
   */

  public async getAllTopupRequests(
    dto: GetAllTopupRequests,
  ): Promise<TopupRequestsPage> {
    let url = `${this.apiEndpoint}/topups`;

    const params = {
      ...dto,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TopupRequestsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }
  /**
   * Get all the wallet messages.
   * @param dto GetAllWalletMessages
   * @returns Promise object representing the Wallet Messages page
   * @throws Error if the request fails
   * @see GetAllWalletMessages
   */

  // ─── Global Products ───────────────────────────────────────────────

  public async createGlobalProduct(
    dto: CreateGlobalProduct,
  ): Promise<GlobalProduct> {
    const url = `${this.apiEndpoint}/global-products`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateGlobalProduct(
    dto: UpdateGlobalProduct,
  ): Promise<GlobalProduct> {
    const url = `${this.apiEndpoint}/global-products/${dto.id}`;

    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getGlobalProduct(id: string): Promise<GlobalProduct> {
    const url = `${this.apiEndpoint}/global-products/${id}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getGlobalProducts(
    dto: GetGlobalProducts,
  ): Promise<GlobalProductPage> {
    let url = `${this.apiEndpoint}/global-products`;

    const params = {
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
      approval_status: dto.approval_status,
      include_children: dto.include_children?.toString(),
      hydrate_fractions: dto.hydrate_fractions?.toString(),
      country: dto.country,
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProductPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getGlobalProductByBarcode(
    dto: GetGlobalProductsByBarcode,
  ): Promise<GlobalProductPage> {
    let url = `${this.apiEndpoint}/global-products/barcode/${encodeURIComponent(dto.barcode)}`;

    const params = {
      approval_status: dto.approval_status,
      include_children: dto.include_children?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProductPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getGlobalProductsByTags(
    dto: GetGlobalProductsByTags,
  ): Promise<GlobalProductPage> {
    let url = `${this.apiEndpoint}/global-products/tags`;

    const params = {
      tags: dto.tags.join(','),
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
      approval_status: dto.approval_status,
      include_children: dto.include_children?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProductPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async deleteGlobalProduct(
    id: string,
  ): Promise<ShopAndSmileOkResponse> {
    const url = `${this.apiEndpoint}/global-products/${id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ShopAndSmileOkResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ─── Global Product Fractions ──────────────────────────────────────

  public async createGlobalProductFraction(
    dto: CreateGlobalProductFraction,
  ): Promise<GlobalProduct> {
    const url = `${this.apiEndpoint}/global-products/fractions`;

    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProduct> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getGlobalProductFractions(
    dto: GetGlobalProductFractions,
  ): Promise<GlobalProductFractionPage> {
    let url = `${this.apiEndpoint}/global-products/fractions/${dto.parent_id}`;

    const params = {
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<GlobalProductFractionPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async deleteGlobalProductFraction(
    parent_id: string,
    fraction_id: string,
  ): Promise<ShopAndSmileOkResponse> {
    const url = `${this.apiEndpoint}/global-products/fractions/${parent_id}/${fraction_id}`;

    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<ShopAndSmileOkResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ─── Wallets ──────────────────────────────────────────────────────

  public async getAllWalletMessages(
    dto: GetAllWalletMessages,
  ): Promise<WalletMessagesPage> {
    let url = `${this.apiEndpoint}/topups/messages/${dto.receiver_number}`;

    const params = {
      ...dto,
    };

    // Generate query string from params
    const queryString = this.toQueryString(params);

    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<WalletMessagesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);

      throw error;
    }
  }

  // ===== Wallets — ledger + snapshot API =====

  /**
   * Add money to a wallet (credit). Repeated references are allowed.
   * POST wallets/{merchant_id}/transactions/credit
   */
  public async addMoneyToWallet(args: {
    merchant_id: string;
    body: CreditWallet;
  }): Promise<CreditDebitResult> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/credit`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CreditDebitResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Take money from a wallet (debit). Repeated references are allowed.
   * POST wallets/{merchant_id}/transactions/debit
   */
  public async takeMoneyFromWallet(args: {
    merchant_id: string;
    body: DebitWallet;
  }): Promise<CreditDebitResult> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/debit`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CreditDebitResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ============================================================================
  // Store Wallet API Methods
  // ============================================================================

  /**
   * Assign funds from a merchant wallet to a store wallet.
   * POST wallets/{merchant_id}/stores/{store_id}/assign
   */
  public async assignFundsToStore(args: {
    merchant_id: string;
    store_id: string;
    body: AssignFundsToStorePayload;
  }): Promise<AssignUnassignResult> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/assign`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AssignUnassignResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Unassign funds from a store wallet back to the merchant wallet.
   * POST wallets/{merchant_id}/stores/{store_id}/unassign
   */
  public async unassignFundsFromStore(args: {
    merchant_id: string;
    store_id: string;
    body: UnassignFundsFromStorePayload;
  }): Promise<AssignUnassignResult> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/unassign`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AssignUnassignResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Take money from a store wallet (debit).
   * POST wallets/{merchant_id}/stores/{store_id}/transactions/debit
   */
  public async takeMoneyFromStoreWallet(args: {
    merchant_id: string;
    store_id: string;
    body: DebitWallet;
  }): Promise<CreditDebitResult> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/transactions/debit`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: args.body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CreditDebitResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the latest store wallet balance.
   * GET wallets/{merchant_id}/stores/{store_id}/snapshots/{payment_method_type}/{payment_method_id}/latest
   */
  public async getLatestStoreWalletSnapshot(args: {
    merchant_id: string;
    store_id: string;
    payment_method_type: string;
    payment_method_id: string;
  }): Promise<StoreWalletBalance | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/latest`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<StoreWalletBalance | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List all store wallet balances for a merchant.
   * GET wallets/{merchant_id}/stores/wallets
   */
  public async listStoreWalletsByMerchant(args: {
    merchant_id: string;
    cursor?: string;
    limit?: number;
  }): Promise<StoreWalletsPage> {
    const params = new URLSearchParams();
    if (args.cursor) params.set('cursor', args.cursor);
    if (args.limit) params.set('limit', args.limit.toString());
    const query = params.toString();
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/wallets${query ? `?${query}` : ''}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<StoreWalletsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the most recent store wallet transaction.
   * GET wallets/{merchant_id}/stores/{store_id}/transactions/{payment_method_type}/{payment_method_id}/latest
   */
  public async getMostRecentStoreWalletTransaction(args: {
    merchant_id: string;
    store_id: string;
    payment_method_type: string;
    payment_method_id: string;
  }): Promise<WalletTransaction | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/transactions/${args.payment_method_type}/${args.payment_method_id}/latest`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransaction | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List store wallet transactions by payment method.
   * GET wallets/{merchant_id}/stores/{store_id}/transactions/{payment_method_type}/{payment_method_id}
   */
  public async listStoreWalletTransactionsByPaymentMethod(args: {
    merchant_id: string;
    store_id: string;
    payment_method_type: string;
    payment_method_id: string;
    start_date?: string;
    end_date?: string;
    cursor?: string;
    limit?: number;
  }): Promise<WalletTransactionsPage> {
    const params = new URLSearchParams();
    if (args.cursor) params.set('cursor', args.cursor);
    if (args.limit) params.set('limit', args.limit.toString());
    if (args.start_date) params.set('start_date', args.start_date);
    if (args.end_date) params.set('end_date', args.end_date);
    const query = params.toString();
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/transactions/${args.payment_method_type}/${args.payment_method_id}${query ? `?${query}` : ''}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a store wallet snapshot at a specific point in time.
   * GET wallets/{merchant_id}/stores/{store_id}/snapshots/{payment_method_type}/{payment_method_id}/at-time?at_time=ISO
   */
  public async getStoreWalletSnapshotAtTime(args: {
    merchant_id: string;
    store_id: string;
    payment_method_type: string;
    payment_method_id: string;
    at_time: string;
  }): Promise<StoreWalletBalance | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/at-time?at_time=${encodeURIComponent(args.at_time)}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<StoreWalletBalance | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List store wallet snapshots within a date range.
   * GET wallets/{merchant_id}/stores/{store_id}/snapshots/{payment_method_type}/{payment_method_id}/history?start_date=&end_date=
   */
  public async listStoreWalletSnapshotsByDateRange(args: {
    merchant_id: string;
    store_id: string;
    payment_method_type: string;
    payment_method_id: string;
    start_date: string;
    end_date: string;
  }): Promise<StoreWalletBalance[]> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/stores/${args.store_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/history?start_date=${encodeURIComponent(args.start_date)}&end_date=${encodeURIComponent(args.end_date)}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data } = await this.transport.send(config);
      return data as StoreWalletBalance[];
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List wallet transactions by creator.
   * GET wallets/{merchant_id}/transactions/by-creator/{created_by_type}/{created_by_id}
   */
  public async listWalletTransactionsByCreator(args: {
    merchant_id: string;
    created_by_type: string;
    created_by_id: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    cursor?: string;
  }): Promise<WalletTransactionsPage> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/by-creator/${args.created_by_type}/${args.created_by_id}`;
    const queryString = this.toQueryString({
      start_date: args.start_date,
      end_date: args.end_date,
      limit: args.limit?.toString(),
      cursor: args.cursor,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List wallet transactions by reference.
   * GET wallets/{merchant_id}/transactions/by-reference/{reference_type}/{reference_id}
   */
  public async listWalletTransactionsByReference(args: {
    merchant_id: string;
    reference_type: string;
    reference_id: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    cursor?: string;
  }): Promise<WalletTransactionsPage> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/by-reference/${args.reference_type}/${args.reference_id}`;
    const queryString = this.toQueryString({
      start_date: args.start_date,
      end_date: args.end_date,
      limit: args.limit?.toString(),
      cursor: args.cursor,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List wallet transactions for a payment method.
   * GET wallets/{merchant_id}/transactions/{payment_method_type}/{payment_method_id}
   */
  public async listWalletTransactionsByPaymentMethod(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    cursor?: string;
  }): Promise<WalletTransactionsPage> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/${args.payment_method_type}/${args.payment_method_id}`;
    const queryString = this.toQueryString({
      start_date: args.start_date,
      end_date: args.end_date,
      limit: args.limit?.toString(),
      cursor: args.cursor,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get most recent wallet transaction for a payment method.
   * GET wallets/{merchant_id}/transactions/{payment_method_type}/{payment_method_id}/latest
   */
  public async getMostRecentWalletTransaction(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
  }): Promise<WalletTransaction | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/${args.payment_method_type}/${args.payment_method_id}/latest`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransaction | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get wallet transactions strictly after a given version (or inclusive if inclusive=true).
   * GET wallets/{merchant_id}/transactions/{payment_method_type}/{payment_method_id}/after/{version}
   */
  public async getWalletTransactionsAfterVersion(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
    version: number;
    inclusive?: boolean;
  }): Promise<WalletTransaction[]> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/${args.payment_method_type}/${args.payment_method_id}/after/${args.version}`;
    if (args.inclusive) url += `?inclusive=true`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransaction[]> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single wallet transaction by version.
   * GET wallets/{merchant_id}/transactions/{payment_method_type}/{payment_method_id}/version/{version}
   */
  public async getWalletTransaction(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
    version: number;
  }): Promise<WalletTransaction | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/transactions/${args.payment_method_type}/${args.payment_method_id}/version/${args.version}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletTransaction | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the latest wallet snapshot (computed up to now).
   * GET wallets/{merchant_id}/snapshots/{payment_method_type}/{payment_method_id}/latest
   */
  public async getLatestWalletSnapshot(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
  }): Promise<WalletSnapshot | null> {
    const url = `${this.apiEndpoint}/wallets/${args.merchant_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/latest`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletSnapshot | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the wallet snapshot at a specific point in time.
   * GET wallets/{merchant_id}/snapshots/{payment_method_type}/{payment_method_id}/at-time?at_time=ISO
   */
  public async getWalletSnapshotAtTime(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
    at_time: string;
  }): Promise<WalletSnapshot | null> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/at-time`;
    const queryString = this.toQueryString({ at_time: args.at_time });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletSnapshot | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List all wallet snapshots within a date range.
   * GET wallets/{merchant_id}/snapshots/{payment_method_type}/{payment_method_id}/history
   */
  public async listWalletSnapshotsByDateRange(args: {
    merchant_id: string;
    payment_method_type: string;
    payment_method_id: string;
    start_date: string;
    end_date: string;
  }): Promise<WalletSnapshot[]> {
    let url = `${this.apiEndpoint}/wallets/${args.merchant_id}/snapshots/${args.payment_method_type}/${args.payment_method_id}/history`;
    const queryString = this.toQueryString({
      start_date: args.start_date,
      end_date: args.end_date,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletSnapshot[]> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ===== Platform Wallet — shop-level wallet for invoice payments =====

  /**
   * Credit the platform wallet (used when a store pays an invoice).
   * POST wallets/platform/transactions/credit
   */
  public async creditPlatformWallet(
    dto: CreditPlatformWalletParams,
  ): Promise<CreditDebitResult> {
    const url = `${this.apiEndpoint}/wallets/platform/transactions/credit`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CreditDebitResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get the latest platform wallet balance for a payment method.
   * GET wallets/platform/snapshots/{payment_method_type}/{payment_method_id}/latest
   */
  public async getPlatformWalletBalance(args: {
    payment_method_type: string;
    payment_method_id: string;
  }): Promise<WalletSnapshot | null> {
    const url = `${this.apiEndpoint}/wallets/platform/snapshots/${args.payment_method_type}/${args.payment_method_id}/latest`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<WalletSnapshot | null> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List platform wallet transactions with optional merchant/store filters.
   * GET wallets/platform/transactions?payment_method_type=&payment_method_id=&merchant_id=&store_id=&cursor=&limit=
   */
  public async listPlatformWalletTransactions(
    dto: ListPlatformTransactionsParams,
  ): Promise<PlatformTransactionsPage> {
    let url = `${this.apiEndpoint}/wallets/platform/transactions`;
    const queryString = this.toQueryString({
      payment_method_type: dto.payment_method_type,
      payment_method_id: dto.payment_method_id,
      merchant_id: dto.merchant_id,
      store_id: dto.store_id,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
      start_date: dto.start_date,
      end_date: dto.end_date,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PlatformTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List ALL platform wallet transactions across every payment method.
   * GET wallets/platform/transactions/all?start_date=&end_date=&cursor=&limit=
   *
   * Unlike listPlatformWalletTransactions, this endpoint does NOT require
   * payment_method_type / payment_method_id. It queries GSI3 which indexes
   * all platform transactions by date regardless of payment method.
   */
  public async listAllPlatformTransactions(args: {
    start_date?: string;
    end_date?: string;
    cursor?: string;
    limit?: number;
  }): Promise<PlatformTransactionsPage> {
    let url = `${this.apiEndpoint}/wallets/platform/transactions/all`;
    const queryString = this.toQueryString({
      start_date: args.start_date,
      end_date: args.end_date,
      cursor: args.cursor,
      limit: args.limit?.toString(),
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PlatformTransactionsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ===== Billing — invoices =====

  public async getInvoices(dto: GetInvoices): Promise<InvoicesPage> {
    let url = `${this.apiEndpoint}/billing/invoices`;
    const params = { ...dto, limit: dto.limit?.toString() };
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<InvoicesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Platform-level invoice rollup for dashboards.
   * GET billing/invoices/summary
   */
  public async getInvoicesSummary(): Promise<InvoiceSummary> {
    const url = `${this.apiEndpoint}/billing/invoices/summary`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<InvoiceSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Platform-level customer-agreement rollup for dashboards.
   * GET pricing/agreements/summary
   */
  public async getAgreementsSummary(
    params?: GetAgreementsSummary,
  ): Promise<AgreementsSummary> {
    let url = `${this.apiEndpoint}/pricing/agreements/summary`;
    const queryString = this.toQueryString(params ?? {});
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<AgreementsSummary> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async generateInvoice(dto: GenerateInvoice): Promise<Invoice> {
    const url = `${this.apiEndpoint}/billing/invoices/generate`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<Invoice> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getInvoice(dto: GetInvoice): Promise<Invoice> {
    const url = `${this.apiEndpoint}/billing/invoices/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<Invoice> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async collectInvoice(dto: CollectInvoice): Promise<Invoice> {
    const url = `${this.apiEndpoint}/billing/invoices/${dto.invoice_id}/collect`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        collected_by: dto.collected_by ?? undefined,
      },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<Invoice> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Pays a store's outstanding invoices from its wallet, oldest first, each
   * in full or not at all. Returns what was paid and what to top up.
   * POST billing/invoices/pay-outstanding
   */
  public async payOutstandingStoreInvoices(
    dto: PayOutstandingStoreInvoices,
  ): Promise<PayOutstandingStoreInvoicesResult> {
    const url = `${this.apiEndpoint}/billing/invoices/pay-outstanding`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        merchant_id: dto.merchant_id,
        store_id: dto.store_id,
        collected_by: dto.collected_by ?? undefined,
      },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PayOutstandingStoreInvoicesResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async retryInvoicePayment(dto: RetryInvoicePayment): Promise<Invoice> {
    const url = `${this.apiEndpoint}/billing/invoices/${dto.invoice_id}/retry-payment`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        retried_by: dto.retried_by ?? undefined,
      },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<Invoice> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async cancelInvoice(dto: CancelInvoice): Promise<Invoice> {
    const url = `${this.apiEndpoint}/billing/invoices/${dto.invoice_id}/cancel`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {
        cancelled_by: dto.cancelled_by,
        reason: dto.reason,
      },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<Invoice> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getInvoicePaymentAttempts(
    dto: GetInvoicePaymentAttempts,
  ): Promise<InvoicePaymentAttemptsPage> {
    let url = `${this.apiEndpoint}/billing/invoices/${dto.invoice_id}/payment-attempts`;
    const params = {
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<InvoicePaymentAttemptsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async runBilling(dto: RunBilling): Promise<BillingRun> {
    const url = `${this.apiEndpoint}/billing/billing-runs`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<BillingRun> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getBillingRun(dto: GetBillingRun): Promise<BillingRun> {
    const url = `${this.apiEndpoint}/billing/billing-runs/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<BillingRun> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getBillingRuns(dto: GetBillingRuns): Promise<BillingRunsPage> {
    let url = `${this.apiEndpoint}/billing/billing-runs`;
    const params = {
      status: dto.status,
      trigger_type: dto.trigger_type,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    };
    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<BillingRunsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ===== Pricing =====

  public async createPricingPlan(dto: CreatePricingPlan): Promise<PricingPlan> {
    const url = `${this.apiEndpoint}/pricing/plans`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingPlan> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updatePricingPlan(dto: UpdatePricingPlan): Promise<PricingPlan> {
    const url = `${this.apiEndpoint}/pricing/plans/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingPlan> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getPricingPlan(dto: GetPricingPlan): Promise<PricingPlan> {
    const url = `${this.apiEndpoint}/pricing/plans/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingPlan> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async listPricingPlans(
    dto: GetPricingPlans = {},
  ): Promise<PricingPlansPage> {
    let url = `${this.apiEndpoint}/pricing/plans`;
    const queryString = this.toQueryString({
      status: dto.status,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingPlansPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async archivePricingPlan(
    dto: ArchivePricingPlan,
  ): Promise<PricingPlan> {
    const url = `${this.apiEndpoint}/pricing/plans/${dto.id}/archive`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingPlan> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async createCustomerAgreement(
    dto: CreateCustomerAgreement,
  ): Promise<CustomerAgreement> {
    const url = `${this.apiEndpoint}/pricing/agreements`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getCustomerAgreement(
    dto: GetCustomerAgreement,
  ): Promise<CustomerAgreement> {
    const url = `${this.apiEndpoint}/pricing/merchants/${dto.merchant_id}/agreements/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async listCustomerAgreements(
    dto: GetCustomerAgreements,
  ): Promise<CustomerAgreementsPage> {
    let url = `${this.apiEndpoint}/pricing/merchants/${dto.merchant_id}/agreements`;
    const queryString = this.toQueryString({
      store_id: dto.store_id,
      status: dto.status,
      cursor: dto.cursor,
      limit: dto.limit?.toString(),
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreementsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getActiveCustomerAgreement(
    dto: GetActiveCustomerAgreement,
  ): Promise<CustomerAgreement> {
    const { merchant_id, store_id } = dto;

    let queryString = '';

    if (store_id) {
      queryString = this.toQueryString({ store_id: store_id });
    }

    const url =
      `${this.apiEndpoint}/pricing/merchants/${merchant_id}/agreements/active` +
      (queryString ? `?${queryString}` : '');
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async cancelCustomerAgreement(
    dto: CancelCustomerAgreement,
  ): Promise<CustomerAgreement> {
    const url = `${this.apiEndpoint}/pricing/agreements/${dto.id}/cancel`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { merchant_id: dto.merchant_id, store_id: dto.store_id },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async switchCustomerAgreement(
    dto: SwitchCustomerAgreement,
  ): Promise<SwitchCustomerAgreementResponse> {
    const url = `${this.apiEndpoint}/pricing/agreements/switch`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<SwitchCustomerAgreementResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Make a store's next 1–3 months free; billing resumes afterwards. */
  public async grantFreeMonths(
    dto: GrantFreeMonths,
  ): Promise<CustomerAgreement> {
    const url = `${this.apiEndpoint}/pricing/agreements/free-months`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async updateCustomerAgreement(
    dto: UpdateCustomerAgreement,
  ): Promise<CustomerAgreement> {
    const url = `${this.apiEndpoint}/pricing/merchants/${dto.merchant_id}/agreements/${dto.id}`;
    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: {
        store_id: dto.store_id,
        active_from: dto.active_from,
        active_to: dto.active_to,
        next_bill_date: dto.next_bill_date,
        expected_next_bill_date: dto.expected_next_bill_date,
        custom_billing_period_in_hours: dto.custom_billing_period_in_hours,
        plan_snapshot: dto.plan_snapshot,
      },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreement> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async listAgreementAuditLog(
    agreement_id: string,
    merchant_id: string,
    limit?: number,
    cursor?: string,
  ): Promise<CustomerAgreementAuditPage> {
    let url = `${this.apiEndpoint}/pricing/merchants/${merchant_id}/agreements/${agreement_id}/audit`;
    const queryString = this.toQueryString({
      limit: limit?.toString(),
      cursor,
    });
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: undefined,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<CustomerAgreementAuditPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async calculatePrice(
    dto: PricingCalculationRequest,
  ): Promise<PricingCalculationResult> {
    const url = `${this.apiEndpoint}/pricing/calculate`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingCalculationResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async previewPrice(
    dto: PreviewPricingRequest,
  ): Promise<PricingCalculationResult> {
    const url = `${this.apiEndpoint}/pricing/preview`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: dto,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PricingCalculationResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Admin Dashboard — aggregated metrics endpoints
  // ---------------------------------------------------------------------------

  public async getAdminStoreTimeSeries(params: {
    merchant_id: string;
    store_id: string;
    from: string;
    to: string;
    interval: string;
  }): Promise<TimeSeriesPoint[]> {
    let url = `${this.apiEndpoint}/reports/admin/${params.merchant_id}/stores/${params.store_id}/time-series`;

    const queryString = this.toQueryString({
      from: params.from,
      to: params.to,
      interval: params.interval,
    });
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<TimeSeriesPoint[]> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getAdminStorePayments(params?: {
    agreement_filter?: string;
    sort_field?: string;
    sort_direction?: string;
    merchant_ids?: string;
  }): Promise<StorePaymentsResponse> {
    let url = `${this.apiEndpoint}/reports/admin/stores/payments`;

    const queryString = this.toQueryString(params ?? {});
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StorePaymentsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getAdminStoreActivity(params: {
    from: string;
    to: string;
    filter?: string;
    relative_from?: string;
    relative_to?: string;
    merchant_ids?: string;
    sort_field?: string;
    sort_direction?: string;
  }): Promise<StoreActivityResponse> {
    let url = `${this.apiEndpoint}/reports/admin/stores/activity`;

    const queryString = this.toQueryString(params);
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<StoreActivityResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  public async getAdminPlatformMetrics(params?: {
    from?: string;
    to?: string;
  }): Promise<PlatformMetricsResponse> {
    let url = `${this.apiEndpoint}/reports/admin/platform/metrics`;

    const queryString = this.toQueryString(params ?? {});
    if (queryString) url += `?${queryString}`;

    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });

    try {
      const { data }: TransportResponse<PlatformMetricsResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** GET reports/admin/platform/billing — billing & receivables group. */
  public getAdminPlatformBilling(
    params: GetPlatformMetricsRangeRequest,
  ): Promise<PlatformBillingMetrics> {
    return this.getAdminPlatformGroup('billing', params);
  }

  /** GET reports/admin/platform/sales — GMV & store-activity group. */
  public getAdminPlatformSales(
    params: GetPlatformMetricsRangeRequest,
  ): Promise<PlatformSalesMetrics> {
    return this.getAdminPlatformGroup('sales', params);
  }

  /** GET reports/admin/platform/registry — merchants, stores & users group. */
  public getAdminPlatformRegistry(
    params: GetPlatformMetricsRangeRequest,
  ): Promise<PlatformRegistryMetrics> {
    return this.getAdminPlatformGroup('registry', params);
  }

  /** GET reports/admin/platform/operations — wallet & top-up counts. */
  public getAdminPlatformOperations(): Promise<PlatformOperationsMetrics> {
    return this.getAdminPlatformGroup('operations');
  }

  private async getAdminPlatformGroup<T>(
    group: string,
    params?: GetPlatformMetricsRangeRequest,
  ): Promise<T> {
    let url = `${this.apiEndpoint}/reports/admin/platform/${group}`;
    const queryString = this.toQueryString(params ?? {});
    if (queryString) url += `?${queryString}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<T> = await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Queues — live SQS status (admin)
  // ────────────────────────────────────────────────────────────

  /**
   * Every SQS queue of the stage with counts, oldest message age, dead-letter
   * wiring, consumers and warnings. Cached 30 s server-side unless `refresh`.
   * GET admin/queues
   */
  public async listQueues({
    refresh,
  }: { refresh?: boolean } = {}): Promise<QueueStatusPage> {
    let url = `${this.apiEndpoint}/admin/queues`;
    if (refresh) url += '?refresh=true';
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<QueueStatusPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Reads (never deletes) up to 10 messages of an unarchived dead-letter
   * queue. GET admin/queues/{queueName}/messages
   */
  public async peekDlqMessages(
    queueName: string,
  ): Promise<PeekedQueueMessages> {
    const url = `${this.apiEndpoint}/admin/queues/${encodeURIComponent(queueName)}/messages`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<PeekedQueueMessages> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Dead Letter Queue — Failed Events Archive
  // ────────────────────────────────────────────────────────────

  /**
   * List all dead letter queues with their failed event counts.
   * GET admin/failed-events/queues
   */
  public async listQueuesWithFailedEvents(): Promise<QueueSummaryPage> {
    const url = `${this.apiEndpoint}/admin/failed-events/queues`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<QueueSummaryPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * List failed events for a specific queue, newest first, with pagination.
   * GET admin/failed-events/{queueName}?limit=50&cursor=...
   */
  public async listFailedEvents(
    queueName: string,
    params?: { limit?: number; cursor?: string },
  ): Promise<FailedEventPage> {
    let url = `${this.apiEndpoint}/admin/failed-events/${queueName}`;
    const qs = this.toQueryString(params ?? {});
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<FailedEventPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Get a single failed event by queue and message ID.
   * GET admin/failed-events/{queueName}/{messageId}?sk=...
   */
  public async getFailedEvent(
    queueName: string,
    messageId: string,
    sk: string,
  ): Promise<FailedEvent> {
    const url = `${this.apiEndpoint}/admin/failed-events/${queueName}/${messageId}?sk=${encodeURIComponent(sk)}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<FailedEvent> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Redrive a failed event back to its source queue.
   * POST admin/failed-events/{queueName}/{messageId}/redrive
   */
  public async redriveFailedEvent(
    queueName: string,
    messageId: string,
    sk: string,
  ): Promise<RedriveResult> {
    const url = `${this.apiEndpoint}/admin/failed-events/${queueName}/${messageId}/redrive`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: { sk },
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<RedriveResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Soft-delete a failed event (marks it as DELETED).
   * DELETE admin/failed-events/{queueName}/{messageId}?sk=...
   */
  public async deleteFailedEvent(
    queueName: string,
    messageId: string,
    sk: string,
  ): Promise<DeleteResult> {
    const url = `${this.apiEndpoint}/admin/failed-events/${queueName}/${messageId}?sk=${encodeURIComponent(sk)}`;
    const config = this.getRequestConfig({
      method: 'DELETE',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<DeleteResult> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Observability — self-hosted native-app telemetry
  // ────────────────────────────────────────────────────────────

  /**
   * Upload one batch of device telemetry.
   * POST observability/ingest
   */
  public async ingestObservabilityBatch(
    body: ObservabilityIngestRequest,
  ): Promise<ObservabilityIngestResponse> {
    const url = `${this.apiEndpoint}/observability/ingest`;
    const config = this.getRequestConfig({
      method: 'POST',
      url,
      data: body,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityIngestResponse> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Signals recorded on one POS device, newest first.
   * GET observability/devices/{pos_device_id}/events
   */
  public async listObservabilityEventsByDevice(
    params: ListObservabilityByDevice,
  ): Promise<ObservabilityEventsPage> {
    const { pos_device_id, ...rest } = params;
    let url = `${this.apiEndpoint}/observability/devices/${pos_device_id}/events`;
    const qs = this.toQueryString(rest as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Raw firehose batches uploaded by one POS device, newest first.
   * GET observability/devices/{pos_device_id}/batches
   */
  public async listObservabilityBatchesByDevice(
    params: ListObservabilityByDevice,
  ): Promise<ObservabilityBatchesPage> {
    const { pos_device_id, ...rest } = params;
    let url = `${this.apiEndpoint}/observability/devices/${pos_device_id}/batches`;
    const qs = this.toQueryString(rest as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityBatchesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Every signal from one app session, in the order the device recorded it.
   * GET observability/sessions/{session_id}/events
   */
  public async listObservabilityEventsBySession(
    params: ListObservabilityBySession,
  ): Promise<ObservabilityEventsPage> {
    const { session_id, ...rest } = params;
    let url = `${this.apiEndpoint}/observability/sessions/${session_id}/events`;
    const qs = this.toQueryString(rest as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Every event emitted while one user action ran — the button press, the
   * inputs, the work, and the result.
   * GET observability/traces/{trace_id}
   */
  public async listObservabilityEventsByTrace(
    params: ListObservabilityByTrace,
  ): Promise<ObservabilityEventsPage> {
    const { trace_id, ...rest } = params;
    let url = `${this.apiEndpoint}/observability/traces/${trace_id}`;
    const qs = this.toQueryString(rest as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Per-function Lambda metrics (invocations, errors, duration, cold starts,
   * memory, billed ms) aggregated by the OpenTelemetry extension.
   * GET observability/backend/metrics
   */
  public async listBackendObservabilityMetrics(
    params?: Omit<ListObservabilityBackendMetrics, 'cursor'>,
  ): Promise<ObservabilityBackendMetricsPage> {
    let url = `${this.apiEndpoint}/observability/backend/metrics`;
    const qs = this.toQueryString((params ?? {}) as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityBackendMetricsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Hourly per-function rollup for charts, paginated by hour bucket: pass the
   * returned `cursor` back until it is absent, then merge the pages with
   * `mergeBackendTimeseriesPages`.
   * GET observability/backend/metrics/timeseries
   */
  public async listBackendObservabilityTimeseries(
    params?: ListObservabilityBackendMetrics,
  ): Promise<ObservabilityBackendTimeseriesPage> {
    let url = `${this.apiEndpoint}/observability/backend/metrics/timeseries`;
    const qs = this.toQueryString((params ?? {}) as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityBackendTimeseriesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /** Backend Lambda signal feed (failures, timeouts, slow traces). GET observability/backend/failures */
  public async listBackendObservabilityFailures(params?: {
    hours?: number;
    from?: string;
    to?: string;
    limit?: number;
    cursor?: string;
  }): Promise<ObservabilityEventsPage> {
    let url = `${this.apiEndpoint}/observability/backend/failures`;
    const qs = this.toQueryString((params ?? {}) as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Captured invocations of one Lambda function, newest first.
   * GET observability/backend/invocations
   */
  public async listLambdaInvocations(
    params: ListLambdaInvocations,
  ): Promise<LambdaInvocationsPage> {
    let url = `${this.apiEndpoint}/observability/backend/invocations`;
    const qs = this.toQueryString(params as unknown as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<LambdaInvocationsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Every captured log line of one Lambda invocation (stored in S3 by the
   * observability service, not read from CloudWatch).
   * GET observability/backend/invocations/{request_id}/logs
   */
  public async getLambdaInvocationLogs(
    requestId: string,
  ): Promise<LambdaInvocationLogs> {
    const url = `${this.apiEndpoint}/observability/backend/invocations/${encodeURIComponent(requestId)}/logs`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<LambdaInvocationLogs> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }
  /**
   * Sampling settings for backend telemetry (defaults when none saved).
   * GET observability/settings
   */
  public async getObservabilitySettings(): Promise<ObservabilitySettings> {
    const url = `${this.apiEndpoint}/observability/settings`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilitySettings> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Admin session only: share of successful Lambda invocations kept.
   * PUT observability/settings
   */
  public async updateObservabilitySettings(
    payload: UpdateObservabilitySettings,
  ): Promise<ObservabilitySettings> {
    const url = `${this.apiEndpoint}/observability/settings`;
    const config = this.getRequestConfig({
      method: 'PUT',
      url,
      data: payload,
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilitySettings> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Fleet-wide feed for one severity, newest first.
   * GET observability/feed
   */
  public async listObservabilityEventsByLevel(
    params: ListObservabilityByLevel,
  ): Promise<ObservabilityEventsPage> {
    let url = `${this.apiEndpoint}/observability/feed`;
    const qs = this.toQueryString(params as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Everything that happened at one merchant on a given day.
   * GET observability/merchants/{merchant_id}/events
   */
  public async listObservabilityEventsByMerchant(
    params: ListObservabilityByMerchant,
  ): Promise<ObservabilityEventsPage> {
    const { merchant_id, ...rest } = params;
    let url = `${this.apiEndpoint}/observability/merchants/${merchant_id}/events`;
    const qs = this.toQueryString(rest as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEventsPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * One signal with its full payload — the exact receipt text, report body
   * or request/response dump that the list views deliberately omit.
   * GET observability/devices/{pos_device_id}/events/{id}?occurred_at=...
   */
  public async getObservabilityEvent(
    posDeviceId: string,
    id: string,
    occurredAt: string,
  ): Promise<ObservabilityEvent> {
    const url = `${this.apiEndpoint}/observability/devices/${posDeviceId}/events/${id}?occurred_at=${encodeURIComponent(occurredAt)}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityEvent> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * One raw batch, decompressed into its individual events — the complete
   * internal log stream for that slice of time.
   * GET observability/devices/{pos_device_id}/batches/{batch_id}?created_at=...
   */
  public async getObservabilityBatch(
    posDeviceId: string,
    batchId: string,
    createdAt: string,
  ): Promise<ObservabilityBatch> {
    const url = `${this.apiEndpoint}/observability/devices/${posDeviceId}/batches/${batchId}?created_at=${encodeURIComponent(createdAt)}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityBatch> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Recurring problems grouped into issues, Sentry-style.
   * GET observability/issues
   */
  public async listObservabilityIssues(params?: {
    level?: string;
    hours?: number;
    merchant_id?: string;
    from?: string;
    to?: string;
  }): Promise<ObservabilityIssuesPage> {
    let url = `${this.apiEndpoint}/observability/issues`;
    const qs = this.toQueryString((params ?? {}) as Record<string, any>);
    if (qs) url += `?${qs}`;
    const config = this.getRequestConfig({
      method: 'GET',
      url,
      data: {},
      extraHeaders: {},
    });
    try {
      const { data }: TransportResponse<ObservabilityIssuesPage> =
        await this.transport.send(config);
      return data;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  // ==========================================================================
  // Mobile-money ledger (service-topups) + platform wallet debit (service-wallets)
  // ==========================================================================

  /** Private helper for the JSON request/response calls below. */
  private async requestJson<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data: unknown = {},
  ): Promise<T> {
    const config = this.getRequestConfig({
      method,
      url,
      data,
      extraHeaders: {},
    });
    try {
      const { data: response }: TransportResponse<T> =
        await this.transport.send(config);
      return response;
    } catch (error) {
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Merged money-in + money-out ledger of one Top-up Number, newest first.
   * GET topups/ledger?receiver_number=
   */
  public getMobileMoneyLedger(
    dto: GetMobileMoneyLedger,
  ): Promise<MobileMoneyLedgerPage> {
    const query = this.toQueryString({ ...dto });
    return this.requestJson<MobileMoneyLedgerPage>(
      'GET',
      `${this.apiEndpoint}/topups/ledger${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Promote a PENDING_SETUP Top-up Number to its real phone number.
   * POST topups/numbers/promote
   */
  public promoteTopupNumber(dto: PromoteTopupNumber): Promise<TopupNumber> {
    return this.requestJson<TopupNumber>(
      'POST',
      `${this.apiEndpoint}/topups/numbers/promote`,
      dto,
    );
  }

  /**
   * Merge a PENDING_SETUP number into an existing Top-up Number (preview
   * unless `dry_run: false`). POST topups/numbers/merge
   */
  public mergeTopupNumber(
    dto: MergeTopupNumber,
  ): Promise<TopupNumberMergeResult> {
    return this.requestJson<TopupNumberMergeResult>(
      'POST',
      `${this.apiEndpoint}/topups/numbers/merge`,
      dto,
    );
  }

  /** Phones (IFTTT devices) that forward SMS. GET topups/devices */
  public getTopupDevices(): Promise<TopupDevicesPage> {
    return this.requestJson<TopupDevicesPage>(
      'GET',
      `${this.apiEndpoint}/topups/devices`,
    );
  }

  /** Assign a phone to a Top-up Number. POST topups/devices/assign */
  public assignTopupDevice(dto: AssignTopupDevice): Promise<TopupDevice> {
    return this.requestJson<TopupDevice>(
      'POST',
      `${this.apiEndpoint}/topups/devices/assign`,
      dto,
    );
  }

  /**
   * Debit the platform wallet (platform expenses). Idempotent per reference.
   * POST wallets/platform/transactions/debit
   */
  public debitPlatformWallet(
    dto: DebitPlatformWalletParams,
  ): Promise<CreditDebitResult> {
    return this.requestJson<CreditDebitResult>(
      'POST',
      `${this.apiEndpoint}/wallets/platform/transactions/debit`,
      dto,
    );
  }

  // ==========================================================================
  // Platform finance (service-admin)
  // ==========================================================================

  private platformFinanceUrl(
    path: string,
    params: Record<string, unknown> = {},
  ): string {
    const query = this.toQueryString({ ...params });
    return `${this.apiEndpoint}/admin/platform-finance${path}${query ? `?${query}` : ''}`;
  }

  /** GET admin/platform-finance/bootstrap — everything the page needs, one call. */
  public getPlatformFinanceBootstrap(
    dto: GetPlatformFinanceBootstrap = {},
  ): Promise<PlatformFinanceBootstrap> {
    return this.requestJson<PlatformFinanceBootstrap>(
      'GET',
      this.platformFinanceUrl('/bootstrap', {
        receiver_number: dto.receiver_number,
      }),
    );
  }

  /** GET admin/platform-finance/transactions/{receiver_number}/{transaction_id} */
  public getPlatformFinanceTransaction(
    dto: GetPlatformFinanceTransaction,
  ): Promise<PlatformFinanceTransaction> {
    return this.requestJson<PlatformFinanceTransaction>(
      'GET',
      this.platformFinanceUrl(
        `/transactions/${encodeURIComponent(dto.receiver_number)}/${encodeURIComponent(dto.transaction_id)}`,
      ),
    );
  }

  /** POST admin/platform-finance/transactions/{receiver_number}/{transaction_id}/assign-in */
  public assignPlatformFinanceIncome(
    dto: AssignPlatformFinanceIncome,
  ): Promise<PlatformFinanceTransaction> {
    const { receiver_number, transaction_id, ...body } = dto;
    return this.requestJson<PlatformFinanceTransaction>(
      'POST',
      this.platformFinanceUrl(
        `/transactions/${encodeURIComponent(receiver_number)}/${encodeURIComponent(transaction_id)}/assign-in`,
      ),
      body,
    );
  }

  /** POST admin/platform-finance/transactions/{receiver_number}/{transaction_id}/assign-out */
  public assignPlatformFinanceExpense(
    dto: AssignPlatformFinanceExpense,
  ): Promise<PlatformFinanceTransaction> {
    const { receiver_number, transaction_id, ...body } = dto;
    return this.requestJson<PlatformFinanceTransaction>(
      'POST',
      this.platformFinanceUrl(
        `/transactions/${encodeURIComponent(receiver_number)}/${encodeURIComponent(transaction_id)}/assign-out`,
      ),
      body,
    );
  }

  /** GET admin/platform-finance/clients/{merchant_id}/{store_id} */
  public getPlatformFinanceClient(
    dto: GetPlatformFinanceClient,
  ): Promise<PlatformFinanceClientDetail> {
    return this.requestJson<PlatformFinanceClientDetail>(
      'GET',
      this.platformFinanceUrl(`/clients/${dto.merchant_id}/${dto.store_id}`),
    );
  }

  /** POST admin/platform-finance/invoices/{invoice_id}/waive */
  public waivePlatformFinanceInvoice(
    dto: WaivePlatformFinanceInvoice,
  ): Promise<PlatformFinanceWaiver> {
    const { invoice_id, ...body } = dto;
    return this.requestJson<PlatformFinanceWaiver>(
      'POST',
      this.platformFinanceUrl(`/invoices/${invoice_id}/waive`),
      body,
    );
  }

  /** POST admin/platform-finance/invoices/{invoice_id}/remove-waiver */
  public removePlatformFinanceWaiver(
    dto: RemovePlatformFinanceWaiver,
  ): Promise<PlatformFinanceWaiver> {
    const { invoice_id, ...body } = dto;
    return this.requestJson<PlatformFinanceWaiver>(
      'POST',
      this.platformFinanceUrl(`/invoices/${invoice_id}/remove-waiver`),
      body,
    );
  }

  /** GET admin/platform-finance/employees/{employee_id}?month= */
  public getPlatformFinanceEmployee(
    dto: GetPlatformFinanceEmployee,
  ): Promise<PlatformFinanceEmployeeDetail> {
    return this.requestJson<PlatformFinanceEmployeeDetail>(
      'GET',
      this.platformFinanceUrl(`/employees/${dto.employee_id}`, {
        month: dto.month,
      }),
    );
  }

  /** GET admin/platform-finance/reconciliation/{receiver_number} */
  public getPlatformFinanceReconciliation(
    dto: GetPlatformFinanceReconciliation,
  ): Promise<PlatformFinanceReconciliation> {
    return this.requestJson<PlatformFinanceReconciliation>(
      'GET',
      this.platformFinanceUrl(
        `/reconciliation/${encodeURIComponent(dto.receiver_number)}`,
      ),
    );
  }

  /** POST admin/platform-finance/replay — re-publish stored IFTTT SMS. */
  public replayPlatformFinanceSms(
    dto: ReplayPlatformFinanceSms,
  ): Promise<ReplayPlatformFinanceSmsResult> {
    return this.requestJson<ReplayPlatformFinanceSmsResult>(
      'POST',
      this.platformFinanceUrl('/replay'),
      dto,
    );
  }

  /** POST admin/platform-finance/accounts/merge — preview unless dry_run is false. */
  public mergePlatformFinanceAccount(
    dto: MergePlatformFinanceAccount,
  ): Promise<PlatformFinanceAccountMergeResult> {
    return this.requestJson<PlatformFinanceAccountMergeResult>(
      'POST',
      this.platformFinanceUrl('/accounts/merge'),
      dto,
    );
  }

  /** POST admin/platform-finance/devices/assign — assign and process held SMS. */
  public assignPlatformFinanceDevice(
    dto: AssignPlatformFinanceDevice,
  ): Promise<PlatformFinanceDeviceAssignResult> {
    return this.requestJson<PlatformFinanceDeviceAssignResult>(
      'POST',
      this.platformFinanceUrl('/devices/assign'),
      dto,
    );
  }

  /** GET admin/platform-finance/audit */
  public getPlatformFinanceAuditLog(
    dto: GetPlatformFinanceAuditLog = {},
  ): Promise<PlatformFinanceAuditLogPage> {
    return this.requestJson<PlatformFinanceAuditLogPage>(
      'GET',
      this.platformFinanceUrl('/audit', {
        cursor: dto.cursor,
        limit: dto.limit,
      }),
    );
  }

  // ---- Integrations: QuickBooks POS (service-integrations) ------------------

  private quickbooksUrl(
    merchantId: string,
    path: string,
    params: Record<string, unknown> = {},
  ): string {
    const query = this.toQueryString({ ...params });
    return `${this.apiEndpoint}/integrations/quickbooks/${merchantId}${path}${query ? `?${query}` : ''}`;
  }

  /** The merchant's QuickBooks connection (`status: 'none'` until one is started). */
  public getQuickBooksConnection(
    merchant_id: string,
  ): Promise<QuickBooksConnection> {
    return this.requestJson<QuickBooksConnection>(
      'GET',
      this.quickbooksUrl(merchant_id, '/connection'),
    );
  }

  /** Sends the owner a WhatsApp code, or reports the business is already connected. */
  public requestQuickBooksConnection(
    merchant_id: string,
    dto: RequestQuickBooksConnection,
  ): Promise<RequestQuickBooksConnectionResponse> {
    return this.requestJson<RequestQuickBooksConnectionResponse>(
      'POST',
      this.quickbooksUrl(merchant_id, '/connection'),
      dto,
    );
  }

  public verifyQuickBooksConnection(
    merchant_id: string,
    dto: VerifyQuickBooksConnection,
  ): Promise<QuickBooksConnection> {
    return this.requestJson<QuickBooksConnection>(
      'POST',
      this.quickbooksUrl(merchant_id, '/connection/verify'),
      dto,
    );
  }

  public async getQuickBooksStores(
    merchant_id: string,
  ): Promise<QuickBooksStoreOption[]> {
    const { stores } = await this.requestJson<{
      stores: QuickBooksStoreOption[];
    }>('GET', this.quickbooksUrl(merchant_id, '/stores'));
    return stores;
  }

  public linkQuickBooksStore(
    merchant_id: string,
    store_id: string,
    dto: LinkQuickBooksStore,
  ): Promise<QuickBooksConnection> {
    return this.requestJson<QuickBooksConnection>(
      'POST',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/link`),
      dto,
    );
  }

  /** The QuickBooks store's catalog as it would be imported (needs the store's agent online). */
  /** The catalog preview's status, with the preview once it is ready. */
  public getQuickBooksCatalog(
    merchant_id: string,
    store_id: string,
  ): Promise<QuickBooksCatalogPreviewStatus> {
    return this.requestJson<QuickBooksCatalogPreviewStatus>(
      'GET',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/catalog`),
    );
  }

  /** Builds the catalog preview in the background; poll getQuickBooksCatalog. */
  public requestQuickBooksCatalog(
    merchant_id: string,
    store_id: string,
  ): Promise<QuickBooksCatalogPreviewStatus> {
    return this.requestJson<QuickBooksCatalogPreviewStatus>(
      'POST',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/catalog`),
    );
  }

  public getQuickBooksMappings(
    merchant_id: string,
  ): Promise<QuickBooksMappings> {
    return this.requestJson<QuickBooksMappings>(
      'GET',
      this.quickbooksUrl(merchant_id, '/mappings'),
    );
  }

  public updateQuickBooksMappings(
    merchant_id: string,
    dto: UpdateQuickBooksMappings,
  ): Promise<QuickBooksMappings> {
    return this.requestJson<QuickBooksMappings>(
      'PUT',
      this.quickbooksUrl(merchant_id, '/mappings'),
      dto,
    );
  }

  public createQuickBooksImport(
    merchant_id: string,
    store_id: string,
    dto: CreateQuickBooksImport,
  ): Promise<QuickBooksImportJob> {
    return this.requestJson<QuickBooksImportJob>(
      'POST',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/imports`),
      dto,
    );
  }

  public getQuickBooksImport(
    merchant_id: string,
    store_id: string,
    job_id: string,
  ): Promise<QuickBooksImportJob> {
    return this.requestJson<QuickBooksImportJob>(
      'GET',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/imports/${job_id}`),
    );
  }

  public listQuickBooksImports(
    merchant_id: string,
    store_id: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<QuickBooksImportJobPage> {
    return this.requestJson<QuickBooksImportJobPage>(
      'GET',
      this.quickbooksUrl(merchant_id, `/stores/${store_id}/imports`, params),
    );
  }

  // Business finances: founders, general expenses, manual income.

  /** GET admin/platform-finance/business?year= — the Business tab in one call. */
  public getPlatformBusinessFinance(
    dto: GetPlatformBusinessFinance = {},
  ): Promise<PlatformBusinessFinance> {
    return this.requestJson<PlatformBusinessFinance>(
      'GET',
      this.platformFinanceUrl('/business', { year: dto.year }),
    );
  }

  /** POST admin/platform-finance/founders */
  public createPlatformFounder(
    dto: CreatePlatformFounder,
  ): Promise<PlatformFounder> {
    return this.requestJson<PlatformFounder>(
      'POST',
      this.platformFinanceUrl('/founders'),
      dto,
    );
  }

  /** PUT admin/platform-finance/founders/{id} */
  public updatePlatformFounder(
    dto: UpdatePlatformFounder,
  ): Promise<PlatformFounder> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformFounder>(
      'PUT',
      this.platformFinanceUrl(`/founders/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** DELETE admin/platform-finance/founders/{id} (soft delete) */
  public deletePlatformFounder(
    dto: DeletePlatformFounder,
  ): Promise<PlatformFounder> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformFounder>(
      'DELETE',
      this.platformFinanceUrl(`/founders/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** POST admin/platform-finance/expenses */
  public createPlatformExpense(
    dto: CreatePlatformExpense,
  ): Promise<PlatformExpense> {
    return this.requestJson<PlatformExpense>(
      'POST',
      this.platformFinanceUrl('/expenses'),
      dto,
    );
  }

  /** PUT admin/platform-finance/expenses/{id} */
  public updatePlatformExpense(
    dto: UpdatePlatformExpense,
  ): Promise<PlatformExpense> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformExpense>(
      'PUT',
      this.platformFinanceUrl(`/expenses/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** DELETE admin/platform-finance/expenses/{id} (soft delete) */
  public deletePlatformExpense(
    dto: DeletePlatformExpense,
  ): Promise<PlatformExpense> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformExpense>(
      'DELETE',
      this.platformFinanceUrl(`/expenses/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** POST admin/platform-finance/expenses/reimburse */
  public reimbursePlatformExpenses(
    dto: ReimbursePlatformExpenses,
  ): Promise<ReimbursePlatformExpensesResult> {
    return this.requestJson<ReimbursePlatformExpensesResult>(
      'POST',
      this.platformFinanceUrl('/expenses/reimburse'),
      dto,
    );
  }

  /** POST admin/platform-finance/incomes */
  public createPlatformIncome(
    dto: CreatePlatformIncome,
  ): Promise<PlatformIncome> {
    return this.requestJson<PlatformIncome>(
      'POST',
      this.platformFinanceUrl('/incomes'),
      dto,
    );
  }

  /** PUT admin/platform-finance/incomes/{id} */
  public updatePlatformIncome(
    dto: UpdatePlatformIncome,
  ): Promise<PlatformIncome> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformIncome>(
      'PUT',
      this.platformFinanceUrl(`/incomes/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** DELETE admin/platform-finance/incomes/{id} (soft delete) */
  public deletePlatformIncome(
    dto: DeletePlatformIncome,
  ): Promise<PlatformIncome> {
    const { id, ...body } = dto;
    return this.requestJson<PlatformIncome>(
      'DELETE',
      this.platformFinanceUrl(`/incomes/${encodeURIComponent(id)}`),
      body,
    );
  }

  /** POST admin/platform-finance/receipts/upload-url — then PUT the file. */
  public createPlatformFinanceReceiptUpload(
    dto: CreatePlatformFinanceReceiptUpload,
  ): Promise<PlatformFinanceReceiptUpload> {
    return this.requestJson<PlatformFinanceReceiptUpload>(
      'POST',
      this.platformFinanceUrl('/receipts/upload-url'),
      dto,
    );
  }

  /** GET admin/platform-finance/receipts/url?key= — short-lived view link. */
  public getPlatformFinanceReceiptUrl(
    dto: GetPlatformFinanceReceiptUrl,
  ): Promise<PlatformFinanceReceiptUrl> {
    return this.requestJson<PlatformFinanceReceiptUrl>(
      'GET',
      this.platformFinanceUrl('/receipts/url', { key: dto.key }),
    );
  }
}

/**
 * The public surface of `ShopAndSmileAPIAdapter`: what use-cases depend on
 * (injected through their DTO) and what tests fake.
 */
export type ShopAndSmileApi = {
  [K in keyof ShopAndSmileAPIAdapter]: ShopAndSmileAPIAdapter[K];
};
